import { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import Modal from '../components/Modal';
import { useParams, useNavigate, useLocation } from '../routing';
import { documentsApi, suppliersApi, catalogApi, creditsApi, paymentsApi, formatCurrency, statusBadge, downloadDocFile, fetchDocFileObjectUrl } from '../utils/api';
import { PAYMENT_METHOD_OPTIONS } from './SupplierManagementPage';
import { toast } from '../components/Toast';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import LinkDeliveryNotesModal from '../components/LinkDeliveryNotesModal';
import LinkOpenCreditsModal from '../components/LinkOpenCreditsModal';
import CommentsModal from '../components/CommentsModal';
import ReviewDocPane from '../components/ReviewDocPane';
import ReviewIssueSummary from '../components/ReviewIssueSummary';
import SearchableSelect from '../components/SearchableSelect';
import { formatDateShort } from '../utils/dateFormat';
import { itemsForSave } from '../utils/lineKind';
import { calcNetUnitPrice, calcLineTotal, calcCreditTotal, roundDocTotal } from '../utils/lineCalc';
import { round2 } from '../utils/money';
import { detectVatRule } from '../utils/vatRule';
import { track, EVENTS } from '../utils/analytics';
import { reconcileNet, signedNet } from '../utils/reconcile';
import { isImplausibleDateYear } from '../utils/dateSanity';
import { rowMismatched, analyzeReviewRow, invoiceDiscountMismatch, deriveReviewIssues, OPEN_NOTES_DOC_TYPES } from '../utils/reviewIssues';
import { useOpenDeliveryNotes, openNotesRange, rangeForInvoice } from '../utils/useOpenDeliveryNotes';
import { draftPosition, draftGroupPosition, sortDraftQueue, nextDraftId, skippedRemaining, markDraftSkipped, skippedDraftIds } from '../utils/draftQueue';
import { readDraftOrder } from '../utils/draftQueueOrder';
import { readGatePreview } from '../utils/gatePreview';
import { prefetchDraftScan, prefetchDraftDoc, takeDraftPrefetch, dropDraftPrefetch, whenDraftPrefetchSettles, draftFingerprint } from '../utils/draftPrefetch';
import { parseTenantPath, tenantUrl } from '../utils/tenantPath';
import { hasUnsavedEdits, leavePrompt } from '../utils/draftEdits';
import { unloadGuardSuspended } from '../utils/unloadGuard';
import { reviewEntryFromState, buildReview } from '../utils/reviewTiming';
import DeleteDocumentDialog from '../components/DeleteDocumentDialog';
import MoveDocumentDialog from '../components/MoveDocumentDialog';
import ReturnNoteDialog from '../components/ReturnNoteDialog';
import DuplicateCompareDialog from '../components/DuplicateCompareDialog';
import { duplicateSignals, keepDraftDeleteContext, keepDraftDeleteQuestion, keepDraftDeleteWarning } from '../utils/duplicateCompare';
import Icon from '../components/Icon';
import NewSupplierOptions, { hasUnmatchedSupplierName } from '../components/NewSupplierOptions';
import EmptyState from '../components/EmptyState';
import InlineLoadError from '../components/InlineLoadError';
import NoAccessNotice from '../components/NoAccessNotice';
import DocFileViewer from '../components/DocFileViewer';
import { usePageRotations } from '../utils/usePageRotations';
import DocItemsList from '../components/DocItemsList';
import { useIsMobile } from '../utils/useIsMobile';
import PaymentModal from '../components/PaymentModal';
import { approvalOfferForDoc, monthRangeFromDocDate, monthLabelFromDocDate } from '../utils/paymentLifecycle';
import { PaymentLifecycleControl } from '../components/PaymentLifecycleBadge';
import { isPayableDocType, isDupExemptDocType } from '../utils/docTypes';
import { useConfirm } from '../utils/useConfirm';
import { confirmAddRecipientAlias, confirmIgnoreRecipientBid } from '../utils/recipientAlias';
import { markSupplierDeliveryNotes } from '../utils/markSupplierDeliveryNotes';
import { HqOnlyLock } from '../components/HqOnlyNotice';
import { ViewAsMenuItems } from '../components/ViewAsMenu';
import PriceSourceBadge, { VatCheckTag } from '../components/PriceSourceBadge';
import { openingForm, openingItems, withCatalogPrices, formTotals, calcInvoiceDiscountAmount, formRev } from '../utils/openingForm';
import { isGatedDraft, newReviewSessionId, gateReviewFields, MAX_STALE_ATTEMPTS } from '../utils/gateView';
import { filterDraftQueue, readGateFilter, rememberGateColors, gateColorScope, gateStripIssues, gateOpeningFields, gateAnalytics, GATE_MARKS } from '../utils/gateMark';
import OcrSupplierReadLine from '../components/OcrSupplierReadLine';
import { ocrSupplierRead } from '../utils/ocrSupplierRead';

const DOC_TYPES = ['חשבונית', 'חשבונית זיכוי', 'חשבונית מרכזת', 'תעודת משלוח', 'תעודת החזרה', 'חשבונית/קבלה', 'קבלה', 'חשבון עסקה', 'כרטסת', 'אחר'];
// Stable option arrays for the searchable selects (module scope so their
// identity never changes → the combobox's internal filter memo isn't defeated).
const DOC_TYPE_OPTIONS = DOC_TYPES.map(t => ({ value: t, label: t }));
const STATUS_OPTIONS = ['טיוטה', 'תקין', 'לא תקין'].map(s => ({ value: s, label: s }));
const PAYMENT_METHODS = PAYMENT_METHOD_OPTIONS.map(o => o.value);
const todayISO = () => new Date().toISOString().split('T')[0];
// The "שולם" block in a draft's edit form, before the admin ticks it.
const NO_PAY_ON_APPROVE = { checked: false, payment_method: '', payment_date: '', approveMonth: false };
// The one-click answer to "why is this paid?" — the paper itself says so.
const PAY_NOTE_QUICK = 'הופיע שולם על המסמך';
// Doc types whose approval should offer to close the supplier's open credits.
const CREDIT_DOC_TYPES = ['חשבונית זיכוי', 'תעודת החזרה'];
// A line that opens a credit — כמות לזיכוי or הפרש מחיר לזיכוי. A document
// with one is always לא תקין (owner, #260; server/lib/creditGuard.js).
const lineHasCredit = (it) =>
  (parseFloat(it.credit_qty) || 0) > 0 || (parseFloat(it.credit_price_diff) || 0) > 0;
// rowMismatched moved to utils/reviewIssues.js — shared with the upload
// review stage and the consolidated issue strip (single source of truth).
// Invoice-level discount: 'percent' → subtotal × (d/100); 'amount' → d as-is.
// The document total's label. "כולל מע"מ" only when the document actually
// carries VAT: a עוסק פטור supplier's document, a VAT-inclusive supplier's
// (prices already include it, vat is stored as 0) or a partial-VAT one at 0
// have none of their own to add, and "כולל מע"מ" there would be a claim the
// paper doesn't make. Never "סה"כ לתשלום" — it read as "still owed"; whether
// it was paid is the payment badge beside the amount.
function totalLabel(vat) {
  return Math.abs(parseFloat(vat) || 0) > 0.005 ? 'סה"כ כולל מע"מ' : 'סה"כ';
}
// An amount in the edit form's totals card. The 9px inset matches the text
// inside the discount / manual-VAT inputs (1px border + 8px padding), so every
// figure in the column starts on the same line (owner, #260).
const TOTALS_AMOUNT = { minWidth: 120, textAlign: 'left', paddingLeft: 9, boxSizing: 'border-box' };
// The invoice discount as the totals show it: −X off, or +X when the supplier
// rounded the total UP through the discount line (התאם הנחה, owner 31/10/2026).
const hasInvoiceDiscount = (amount) => Math.round(amount * 100) !== 0;
const signedDiscount = (amount) => `${amount > 0 ? '−' : '+'}${Math.abs(amount).toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
// The small התאם beside a totals field: the same fix as the chip's button,
// where the eye already is (owner, 31/10/2026). Only while the chip offers it.
function FitButton({ fix, title, onFix }) {
  if (!fix) return null;
  return (
    <button type="button" className="ri-fit-btn" title={title(fix)} aria-label={fix.label} onClick={() => onFix(fix)}>
      <Icon name="sparkle" size={12} /> התאם
    </button>
  );
}

// The supplier as the opening sees it (utils/openingForm.js `context`): the
// deposit / purchase-tax masks from the document's own join, as the form has
// always read them; the VAT rule and the agreed terms from the supplier list
// row when there is one, with the same fallbacks the review checks use.
function openingSupplier(d, row) {
  return {
    has_deposit: d.supplier_has_deposit, has_purchase_tax: d.supplier_has_purchase_tax,
    invoice_rules: row?.invoice_rules ?? null,
    fixed_invoice_discount: row ? row.fixed_invoice_discount : d.supplier_fixed_invoice_discount,
    fixed_invoice_discount_type: row?.fixed_invoice_discount_type || d.supplier_fixed_invoice_discount_type || 'percent',
    uses_delivery_notes: row ? row.uses_delivery_notes : null,
  };
}

// The lines the review form opens with, before the catalog match — what the
// next-draft prefetch and the re-check ask the match about, so it is asked
// about the same rows the form opens with.
const reviewBaseItems = (d) => openingItems(d.items, openingSupplier(d, null));

// A fresh catalog match laid onto a form already open: only the catalog_*
// fields change, and only on the lines the form opened with (`_key` is the
// line's index in the document; a line added by hand has a timestamp key and
// is left alone). What the reviewer typed is never touched.
const CATALOG_FIELDS = ['catalog_last_price', 'catalog_fixed_price', 'catalog_fixed_discount', 'catalog_fixed_discount_type'];
function patchCatalogPrices(items, enriched) {
  let changed = false;
  const next = items.map(it => {
    if (!Number.isInteger(it._key) || it._key >= enriched.length) return it;
    const patched = withCatalogPrices([it], [enriched[it._key]])[0];
    if (CATALOG_FIELDS.every(k => patched[k] === it[k])) return it;
    changed = true;
    return patched;
  });
  // The same array when nothing moved: no re-render of the items table.
  return changed ? next : items;
}

// The match endpoint is supplier-scoped; null-supplier drafts (bulk-uploaded,
// AI couldn't identify supplier) and drafts with no lines skip it (null). A
// failed match leaves `catalog` undefined, so the form asks again.
async function loadDraftForReview(docId) {
  const r = await documentsApi.getOne(docId);
  const doc = r.data;
  const base = doc ? reviewBaseItems(doc) : [];
  if (!doc?.supplier_id || base.length === 0) return { doc, catalog: null };
  try {
    const m = await catalogApi.match(doc.supplier_id, base, { pricesOnly: true });
    return { doc, catalog: m.data || [] };
  } catch (e) {
    console.warn('[draft-prefetch] catalog match skipped:', e?.message || e);
    return { doc };
  }
}

// One try of a prefetched draft's re-check, at most (then retried once).
const RECHECK_TIMEOUT_MS = 8000;
// `promise`, or a rejection once `ms` pass first.
function settleOrFail(promise, ms) {
  let timer;
  const timeout = new Promise((_resolve, reject) => {
    timer = setTimeout(() => reject(new Error(`timed out after ${ms}ms`)), ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

// This tab's restaurant — the prefetch cache's key (utils/draftPrefetch.js).
const tabRestaurantId = () => parseTenantPath(window.location.pathname).rid ?? null;

export default function DocumentViewPage() {
  const { user, canEdit, isAdmin, viewAsTargets = [] } = useAuth();
  // The reviewer's queue order (#313), the same one /drafts shows.
  const queueOrder = useMemo(() => readDraftOrder(user?.id), [user?.id]);
  const { vatRate, paymentTrackingEnabled } = useSettings();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // When the user arrived from the מסמכים tab via "צפה במסמך", the file panel
  // expands automatically — the click was an explicit "show me the document"
  // intent, no extra toggle needed.
  const autoOpenFiles = location.state?.autoOpenFiles === true;
  // Draft-review entry point (DraftsPage) sets autoEdit so we land directly
  // in the editable items table with no extra clicks.
  const autoEdit = location.state?.autoEdit === true;
  // /drafts' compare screen sends "לא כפיל · שנה מספר מסמך" here with
  // autoEdit: once the form is up, the cursor lands in the doc-number field.
  const focusDocNumOnArrive = location.state?.focusDocNum === true;
  // How the reviewer got here, reported with the decision (utils/reviewTiming.js):
  // approve_next from the previous draft's אשר והבא, queue_edit from /drafts,
  // else direct. The server stores it next to the seconds on screen.
  const reviewEntry = reviewEntryFromState(location.state);
  // When this review started — the page opening on a draft, or עריכה on one;
  // whichever came first. Reset per document, read at the decision.
  const openedAtRef = useRef(null);
  // The draft gate (stage 3, #331; utils/gateView.js): this screen's review
  // session, the gate view it activated for each draft and the copy of it
  // last committed (docId → { viewId, formRev }: one view per session and
  // document, kept when the reviewer moves to another draft and back), and
  // the stale answers in a row for the draft on screen.
  const reviewSessionRef = useRef(null);
  if (reviewSessionRef.current == null) reviewSessionRef.current = newReviewSessionId();
  const gateViewsRef = useRef(new Map());
  const gateStaleRef = useRef({ docId: null, n: 0 });
  // The mark the strip shows (stage 3 / C, #332): the activation's own answer
  // { docId, gate_state, gate_color, gate_reasons }, never a listed or cached
  // colour — nothing until it answers, and only for the draft it answered.
  // Its chips close against gateBaseline (below).
  const [gateAnswer, setGateAnswer] = useState(null);
  // docId → the baseline the gate's chips close against: the fields of the
  // opening whose activation CREATED the session's view. The view's reasons
  // never change after it, so neither does the baseline — a correction saved
  // as a draft and reopened stays a correction (Codex review of #343).
  // Bound to the order the activations were SENT, not the order their answers
  // arrive: { fixed, tries: [{ seq, opening, state }] }. Until the earliest
  // try still standing has answered, it is the baseline (a slow first answer
  // whose view the server already made must not lose to a quick second one;
  // round 2); a try that failed drops out.
  const gateOpeningsRef = useRef(new Map());
  const gateSeqRef = useRef(0);
  // The baseline lives in a ref, so a change to it re-renders through this
  // tick — e.g. an earlier try that fails AFTER a later one answered moves the
  // baseline with no answer to render it (Codex review of #343, round 3).
  const [, setGateBaselineTick] = useState(0);
  const gateBaseline = (docId) => {
    const e = gateOpeningsRef.current.get(String(docId));
    return e ? (e.fixed || e.tries[0]?.opening || null) : null;
  };
  const noteGateTry = (docId, seq, ok) => {
    const e = gateOpeningsRef.current.get(docId);
    if (!e || e.fixed) return;
    const before = gateBaseline(docId);
    if (!ok) e.tries = e.tries.filter(t => t.seq !== seq);
    else { const t = e.tries.find(x => x.seq === seq); if (t) t.state = 'ok'; }
    if (e.tries[0]?.state === 'ok') { e.fixed = e.tries[0].opening; e.tries = []; }
    if (gateBaseline(docId) !== before && mountedRef.current) setGateBaselineTick(n => n + 1);
  };
  // The queue as the reviewer works it: the chosen order, inside /drafts'
  // לתיקון / לבדיקה filter when one is on (owner answer 6), the draft on
  // screen always kept so "next" is the one after it.
  // Only while working the queue (opened from /drafts or by אשר והבא): a
  // draft opened from a supplier page or a link keeps the whole queue, even
  // with a filter left on in /drafts.
  const queueFilter = () => (reviewEntry === 'queue_edit' || reviewEntry === 'approve_next' ? readGateFilter() : null);
  const workQueue = (rows, keepId) => {
    rememberGateColors(rows, gateColorScope(user?.id, readGatePreview(user?.id)));
    return filterDraftQueue(sortDraftQueue(rows, queueOrder), queueFilter(), keepId);
  };
  // "Every draft handled" is "every red" / "every yellow" while a filter is on.
  const queueDoneWord = () => { const f = queueFilter(); return f ? `כל הטיוטות ${GATE_MARKS[f].label}` : 'כל הטיוטות'; };
  // Whose review this is, by default whatever is on screen now. An approval
  // notes it before its first request, since these refs follow the route; the
  // payload itself is built at the save, for the seconds so far and a gate view
  // that may have been answered meanwhile.
  const reviewOrigin = () => ({ openedAt: openedAtRef.current, docId: currentIdRef.current });
  const reviewPayload = ({ openedAt, docId } = reviewOrigin()) => ({
    ...buildReview({ openedAt, entry: reviewEntry }),
    ...gateReviewFields(gateViewOf(docId), docId, reviewSessionRef.current),
  });
  // Same 640px line as DocumentsPage (which decides autoOpenFiles with it)
  // and the .m-row / table-scroll CSS: at or below it the items render as
  // rows, the original opens full screen instead of in a side pane, and the
  // primary action folds into the ⋮ menu.
  const isMobile = useIsMobile(640);
  // ≤1024px (tablets, a half-width laptop window) is "narrow": the view table
  // (~1000px for its 9 base columns) doesn't fit — it forced the whole PAGE
  // into a sideways scroll — so the items fall back to the phone's rows; and
  // there's no room for a usable data column beside a readable scan, so the
  // original opens straight to full screen. ~1200px once the four credit
  // columns join the table. See compactItems / filePane below.
  const isNarrow = useIsMobile(1024);
  const tableTooWideWithCredits = useIsMobile(1200);
  // The edit screen's split stacks at 900px (.review-split) — below that its
  // action bar is a page-level bottom sheet, above it lives in the items column.
  const narrowEdit = useIsMobile(900);

  const [doc,       setDoc]       = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [editing,   setEditing]   = useState(false);
  // The document's comment thread, reachable from edit mode (any doc type).
  // Opening it neither saves nor discards the edit in progress.
  const [commentsOpen, setCommentsOpen] = useState(false);
  // Notes added from this screen since the document was last loaded; the
  // base count comes with the document (same sum as the list's note cell).
  const [commentsAdded, setCommentsAdded] = useState(0);
  // A note written on a DRAFT for the customer. Customers can't see drafts,
  // so it is held here (not saved) and re-opened with the recipient picker
  // right after the save that approves the draft — same shape as the מרכזת
  // missing-list flow. Page state: lost if the page is left before approval,
  // which the חזור button and a tab close warn about.
  const [pendingCustomerNote, setPendingCustomerNote] = useState(null);
  // { text, next, resolve } while the after-approval thread is open; `next` is
  // the rest of the approval flow (link offers, navigation), run once it
  // closes, and `resolve` settles the approval's own await with it.
  const [afterApprovalNote, setAfterApprovalNote] = useState(null);
  const [form,      setForm]      = useState({});
  const [items,     setItems]     = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [saving,    setSaving]    = useState(false);
  const [vatMode,   setVatMode]   = useState('auto'); // 'auto' | 'manual'
  // True only while the edit form sits in the manual mode it OPENED in by itself
  // (utils/editVatMode.js) — the one case the hint under the VAT row explains.
  // Any click on the toggle is the user's own choice and clears it.
  const [vatOpenedManual, setVatOpenedManual] = useState(false);
  const [showFiles, setShowFiles] = useState(autoOpenFiles);
  const [pageIdx,   setPageIdx]   = useState(0);
  // Desktop only: the original taken from the side pane to full screen (and
  // back). A phone has no pane — full screen is all it has.
  const [fileFull,  setFileFull]  = useState(false);
  // Delivery-note linking modal for a חשבונית מרכזת. null when closed; when
  // open it carries the invoice figures to reconcile against plus an optional
  // `afterClose` so an approval can defer its navigation until the user is
  // done linking (same shape as openCreditsModal below).
  const [linkModal, setLinkModal] = useState(null);
  // Where the approve button's link window leaves to, once a button there decided.
  // Empty after ביטול, so closing it stays on the form with nothing saved.
  const linkDecisionLeaveRef = useRef(null);
  // What the last draft save for that window wrote: `doc` is not refreshed under an
  // open form, and the window lists the notes of the SAVED supplier and type.
  const linkListedForRef = useRef(null);
  // Set when the "open delivery notes" chip retyped this invoice to a מרכזת:
  // the window that chip counted, so the link modal opens on those notes.
  const [convertRange, setConvertRange] = useState(null);
  // Post-approval credit-linking modal for חשבונית זיכוי / תעודת החזרה drafts —
  // mirrors the interactive upload flow so credits that arrive as drafts (bulk
  // upload / folder ingester) still get offered against the supplier's open
  // credits. null when closed; navigation to /drafts is deferred until close.
  const [openCreditsModal, setOpenCreditsModal] = useState(null);
  // Blob URL for the currently-viewed page. The /api/files route requires a
  // JWT header, which iframe/img src can't carry — so we fetch via axios,
  // turn the blob into an object URL, and revoke it on change/unmount.
  const [fileObjectUrl, setFileObjectUrl] = useState('');
  // Admin page turns (utils/usePageRotations.js) — kept off `doc` on purpose.
  const { withRotations, rotatePage: savePageTurn } = usePageRotations(doc, {
    save: (docId, page, rotation) => documentsApi.rotatePage(docId, page, rotation),
    onError: (err) => {
      console.error('[doc-view] saving the page rotation failed', err);
      toast(err?.response?.data?.error || 'לא ניתן לשמור את סיבוב העמוד — נסה שוב', 'error');
    },
  });
  // Last Dokka forward attempt for this document. Null while loading or if no
  // forward has been attempted. Refreshed after manual forward + approval.
  const [forwardInfo,   setForwardInfo]   = useState(null);
  const [forwardSending, setForwardSending] = useState(false);
  // Draft queue for "אשר והבא". null = not loaded.
  // Fetched when the admin edits a draft so the strip can show "N מתוך M"
  // and approval can advance in place instead of bouncing to /drafts.
  const [draftQueue, setDraftQueue] = useState(null);
  // A failed load leaves draftQueue as [] (approve-next falls back to /drafts),
  // which reads like an EMPTY queue; a delete must not claim "כל הטיוטות טופלו"
  // from it, so it fetches again (continueAfterDraftDelete, #303 review).
  const [draftQueueFailed, setDraftQueueFailed] = useState(false);
  // The next draft ready before the reviewer gets there (#315,
  // utils/draftPrefetch.js). The queue carried over from the draft just
  // decided — minus it, unless it was skipped — so the next one shows its
  // "N מתוך M" and can be skipped at once; refetched in the background.
  const carriedQueueRef = useRef(null);
  // The first scan page the prefetch handed over: this page owns the blob
  // from then on and revokes it when the next document supersedes it, or on
  // unmount (handOverScan).
  const [handedScan, setHandedScan] = useState(null);
  // True from a prefetched draft's arrival until its re-check is back: the
  // decisions (approve, skip, save) wait for it, so a draft loaded before the
  // previous approval can never be saved with the prices from before it.
  const [recheckPending, setRecheckPending] = useState(false);
  // The re-check found the draft changed after the reviewer had started on
  // it: their edits stay; an amber note says so and offers to reload.
  const [updatedMeanwhile, setUpdatedMeanwhile] = useState(false);
  // "טען את הגרסה העדכנית" in progress: the conflict (and its lock) stays
  // until the fresh form has committed (Codex review, round 3).
  const [reloadingFresh, setReloadingFresh] = useState(false);
  // The re-check could not be done (twice): the decisions stay locked — a
  // copy loaded before the previous decision is never saved unchecked — and
  // an amber note offers to try again.
  const [recheckFailed, setRecheckFailed] = useState(false);
  const recheckArgsRef = useRef(null);
  const handedScanRef = useRef(null);
  // The catalog match that came with a prefetched document, for openEditForm.
  const warmCatalogRef = useRef(null);
  // draft_ready: { docId, startedAt, cache } from the advance (openNextDraft)
  // until that draft's form is up.
  const readyClockRef = useRef(null);
  // The id on screen, for async work that must not act on a draft left behind.
  const currentIdRef = useRef(id);
  currentIdRef.current = id;
  // The route's generation: bumped on every id change and on unmount. Async
  // work (a load, a form opening, a revalidation) notes it when it starts and
  // commits nothing once it moved on — an answer for 43 must never land on
  // 44's screen, even when the id later comes back to 43 (Codex review, P1).
  const routeGenRef = useRef(0);
  const routeOwner = () => {
    const gen = routeGenRef.current;
    return () => routeGenRef.current === gen;
  };
  // The queue order the reviewer arrived with ({ docId, queue }), kept until
  // the next advance: "the one after" a draft decided elsewhere is read from
  // it, not from a refresh that has already dropped that draft.
  const arrivalQueueRef = useRef(null);
  // The form opening in flight ({ gen, docId, promise }): one per route, so
  // the warm path and the autoEdit effect never open the form twice.
  const editOpeningRef = useRef(null);
  // The draft openNextDraft is moving to, consumed by the [id] effect: only
  // an advance made on this page passes a decided draft over. Router state
  // survives in history, so Back or a reload onto a draft the reviewer just
  // approved must show it, not bounce on with "טופלה במקום אחר".
  const advanceTargetRef = useRef(null);
  // The route generation the autoEdit arrival already opened the form for.
  const autoEditGenRef = useRef(-1);
  // Set when openNextDraft navigates; cleared by the [id] effect.
  const advancingRef = useRef(false);
  const docIsCurrent = !doc || String(doc.id) === String(id);
  // No decision while a copy loaded early is being re-checked (or could not
  // be), while a change found meanwhile waits for the reviewer's choice, or
  // while the screen still holds another document than the URL.
  const decisionsLocked = recheckPending || updatedMeanwhile || !docIsCurrent;
  // This render's queue and edit check, for the background revalidation.
  const latestRef = useRef({ draftQueue: null, saving: false, editing: false, hasEdits: () => false, hasUnsavedWork: () => false });
  // What the edit form opened with (startEdit), so דלג can tell whether the
  // reviewer changed anything (utils/draftEdits.js). null outside edit mode.
  const editBaselineRef = useRef(null);
  // דלג with unsaved changes or a waiting customer note: the confirm dialog.
  // null | { changed } — `changed` false means only the note is at stake.
  const [skipPrompt, setSkipPrompt] = useState(null);
  // ⋮ menu on the title row: בוצע תשלום (manager+) and מחק מסמך (admin).
  const [docMenuOpen, setDocMenuOpen] = useState(false);
  const [askConfirm, confirmDialog] = useConfirm();
  const [newSupplierOptions, setNewSupplierOptions] = useState({ hq_only: false, category_line_id: '' });
  const [newSupplierName, setNewSupplierName] = useState('');
  // "בוצע תשלום" from this page — the same PaymentModal the supplier-documents
  // list opens, with the same rules: never on a draft (the server refuses,
  // and a payment must not double as the review step), only while money is
  // still owed, only for a payable document type, manager+ with payment
  // tracking on. `supplierLifecycle` is the document's OWN month on its
  // supplier, fetched so the modal can offer "אשר לתשלום לחודש" exactly as the
  // supplier-documents page does.
  const [paymentModal, setPaymentModal] = useState(false);
  const [supplierLifecycle, setSupplierLifecycle] = useState(null);
  // A draft's edit form carries a "שולם" checkbox: when ticked, אשר והבא /
  // שמור כלא תקין והבא record the payment right after the approval (the approval is
  // what makes the document payable — paying a draft is refused). The
  // payment is a second request after the approve succeeded; if IT fails the
  // approval stands and the error is shown, never the other way round.
  const [payOnApprove, setPayOnApprove] = useState(NO_PAY_ON_APPROVE);
  // With שולם ticked, אשר והבא first asks for a note on WHY the document is
  // considered paid (a quick chip "הופיע שולם על המסמך" is the usual answer).
  // The note is stored as the payment's notes — who recorded it and when come
  // with the payment row — so it shows on the paid tab, in the payment
  // history and in the document's comments thread, for every role.
  // `{ goAfter }` remembers which approve button was clicked.
  const [payNoteModal, setPayNoteModal] = useState(null);
  const [payNote, setPayNote] = useState('');
  const payNoteModalRef = useRef(null);
  payNoteModalRef.current = payNoteModal;
  const docMonth = monthRangeFromDocDate(doc?.date);
  const canPay = Boolean(
    paymentTrackingEnabled && canEdit && doc && doc.is_payable && doc.supplier_id &&
    doc.status !== 'טיוטה' && Number(doc.payment_remaining) > 0.005,
  );
  const lifecycleToken = useRef(0);
  // "מאושר לתשלום" from the document itself — the SAME supplier-month approval
  // the supplier page header toggles (POST /suppliers/:id/approve), scoped to
  // the month of this document's date. It approves the whole month for the
  // supplier, not this one document; the label beside the badge says which
  // month. Ora's call, 2026-10-01: approve from where the document was checked.
  const [approvingMonth, setApprovingMonth] = useState(false);
  const toggleMonthApproval = async () => {
    const range = monthRangeFromDocDate(doc?.date);
    if (approvingMonth || !supplierLifecycle || !range || !doc?.supplier_id) return;
    setApprovingMonth(true);
    try {
      const res = await suppliersApi.approve(doc.supplier_id, range.dateFrom, range.dateTo);
      if (res.data?.lifecycle) setSupplierLifecycle(res.data.lifecycle);
      toast(res.data?.approved
        ? `${monthLabelFromDocDate(doc.date)} אושר לתשלום`
        : `האישור לתשלום ל-${monthLabelFromDocDate(doc.date)} בוטל`);
    } catch (err) {
      toast(err?.response?.data?.error || 'שגיאה בעדכון אישור', 'error');
    } finally {
      setApprovingMonth(false);
    }
  };
  // The link modal's "אשר גם לתשלום" offer: the invoice's supplier-month, when
  // that month is not approved yet. Same rule as the draft form's box
  // (approvalOfferForDoc), judged on the lifecycle this page already holds —
  // which is the ORIGINAL supplier's, so a re-assigned invoice gets no offer.
  const linkMonthApproval = (lm) => {
    if (!paymentTrackingEnabled || !canEdit || !lm?.supplierId) return null;
    if (String(lm.supplierId) !== String(doc?.supplier_id || '')) return null;
    const range = monthRangeFromDocDate(lm.docDate);
    if (!range) return null;
    const offer = approvalOfferForDoc({ lifecycle: supplierLifecycle, docDate: lm.docDate, docType: 'חשבונית מרכזת', docTotal: lm.invoiceTotal, dateFrom: range.dateFrom, dateTo: range.dateTo });
    return offer ? { supplierId: lm.supplierId, dateFrom: range.dateFrom, dateTo: range.dateTo, monthLabel: offer.monthLabel } : null;
  };
  const loadSupplierLifecycle = (d) => {
    const token = ++lifecycleToken.current;
    const range = monthRangeFromDocDate(d?.date);
    if (!d?.supplier_id || !range || !paymentTrackingEnabled) { setSupplierLifecycle(null); return; }
    suppliersApi.getOne(d.supplier_id, { date_from: range.dateFrom, date_to: range.dateTo })
      .then(r => { if (token === lifecycleToken.current) setSupplierLifecycle(r.data?.lifecycle ?? null); })
      .catch(() => { if (token === lifecycleToken.current) setSupplierLifecycle(null); });
  };

  // A failed load is not "no such document". A 404 IS (EmptyState below); any
  // other failure — network, 500 — keeps `doc` as it was and shows a retry,
  // rather than rejecting unhandled (a dev crash overlay) or claiming the
  // document does not exist. The stale-tenant 403 never reaches here: the api
  // interceptor redirects to /suppliers and swallows it.
  const [loadError, setLoadError] = useState(false);
  const [accessDenied, setAccessDenied] = useState('');
  // The server's 404 text: 'המסמך נמחק' for a soft-deleted document (an old
  // link, an email) says more than a bare "not found".
  const [missingMessage, setMissingMessage] = useState('');

  // Returns the freshly-loaded doc as well as storing it: `doc` in a handler's
  // closure is still the pre-save value after an await, so a post-save
  // decision (see saveEdit's linking offer) has to read the returned copy.
  // Resolves to null on failure so callers can bail without a second catch.
  // An arrival whose early load was still in flight reads the document fresh
  // and lets the form ask its own catalog match: a match fetched before the
  // decision — or before another reviewer's approval of this supplier — is
  // never trusted (Codex review, rounds 1 and 3). `skipDecided`: arriving by
  // אשר והבא, a document that is no longer a draft — decided or deleted by
  // someone else meanwhile — is passed over, never shown (leaveDecidedDraft);
  // the spinner stays until the page moves. Nothing is committed once the
  // route has moved on (routeGenRef).
  const load = async ({ skipDecided = false } = {}) => {
    setLoadError(false);
    setAccessDenied('');
    const requestedId = String(id);
    const gen = routeGenRef.current;
    const owns = () => routeGenRef.current === gen;
    let leaving = false;
    try {
      const d = (await documentsApi.getOne(id)).data;
      if (!owns()) return null;
      if (skipDecided && d && d.status !== 'טיוטה') {
        leaving = true;
        leaveDecidedDraft(requestedId);
        return null;
      }
      setDoc(d);
      loadSupplierLifecycle(d);
      return d;
    } catch (err) {
      if (!owns()) return null;
      if (skipDecided && err?.response?.status === 404) {
        leaving = true;
        leaveDecidedDraft(requestedId);
        return null;
      }
      if (err?.response?.data?.code === 'HQ_ONLY_SUPPLIER') setAccessDenied(err.response.data.error);
      else if (err?.response?.status === 404) { setMissingMessage(err.response.data?.error || ''); setDoc(null); }
      else setLoadError(true);
      return null;
    } finally {
      if (!leaving && owns()) setLoading(false);
    }
  };

  // Honor the selected supplier's VAT rule (same logic as the upload review
  // screen, via the shared detectVatRule). Without this, opening a draft from a
  // VAT-inclusive or partial-VAT supplier silently recomputes 18% and overwrites
  // the saved VAT — the exact bug that made bulk-uploaded produce drafts flip to
  // full VAT the moment they were opened for review.
  const ruleSupplier = suppliers.find(s => String(s.id) === String(form.supplier_id));
  const { vatInclusive: supplierVatInclusive, partialVat: supplierPartialVat } = detectVatRule(ruleSupplier?.invoice_rules, form.doc_type);

  // Answers for a document left meanwhile are dropped.
  const loadForwardStatus = () => {
    const forId = String(id);
    documentsApi.forwardStatus(id)
      .then(r => { if (currentIdRef.current === forId) setForwardInfo(r.data.forward); })
      .catch(() => { if (currentIdRef.current === forId) setForwardInfo(null); });
  };

  // On id change (including "אשר והבא" in-place advancement) reset the edit
  // state — without this, navigating from one draft to the next would keep the
  // previous doc's form/items on screen until the new doc's autoEdit kicks in.
  //
  // The next draft of the queue may be held ready (utils/draftPrefetch.js):
  // with its document in hand it shows at once — no spinner, the form open in
  // the same render — and is revalidated in the background; still loading,
  // the request in flight is awaited instead of a new one. Its first scan
  // page, when held, goes to the pane. A draft opening straight into the
  // review form skips forward-status (a draft is never forwarded).
  useEffect(() => {
    routeGenRef.current += 1;
    lifecycleToken.current += 1; // the previous document's lifecycle answer is void
    editOpeningRef.current = null;
    const advancedHere = advanceTargetRef.current === String(id);
    advanceTargetRef.current = null;
    openedAtRef.current = null;
    editBaselineRef.current = null; setSkipPrompt(null);
    // A question asked about the previous document answers nothing on this one.
    askConfirm(null);
    setEditing(false); setForm({}); setItems([]); setVatMode('auto');
    // Never the previous document's: the warm form is interactive at once and
    // its אשר גם לתשלום offer reads the lifecycle.
    advancingRef.current = false;
    setForwardInfo(null); setSaving(false); setSupplierLifecycle(null);
    setRecheckPending(false); setUpdatedMeanwhile(false); setRecheckFailed(false); setReloadingFresh(false);
    setPageIdx(0); setShowFiles(autoOpenFiles); setFileFull(false);
    setCommentsOpen(false); setPendingCustomerNote(null); setAfterApprovalNote(null);
    // Browser Back with the link window open: its buttons act on the route's document.
    setLinkModal(null); linkDecisionLeaveRef.current = null; linkListedForRef.current = null;
    setConvertRange(null);
    warmCatalogRef.current = null;
    const held = takeDraftPrefetch({ rid: tabRestaurantId(), docId: id });
    // Nothing for this draft: whatever is held belongs to a draft not
    // reached — free its blob now rather than at unmount.
    if (!held) dropDraftPrefetch();
    if (held?.scanPending) {
      // Still downloading: the pane waits for it (prefetched.pending) rather
      // than fetching the same page again.
      const { filePath, promise } = held.scanPending;
      const gen = routeGenRef.current;
      handOverScan({ filePath, url: '', pending: true });
      promise.then(scan => {
        if (routeGenRef.current !== gen) { if (scan?.url?.startsWith('blob:')) URL.revokeObjectURL(scan.url); return; }
        handOverScan(scan);
      });
    } else {
      handOverScan(held?.scan || null);
    }
    const warm = autoEdit && held?.value?.doc?.status === 'טיוטה' ? held.value : null;
    const clock = readyClockRef.current;
    if (clock && clock.docId === String(id)) clock.cache = warm ? 'warm' : (held?.promise ? 'in_flight' : 'none');
    if (warm) { autoEditGenRef.current = routeGenRef.current; showWarmDraft(warm); return; }
    setLoading(true);
    load({ skipDecided: autoEdit && advancedHere })
      .then(d => { if (d && !(autoEdit && d.status === 'טיוטה')) loadForwardStatus(); });
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Hand the prefetched scan page to the pane, revoking the one it replaces.
  const handOverScan = (scan) => {
    const prev = handedScanRef.current;
    handedScanRef.current = scan;
    setHandedScan(scan);
    if (prev?.url && prev.url !== scan?.url && prev.url.startsWith('blob:')) URL.revokeObjectURL(prev.url);
  };
  // Set in the effect, not only at creation: StrictMode's dev remount runs
  // the cleanup once and must not leave the page thinking it is gone.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      routeGenRef.current += 1;
      dropDraftPrefetch();
      const h = handedScanRef.current;
      handedScanRef.current = null;
      if (h?.url && h.url.startsWith('blob:')) URL.revokeObjectURL(h.url);
    };
  }, []);

  // A prefetched next draft, shown now. The review clock starts here, when it
  // is on screen (openEditForm), never at prefetch time.
  // It was loaded early, before the previous decision was written, so the
  // decisions wait for the re-check (recheckPending).
  const showWarmDraft = ({ doc: d, catalog }) => {
    setDoc(d);
    setLoading(false);
    loadSupplierLifecycle(d);
    warmCatalogRef.current = { docId: String(d.id), catalog };
    setRecheckPending(true);
    openEditForm(d).catch(err => {
      console.error('[doc-view] could not open the prefetched draft', err);
      toast('לא ניתן לפתוח את הטופס לעריכה — נסה שוב', 'error');
    });
    // A held catalog match is never trusted: catalog prices move with any
    // approval of this supplier — this reviewer's or another's (Codex review,
    // round 3) — so the re-check always verifies the match, whether the held
    // one was used or the form had to ask its own (best-effort) one.
    revalidateWarmDraft(d);
  };

  // Did the decision just written move this draft's catalog prices? Only an
  // approval of the same supplier (or the one it was moved from) does.

  // The re-check of a draft loaded early: the server's copy, read after the
  // previous decision was written (it may have back-filled this draft's
  // prices), and — when that decision moved this supplier's catalog — a
  // fresh catalog match. Decided elsewhere meanwhile, or deleted (a 404 is
  // an answer, not a failure — Codex review) → on to the one after. Changed →
  // reopen from the fresh copy, unless the reviewer has already started on
  // it: their edits stay and the amber note says so. Same copy, new match →
  // the price indicators are updated in place. The decisions unlock when it
  // is done; any other failure keeps what is on screen.
  //
  // Fails closed: each try is bounded (RECHECK_TIMEOUT_MS) and retried once;
  // after that the decisions stay locked under an amber "try again" note —
  // an unchecked copy is never saved (self-review). `recheckAttemptRef`
  // drops the answer of a try that timed out.
  const recheckAttemptRef = useRef(0);
  const revalidateWarmDraft = (cached) => {
    recheckArgsRef.current = { cached };
    const rematch = Boolean(cached.supplier_id) && reviewBaseItems(cached).length > 0;
    const docId = String(cached.id);
    const gen = routeGenRef.current;
    const tryOnce = () => {
      const attempt = ++recheckAttemptRef.current;
      const live = () => routeGenRef.current === gen && recheckAttemptRef.current === attempt;
      // The fresh match is asked beside the read, on the held lines: used
      // only if the read finds the same copy (a changed one reopens and asks
      // for its own). One round trip of lock instead of two.
      const rematchP = rematch && cached.supplier_id
        ? catalogApi.match(cached.supplier_id, reviewBaseItems(cached), { pricesOnly: true })
        : null;
      if (rematchP) rematchP.catch(() => {}); // awaited below, or not needed
      const check = async () => {
        const fresh = (await documentsApi.getOne(docId)).data;
        if (!live()) return;
        if (!fresh) throw new Error('empty re-check answer');
        if (fresh.status !== 'טיוטה') { leaveDecidedDraft(docId); return; }
        if (draftFingerprint(fresh) !== draftFingerprint(cached)) {
          setDoc(fresh);
          // Left the form (ביטול) before the answer: view mode shows the
          // fresh copy, and the form is not brought back — unless the first
          // opening is still in flight, which the reopen then replaces.
          if (!latestRef.current.editing && !editOpeningRef.current) return;
          if (latestRef.current.hasEdits()) { setUpdatedMeanwhile(true); return; }
          // Reopened in place (it asks for its own match — strictly: a failed
          // match fails this check): edit mode and the queue stay up meanwhile.
          const outcome = await openEditForm(fresh, { reopen: true, strict: true });
          if (outcome === 'gave-way' && live()) setUpdatedMeanwhile(true);
          return;
        }
        if (!rematchP) return;
        const m = await rematchP;
        if (!live()) return;
        // The form may still be opening (its own match when the held one had
        // failed): the verified prices are laid on once it is in, never under it.
        const opening = editOpeningRef.current;
        if (opening && opening.gen === gen) await opening.promise.catch(() => {});
        if (!live()) return;
        const enriched = m.data || [];
        setItems(prev => patchCatalogPrices(prev, enriched));
        if (editBaselineRef.current) {
          editBaselineRef.current = { ...editBaselineRef.current, items: patchCatalogPrices(editBaselineRef.current.items, enriched) };
        }
      };
      return settleOrFail(check(), RECHECK_TIMEOUT_MS).then(() => 'done', (err) => {
        if (!live()) return 'stale';
        if (err?.response?.status === 404) { leaveDecidedDraft(docId); return 'done'; }
        console.warn('[draft-prefetch] re-check failed', err?.message || err);
        return 'failed';
      });
    };
    setRecheckFailed(false);
    setRecheckPending(true);
    tryOnce()
      .then(r => (r === 'failed' ? tryOnce() : r))
      .then(r => {
        if (routeGenRef.current !== gen || r === 'stale') return;
        if (r === 'failed') { setRecheckFailed(true); return; } // stays locked
        setRecheckPending(false);
      });
  };
  // The reviewer chose the server's copy: the decisions stay locked (the
  // conflict stays up) until the replacement form has committed — its
  // catalog match can take a moment, and approving meanwhile sent the old
  // form (Codex review, round 3). A failed reload keeps the choice open.
  const reloadFreshCopy = () => {
    const gen = routeGenRef.current;
    setReloadingFresh(true);
    // Strict: a failed catalog match keeps the choice open (and the lock).
    openEditForm(doc, { reopen: true, force: true, strict: true })
      .then(outcome => { if (routeGenRef.current === gen && outcome === 'opened') setUpdatedMeanwhile(false); })
      .catch(err => {
        console.error('[doc-view] could not reload the draft', err);
        if (routeGenRef.current === gen) toast('לא ניתן לטעון את הגרסה העדכנית — נסה שוב', 'error');
      })
      .finally(() => { if (routeGenRef.current === gen) setReloadingFresh(false); });
  };
  const retryRecheck = () => {
    const args = recheckArgsRef.current;
    if (args) revalidateWarmDraft(args.cached);
  };

  // The draft we advanced to was decided by someone else: pass it over, like
  // a stale row on /drafts, and go on to the one after it.
  const leaveDecidedDraft = (docId) => {
    if (currentIdRef.current !== docId) return;
    // "The one after" is read in the order the reviewer arrived with, the
    // decided draft still in it — without it nextDraftId starts over from the
    // top, and a refresh that landed first has already dropped it (Codex
    // review). Drafts that refresh no longer lists are gone too.
    const arrival = arrivalQueueRef.current?.docId === docId ? arrivalQueueRef.current.queue : null;
    const live = latestRef.current.draftQueue;
    let order = arrival || carriedQueueRef.current || live || [];
    if (arrival && live) {
      const alive = new Set(live.map(d => String(d.id)));
      order = order.filter(d => String(d.id) === docId || alive.has(String(d.id)));
    }
    const nextId = nextDraftId(order, docId, skippedDraftIds());
    const queue = order.filter(d => String(d.id) !== docId);
    if (nextId) {
      toast('הטיוטה כבר טופלה במקום אחר · עוברים לבאה');
      openNextDraft(nextId, queue).catch(err => console.error('[doc-view] could not move to the next draft', err));
      return;
    }
    const left = skippedRemaining(queue, docId, skippedDraftIds());
    toast(`הטיוטה כבר טופלה במקום אחר · ${left > 0 ? `נותרו ${left} שדילגת עליהן` : `${queueDoneWord()} טופלו`}`);
    navigate('/drafts');
  };

  // An auto-opened original counts as opened once the document is in hand —
  // only in view mode (a draft-queue advance also carries autoOpenFiles but
  // lands in edit mode, where this viewer never renders) and only when there
  // is a file to open. Once per document.
  const autoOpenTracked = useRef(null);
  useEffect(() => {
    if (!doc || !autoOpenFiles || autoEdit || autoOpenTracked.current === doc.id) return;
    if (!(doc.files?.length > 0 || doc.file_path)) return;
    autoOpenTracked.current = doc.id;
    track(EVENTS.DOCUMENT_FILE_OPENED, { device: isMobile ? 'mobile' : 'desktop', trigger: 'auto' });
  }, [doc]); // eslint-disable-line react-hooks/exhaustive-deps

  // Closing the tab with a note still waiting, or with unsaved edits (#369),
  // would drop them silently. The edits are read at the moment of closing, so
  // an open form nobody touched closes without a prompt. A layout effect so the
  // listener is gone by the time a restaurant change reloads: TenantUrlProvider
  // has emptied the page by then, and Cancel on a prompt would leave it blank.
  useLayoutEffect(() => {
    if (!pendingCustomerNote && !editing) return undefined;
    const onUnload = (e) => {
      if (unloadGuardSuspended()) return;
      if (!pendingCustomerNote && !latestRef.current.hasUnsavedWork()) return;
      e.preventDefault(); e.returnValue = '';
    };
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, [pendingCustomerNote, editing]);

  // Every approval runs the rest of its flow through here, so a waiting
  // customer note is offered first — the document is out of טיוטה by now.
  // Returns a promise the caller AWAITS: the approval keeps `saving` (and its
  // own try/catch) until the rest of the flow has run — with a waiting note,
  // that is after the thread closes. Not awaiting it re-enabled the approve
  // buttons mid-flow, where a second click re-ran the approval (a second PUT
  // and a second recorded payment).
  const continueAfterPendingNote = (next) => {
    if (!pendingCustomerNote) return next();
    const text = pendingCustomerNote;
    setPendingCustomerNote(null);
    // The thread can finish sending after the route has moved and its dialog
    // has gone (CommentsModal calls back regardless). The rest of the flow takes
    // its own owner when it starts, so it must not start on the new document.
    const stillHere = routeOwner();
    return new Promise(resolve => setAfterApprovalNote({ text, stillHere, next: () => (stillHere() ? next() : undefined), resolve }));
  };
  const finishAfterApprovalNote = () => {
    const { next, resolve } = afterApprovalNote || {};
    setAfterApprovalNote(null);
    resolve?.(Promise.resolve().then(next));
  };

  // A reload already counts what was added here — start over from it.
  useEffect(() => { setCommentsAdded(0); }, [doc]);
  const commentCount = (doc?.comment_count || 0) + (doc?.payment_note_count || 0) + commentsAdded;
  // Shown in both action rows (edit and view), since a waiting note survives
  // leaving edit mode and the view-mode אשר טיוטה sends it too.
  const pendingNoteChip = pendingCustomerNote ? (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', color: 'var(--warning-ink)' }}>
      <Icon name="mail-send" size={14} /> הערה ממתינה לשליחה ללקוח אחרי האישור
      <button type="button" className="btn btn-sm btn-outline" onClick={() => setPendingCustomerNote(null)}>בטל שליחה</button>
    </span>
  ) : null;

  // Load the draft queue for approve-and-next (flag-on edit mode on a draft).
  // Arriving from the previous draft, its queue is used at once and refreshed
  // in the background; a failed refresh keeps it.
  // `docIsCurrent`: the first render after an in-place advance still holds the
  // previous draft (and its edit mode) until the [id] reset lands — effects
  // reading `doc` / `editing` must not act on it.
  useEffect(() => {
    if (!docIsCurrent) return;
    if (!isAdmin || !editing || doc?.status !== 'טיוטה') {
      setDraftQueue(null);
      return;
    }
    let cancelled = false;
    const carried = carriedQueueRef.current;
    carriedQueueRef.current = null;
    if (carried) setDraftQueue(carried);
    setDraftQueueFailed(false);
    documentsApi.getDrafts({ gatePreview: readGatePreview(user?.id) })
      .then(r => { if (!cancelled) setDraftQueue(workQueue(Array.isArray(r.data) ? r.data : [], id)); })
      .catch(() => { if (!cancelled && !carried) { setDraftQueue([]); setDraftQueueFailed(true); } });
    return () => { cancelled = true; };
  }, [id, editing, isAdmin, doc?.status, docIsCurrent]); // eslint-disable-line react-hooks/exhaustive-deps

  // Early, while the reviewer reads this draft: the next one — its document,
  // catalog match and first scan page (utils/draftPrefetch.js). What the
  // decision may change (back-filled prices, catalog prices) is re-checked
  // when it is shown, with the decisions locked until then (showWarmDraft).
  // The queue row carries the first page's path.
  useEffect(() => {
    // Not while this draft's own re-check is out: the load of the one after
    // would queue beside it on the server and hold the lock longer.
    if (!docIsCurrent || !editing || recheckPending || doc?.status !== 'טיוטה' || !draftQueue) return;
    // On the way out the carried queue (without this draft) can commit before
    // the route changes; "next" would then read from the top and drop the
    // draft actually held.
    if (!draftQueue.some(d => String(d.id) === String(id))) return;
    const nextId = nextDraftId(draftQueue, id, skippedDraftIds());
    const row = nextId ? draftQueue.find(d => String(d.id) === String(nextId)) : null;
    if (!nextId) return;
    prefetchDraftDoc({ rid: tabRestaurantId(), docId: nextId, load: () => loadDraftForReview(nextId) });
    if (row?.file_path) {
      prefetchDraftScan({ rid: tabRestaurantId(), docId: nextId, filePath: row.file_path, fetchUrl: fetchDocFileObjectUrl });
    }
  }, [draftQueue, editing, recheckPending, doc?.status, id, docIsCurrent]);

  // draft_ready: from the advance to the next draft's form being up — the
  // wait the prefetch removes. review_seconds cannot show it: the clock
  // starts when the draft is on screen. `cache`: what the prefetch had ready.
  useEffect(() => {
    const clock = readyClockRef.current;
    if (!docIsCurrent || !editing || !clock || clock.docId !== String(id)) return;
    readyClockRef.current = null;
    track(EVENTS.DRAFT_READY, { ready_ms: Math.max(0, Math.round(performance.now() - clock.startedAt)), cache: clock.cache || 'none' });
  }, [editing, id, docIsCurrent]);

  // Turn a server backfill summary { documents_updated, lines_filled,
  // lines_from_invoice, lines_still_zero } into a Hebrew toast line, or null
  // when nothing happened.
  const describeBackfill = (b) => {
    if (!b || !b.documents_updated) return null;
    const parts = [`עודכנו ${b.documents_updated} תעודות`];
    if (b.lines_from_invoice) parts.push(`${b.lines_from_invoice} שורות קיבלו מחיר מהחשבונית`);
    if (b.lines_filled) parts.push(`מולאו ${b.lines_filled} שורות ללא מחיר`);
    if (b.lines_still_zero) parts.push(`${b.lines_still_zero} שורות עדיין ללא מחיר`);
    return parts.join(' · ');
  };

  const runBackfill = async () => {
    try {
      const r = await documentsApi.backfillDeliveryPrices(id);
      toast(describeBackfill(r.data) || 'לא נמצאו תעודות עם מחיר 0 למילוי');
    } catch {
      toast('שגיאה במילוי מחירים', 'error');
    }
  };

  const handleManualForward = async () => {
    if (forwardSending) return;
    setForwardSending(true);
    try {
      const r = await documentsApi.forwardToDokka(id);
      track(EVENTS.DOCUMENT_FORWARDED_TO_DOKKA, {
        outcome: r.data.status === 'already_sent' ? 'already_sent' : 'sent',
        trigger: 'manual',
        doc_type: doc?.doc_type,
      });
      if (r.data.status === 'already_sent') {
        toast('המסמך כבר הועבר להנהלת חשבונות');
      } else {
        toast('המסמך נשלח להנהלת חשבונות');
      }
      loadForwardStatus();
    } catch (err) {
      track(EVENTS.DOCUMENT_FORWARDED_TO_DOKKA, { outcome: 'failed', trigger: 'manual', doc_type: doc?.doc_type });
      const msg = err.response?.data?.error || 'שגיאה בשליחה להנהלת חשבונות';
      toast(msg, 'error');
    } finally {
      setForwardSending(false);
    }
  };

  // The same reason dialog as DraftsPage's מחק (components/DeleteDocumentDialog
  // .jsx); the only difference is where we land afterward: this page IS the
  // document being deleted, so it navigates to the supplier's document list
  // (the same place the breadcrumb above links to). The review timing goes
  // with it, so a draft thrown away from the review screen is timed like an
  // approval.
  // false | true (the ⋮ menu) | 'duplicate' (the compare screen's "זה כפיל ·
  // מחק": the reason is pre-picked and the delete returns to the queue).
  const [deleting, setDeleting] = useState(false);
  // The move dialog (#304, components/MoveDocumentDialog): from the recipient
  // chip, the ⋮ menu, or the delete dialog's לא שייך למסעדה הזו callout.
  const [moving, setMoving] = useState(false);
  // ⋮ → צור תעודת החזרה בנוסף (#367, components/ReturnNoteDialog). Both hold the
  // id of the document they belong to, so a route advance leaves neither behind.
  // The created draft is its own state rather than a setDoc: a new `doc` resets
  // this session's comment count and refetches an open scan.
  const [creatingReturnFor, setCreatingReturnFor] = useState(null);
  const [createdReturn, setCreatedReturn] = useState(null); // { forDocId, id, doc_num }
  // The duplicate compare screen (DuplicateCompareDialog, #234), opened from
  // the view-mode banner or the edit-mode issue chip.
  const [comparing, setComparing] = useState(false);
  // "השאר את הטיוטה · מחק את הקיים": the existing document (duplicate_of)
  // while its reason dialog is up. Once it is deleted the draft reloads — the
  // server lifted its duplicate flag (lib/softDelete.js) — and opens for
  // editing, since keeping it means fixing and approving it.
  const [deletingExisting, setDeletingExisting] = useState(null);
  const [focusDocNumPending, setFocusDocNumPending] = useState(focusDocNumOnArrive);
  // "לא כפיל · שנה מספר מסמך": the form is the only place the number changes,
  // so open it if needed, then put the cursor in the field.
  useEffect(() => {
    if (!editing || !focusDocNumPending) return;
    const el = document.getElementById('ri-doc_num');
    if (el) { el.focus(); el.select?.(); }
    setFocusDocNumPending(false);
  }, [editing, focusDocNumPending]);
  // The arrival state is for a draft's auto-opened form only: on a document
  // that no longer is one (a stale queue) it must not lie in wait and steal
  // focus from a later manual עריכה. Same for the dialog: once a reload says
  // there is no duplicate any more, there is nothing to compare.
  useEffect(() => {
    if (doc && doc.status !== 'טיוטה') setFocusDocNumPending(false);
    if (doc && !doc.duplicate_of) setComparing(false);
  }, [doc]);
  const changeDocNumber = async () => {
    setComparing(false);
    try {
      if (!editing) await startEdit();
    } catch (err) {
      // startEdit loads the supplier list first; a failed load must not
      // leave the user staring at the view mode with no explanation.
      console.error('[doc-view] could not open the form', err);
      toast('לא ניתן לפתוח את הטופס לעריכה — נסה שוב', 'error');
      return;
    }
    setFocusDocNumPending(true);
  };
  // The form opens on the render AFTER the reload: startEdit reads `doc` from
  // its render, and this handler's closure still holds the pre-delete copy.
  const [editAfterReload, setEditAfterReload] = useState(false);
  const keepDraftAfterDelete = async () => {
    setDeletingExisting(null);
    const fresh = await load();
    if (fresh && fresh.status === 'טיוטה') setEditAfterReload(true);
  };
  useEffect(() => {
    if (!editAfterReload || !doc) return;
    setEditAfterReload(false);
    if (editing) return;
    startEdit().catch(err => {
      console.error('[doc-view] could not open the form after deleting the existing document', err);
      toast('לא ניתן לפתוח את הטופס לעריכה — נסה שוב', 'error');
    });
  }, [editAfterReload, doc]); // eslint-disable-line react-hooks/exhaustive-deps
  const handleDeleteDocument = () => setDeleting(true);

  // Fetch the currently-viewed file as a blob (carries JWT/restaurant headers)
  // and expose it via an object URL the iframe/img/<a> can consume. External
  // URLs (Cloudinary) pass through. Revoke prior object URLs so blobs don't
  // accumulate as the user pages through a multi-page doc. View mode only:
  // the edit form's ReviewDocPane fetches its own page, and a draft arriving
  // from the queue carries autoOpenFiles — every scan used to download twice.
  useEffect(() => {
    if (!doc || !showFiles || editing) { setFileObjectUrl(''); return; }
    const files = (doc.files && doc.files.length > 0)
      ? doc.files
      : (doc.file_path ? [{ file_path: doc.file_path, page_number: 1 }] : []);
    if (files.length === 0) { setFileObjectUrl(''); return; }
    const idx = Math.min(Math.max(0, pageIdx), files.length - 1);
    const fp  = files[idx]?.file_path;
    if (!fp) { setFileObjectUrl(''); return; }

    let cancelled = false;
    let createdUrl = '';
    fetchDocFileObjectUrl(fp).then(u => {
      if (cancelled) {
        if (u && u.startsWith('blob:')) URL.revokeObjectURL(u);
        return;
      }
      createdUrl = u && u.startsWith('blob:') ? u : '';
      setFileObjectUrl(u || '');
    }).catch(err => {
      if (!cancelled) {
        console.error('[file-fetch]', err);
        setFileObjectUrl('');
      }
    });
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [doc, pageIdx, showFiles, editing]);

  // A draft on screen is a review in progress from the moment it loads, even
  // before עריכה — reading the scan is part of the time being measured.
  useEffect(() => {
    if (doc?.status === 'טיוטה' && openedAtRef.current == null) openedAtRef.current = Date.now();
  }, [doc]);

  // When the user arrives from /drafts with autoEdit, jump straight into
  // edit mode once the doc and (already-cached) suppliers are available.
  // Once per route (autoEditGenRef): after ביטול, a later reload of the
  // document — a revalidation, a save elsewhere — must not reopen the form.
  // Only for a draft: the queue can be stale (another admin approved or
  // deleted the next one), and the form must not open on a תקין document
  // as if it were the next draft.
  useEffect(() => {
    if (!autoEdit || !doc || editing || doc.status !== 'טיוטה' || !docIsCurrent) return;
    if (autoEditGenRef.current === routeGenRef.current) return;
    autoEditGenRef.current = routeGenRef.current;
    startEdit();
  }, [autoEdit, doc]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-recalculate subtotal (always) and vat (by mode and supplier rule) when
  // items change — the same formula the form opened with (utils/openingForm.js
  // formTotals), so on an untouched opening this changes nothing.
  useEffect(() => {
    if (!editing) return;
    const vatRule = { vatInclusive: supplierVatInclusive, partialVat: supplierPartialVat };
    // No lines now: a document that opened with none goes back to its own net. One
    // whose lines were all removed has nothing behind its old net any more (#385).
    const base = editBaselineRef.current;
    setForm(f => {
      const from = items.length > 0 || !base ? f
        : base.items.length > 0 ? { ...f, subtotal: 0, total: 0 }
        : { ...f, subtotal: base.form.subtotal, total: base.form.total };
      return { ...f, ...formTotals(from, items, { vatMode, vatRate, vatRule }) };
    });
  }, [items, editing, vatMode, vatRate, supplierVatInclusive, supplierPartialVat]); // eslint-disable-line react-hooks/exhaustive-deps

  // Flip the VAT toggle to manual when a supplier rule governs VAT, so the
  // on-screen toggle matches the math (the user can still override).
  useEffect(() => {
    if (!editing) return;
    if ((supplierVatInclusive || supplierPartialVat) && vatMode !== 'manual') {
      setVatMode('manual');
    }
  }, [supplierVatInclusive, supplierPartialVat, editing]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-change status when credit_qty changes.
  //
  // The clear branch is deliberately NARROWER than the raise branch: it only
  // undoes a 'לא תקין' THIS editing session set. 'לא תקין' has more than one
  // producer — a חשבונית מרכזת whose linked delivery notes don't reconcile is
  // flagged by the link modal, and such a document has no credit lines at all,
  // so an unconditional clear wiped that flag the moment anyone clicked ערוך
  // and the next save persisted it. Only the person who raised a flag gets to
  // lower it automatically; a status that arrived from the server is cleared
  // by hand, through the status field.
  //
  // Not on a draft (#260): a draft keeps טיוטה while credits come and go, and
  // the review bar files a credit document as לא תקין at the decision itself
  // (approveCurrent; the server enforces the same rule). Flipping the field
  // here made Ctrl+S file the draft as לא תקין, and removing the credit then
  // set it to תקין — out of the queue either way.
  const autoInvalidRef = useRef(false);
  useEffect(() => {
    if (!editing || doc?.status === 'טיוטה') { autoInvalidRef.current = false; return; }
    const hasCredits = items.some(it =>
      (parseFloat(it.credit_qty) || 0) > 0 || (parseFloat(it.credit_price_diff) || 0) > 0
    );
    // The ref writes sit inside the updater so they can't disagree with the
    // branch actually taken. Idempotent under a double-invoked updater: both
    // runs see the same `f` and take the same branch.
    setForm(f => {
      if (hasCredits && f.status !== 'לא תקין') {
        autoInvalidRef.current = true;
        return { ...f, status: 'לא תקין' };
      }
      if (!hasCredits && f.status === 'לא תקין' && autoInvalidRef.current) {
        autoInvalidRef.current = false;
        return { ...f, status: 'תקין' };
      }
      return f;
    });
  }, [items, editing, doc?.status]);

  // `d` is the document to open — the one on screen (startEdit), or the next
  // draft handed over by the prefetch before it is committed to `doc`, so the
  // form opens in the same render as the document (showWarmDraft).
  //
  // One opening per route and document (editOpeningRef): a second call while
  // one is in flight joins it — the warm path and the autoEdit effect used to
  // each start one when the catalog match had to be asked again, and the
  // later answer wiped what the reviewer had typed meanwhile (Codex review).
  // `reopen` (a revalidation found the draft changed) replaces the one in
  // flight, and gives way if the reviewer starts typing before it lands. An
  // opening whose route moved on commits nothing.
  const startEdit = () => openEditForm(doc);
  // `strict`: the opening is part of a required check (a re-check's reopen,
  // "טען את הגרסה העדכנית") — a failed catalog match fails the opening, so
  // the caller's retry / error state keeps the decisions locked, instead of
  // a form with unverified price flags reporting 'opened' (Codex review,
  // round 4). An ordinary opening stays best-effort, as it always was.
  const openEditForm = (d, { reopen = false, force = false, strict = false } = {}) => {
    const gen = routeGenRef.current;
    const cur = editOpeningRef.current;
    if (!reopen && cur && cur.gen === gen && cur.docId === String(d.id)) return cur.promise;
    const opening = { gen, docId: String(d.id), promise: null };
    editOpeningRef.current = opening;
    opening.promise = initEditForm(d, opening, reopen, force, strict).finally(() => {
      if (editOpeningRef.current === opening) editOpeningRef.current = null;
    });
    return opening.promise;
  };
  // Resolves 'opened' once the form is committed, 'gave-way' when a reopen
  // stood back for the reviewer's typing, undefined when superseded.
  const initEditForm = async (d, opening, reopen, force, strict) => {
    const superseded = () => editOpeningRef.current !== opening || routeGenRef.current !== opening.gen;
    if (openedAtRef.current == null) openedAtRef.current = Date.now();
    if (!reopen) setPayOnApprove(NO_PAY_ON_APPROVE);
    let supplierList = suppliers;
    if (!supplierList.length) {
      // include_empty=true so newly-created suppliers (no docs yet) are
      // selectable when re-assigning a document.
      const r = await suppliersApi.getAll({ include_empty: true });
      if (superseded()) return;
      supplierList = r.data;
      setSuppliers(r.data);
    }
    const baseItems = reviewBaseItems(d);
    // The catalog match the prefetch brought with this draft, when it did;
    // otherwise ask now. Null-supplier drafts and drafts with no lines skip it
    // (null), and so does a failed match outside a strict opening.
    let catalog = null;
    const held = warmCatalogRef.current;
    warmCatalogRef.current = null;
    if (held && held.docId === String(d.id) && held.catalog !== undefined) {
      catalog = held.catalog;
    } else if (d.supplier_id && baseItems.length > 0) {
      try {
        // Prices only (#328): the flags read the row's prices and agreements,
        // never the estimate or the history names.
        const r = await catalogApi.match(d.supplier_id, baseItems, { pricesOnly: true });
        catalog = r.data || [];
      } catch (e) {
        if (strict) throw e;
        console.warn('[catalog-match] skipped due to error:', e.message);
      }
    }
    if (superseded()) return;
    if (reopen && !force && reviewerHasTyped()) return 'gave-way';
    // The form as it opens — lines, VAT mode and the totals recomputed from
    // the lines — built in one place (utils/openingForm.js), the same one the
    // server's gate builds. A note or invoice keeps its stored VAT unless the
    // user asks for auto; a supplier VAT rule settles it in manual.
    const ruleRow = supplierList.find(s => String(s.id) === String(d.supplier_id));
    const opened = openingForm(d, d.items, { supplier: openingSupplier(d, ruleRow), vatRate, catalog });
    // Header and lines together, once the match is in: a form is never half
    // one copy and half another.
    setForm(opened.form);
    setItems(opened.items);
    // The mode the form settles in: a supplier VAT rule settles it in manual
    // on EVERY opening (owner, 27/10/2026 — the warm advance and the
    // re-check's reopen used to stay in auto, since the rule effect above only
    // runs when `editing` or the rule changes). The totals do not depend on
    // which: under a rule the VAT follows the rule, in either mode. The
    // baseline keeps the mode chosen from the STORED header — only a click on
    // the toggle counts as a change, never the rule's switch — and the STORED
    // totals, as it always has: דלג compares a hand-kept VAT with the stored
    // one (utils/draftEdits.js compareVat).
    setVatMode(opened.vatMode);
    const baselineForm = { ...opened.form, subtotal: d.subtotal, vat: d.vat, total: d.total };
    editBaselineRef.current = { form: baselineForm, items: opened.items, vatMode: opened.openedVatMode, vatToggled: false };
    setVatOpenedManual(opened.openedVatMode === 'manual');
    setEditing(true);
    activateGateView(d, opened, opening.gen);
    return 'opened';
  };

  // The draft gate's exposure record: the form above is now what the screen
  // shows, so the screen says so — named by the formRev of the opening it
  // committed (never of live state, which the reviewer may already be
  // editing). Prefetch, revalidation reads and the compare dialog never get
  // here. A 409 stale_form means the database moved since this copy was read
  // (a retag, a repricing): re-read and reopen in place while the reviewer has
  // not typed, which activates again; after MAX_STALE_ATTEMPTS the screen
  // keeps its copy and the draft has no view. Never blocks the review.
  // Typed into the open form. Asked only once the form has rendered: until
  // then latestRef's edit check compares the new baseline (set at commit)
  // with the empty form of the render before, and reads as edits.
  const reviewerHasTyped = () => latestRef.current.editing && latestRef.current.hasEdits();
  const gateViewOf = (id) => {
    const v = gateViewsRef.current.get(String(id));
    return v ? { docId: String(id), ...v } : null;
  };
  const activateGateView = (d, opened, gen) => {
    const docId = String(d.id);
    const known = gateViewsRef.current.get(docId);
    // A view is made only for a gated draft, but one this session already
    // holds keeps the draft in its cohort after the reviewer corrects its
    // type out of the gate: the server keeps measuring it (Codex review of
    // #342, round 2).
    if (d.status !== 'טיוטה') return;
    // The session's view for this draft outlives a copy that fails to
    // activate; the copy on screen is named by its own formRev, which the
    // decision sends too. A copy no segment holds is then measured as
    // unknown, never dropped and never diffed against another copy's
    // baseline (server lib/gateViews.js; Codex review of #342).
    const rev = formRev(opened);
    if (!isGatedDraft(d) && known?.viewId == null) {
      // A view still pending from an earlier, gated copy: name the copy on
      // screen anyway, so the late answer attaches its id to this copy and
      // never revives the earlier one (Codex review of #342, round 3).
      if (known) gateViewsRef.current.set(docId, { viewId: null, formRev: rev });
      return;
    }
    if (gateStaleRef.current.docId !== docId) gateStaleRef.current = { docId, n: 0 };
    const attempt = gateStaleRef.current.n + 1;
    gateViewsRef.current.set(docId, { viewId: known?.viewId ?? null, formRev: rev });
    const seq = ++gateSeqRef.current;
    const tries = gateOpeningsRef.current.get(docId) || { fixed: null, tries: [] };
    if (!tries.fixed) tries.tries.push({ seq, opening: gateOpeningFields(opened.form), state: 'pending' });
    gateOpeningsRef.current.set(docId, tries);
    // A promise from the first step, so nothing here can throw into the opening.
    Promise.resolve()
      .then(() => documentsApi.gateView(d.id, { review_session_id: reviewSessionRef.current, form_rev: rev, attempt, ...(readGatePreview(user?.id) ? { gate_preview: true } : {}) }))
      .then((r) => {
        // The view is the server's, whichever draft the screen moved to
        // meanwhile; the copy stays whichever opening committed last.
        const cur = gateViewsRef.current.get(docId);
        gateViewsRef.current.set(docId, { viewId: r.data?.gate_view_id ?? cur?.viewId ?? null, formRev: cur?.formRev ?? rev });
        noteGateTry(docId, seq, true);
        if (routeGenRef.current !== gen) return;
        gateStaleRef.current = { docId, n: 0 };
        // One view per session and document, so every answer for this draft
        // carries the same colour: the latest is as good as the first.
        const { gate_state = 'none', gate_color = null, gate_reasons = null } = r.data || {};
        setGateAnswer({ docId, gate_state, gate_color, gate_reasons });
      })
      .catch((err) => {
        noteGateTry(docId, seq, false);
        if (routeGenRef.current !== gen) return;
        if (err?.response?.status !== 409 || !err.response.data?.stale_form) {
          console.warn('[gate-view] not recorded', err?.message || err);
          return;
        }
        gateStaleRef.current = { docId, n: attempt };
        if (attempt >= MAX_STALE_ATTEMPTS) return;
        // Whether the reviewer has typed is asked once the fresh copy is in:
        // a 409 can land before the form's first render, when the edit check
        // still reads the empty form from before the opening.
        documentsApi.getOne(docId).then(({ data: fresh }) => {
          if (routeGenRef.current !== gen || !fresh || fresh.status !== 'טיוטה' || reviewerHasTyped()) return;
          // Only an open form is replaced: left for view mode (a save, ביטול)
          // it is not brought back, and an opening already in flight (the
          // re-check's strict reopen) activates again itself.
          if (!latestRef.current.editing || editOpeningRef.current) return;
          setDoc(fresh);
          return openEditForm(fresh, { reopen: true });
        }).catch(e => console.warn('[gate-view] could not refresh the stale copy', e?.message || e));
      });
  };

  // Also ends any opening in flight (a revalidation's reopen): its answer
  // must not bring the form back after ביטול (Codex review, round 2).
  const cancelEdit = () => {
    // The link window saved a changed supplier or type: the view must show it.
    if (linkListedForRef.current) { linkListedForRef.current = null; load(); }
    editOpeningRef.current = null; editBaselineRef.current = null; setEditing(false); setForm({}); setItems([]); setVatMode('auto'); setPayOnApprove(NO_PAY_ON_APPROVE); };

  // The payment recorded by אשר והבא when the "שולם" box is ticked. Runs
  // after the approval succeeded; returns nothing and never throws — the
  // caller's flow (credit-link offer, navigation) continues either way.
  // `status` is what the save just wrote: only an approval proper (תקין) may
  // approve the supplier's month — a לא תקין save, forced by a credit or
  // chosen, says something is still owed (#260 review).
  const recordPaymentOnApprove = async (note, status) => {
    if (!payOnApprove.checked || !form.supplier_id) return;
    try {
      const payload = {
        supplier_id: Number(form.supplier_id),
        document_id: Number(id),
        payment_method: payOnApprove.payment_method || selectedSupplierMethod() || PAYMENT_METHODS[0],
        payment_date: payOnApprove.payment_date || todayISO(),
        // The "why is this paid" note rides on the PAYMENT: that is what the
        // paid tab, the payment history and the document's comments thread
        // (which merges payment notes) all read — with who and when. A
        // separate document comment showed up in none of the payment views.
        notes: note || '',
      };
      if (status === 'תקין' && payOnApprove.approveMonth && approveOnPayOffer()) payload.approve_month = true;
      const r = await paymentsApi.create(payload);
      track(EVENTS.PAYMENT_RECORDED, { scope: 'single_doc', source: 'draft_approve', payment_method: payload.payment_method, doc_count: 1, month_approved: Boolean(r?.data?.month_approved) });
      toast(r?.data?.month_approved ? 'התשלום נרשם · החודש אושר לתשלום' : 'התשלום נרשם');
    } catch (err) {
      toast(err.response?.data?.error || 'המסמך אושר, אך רישום התשלום נכשל', 'error');
      return;
    }
  };
  // "אשר גם לתשלום" without שולם: approve the supplier-month right after the
  // document is approved (with שולם ticked, the payment request carries
  // approve_month instead — see above). The endpoint TOGGLES, so it re-reads
  // the month first: one approved meanwhile (another tab, the supplier page)
  // must not be revoked by a box that said "approve". Like the payment, a
  // second request after the approval: if it fails the approval stands.
  const approveMonthOnApprove = async () => {
    if (payOnApprove.checked || !payOnApprove.approveMonth || !approveOnPayOffer()) return;
    const range = monthRangeFromDocDate(form.date);
    if (!range || !form.supplier_id) return;
    try {
      const cur = await suppliersApi.getOne(form.supplier_id, { date_from: range.dateFrom, date_to: range.dateTo });
      if (cur?.data?.lifecycle?.approved_by) return;
      const r = await suppliersApi.approve(form.supplier_id, range.dateFrom, range.dateTo);
      // Keep the page's copy current: the link modal that may open next
      // offers the same approval, and must not offer an approved month. Only
      // this document's: the approval may have outlived the route.
      if (r?.data?.lifecycle && currentIdRef.current === String(id)) setSupplierLifecycle(r.data.lifecycle);
      if (r?.data?.approved) toast(`${monthLabelFromDocDate(form.date)} אושר לתשלום`);
    } catch (err) {
      toast(err.response?.data?.error || 'המסמך אושר, אך האישור לתשלום נכשל', 'error');
    }
  };
  const selectedSupplierMethod = () => suppliers.find(s => String(s.id) === String(form.supplier_id))?.payment_method || '';
  // A הוראת קבע supplier's invoice is paid by the server the moment it is
  // approved (tryAutoPay on save), so offering "שולם" here would only race
  // that payment and lose the note; the block hides and the approve toast says
  // the standing order paid it.
  const autoPaysOnApproval = () =>
    String(selectedSupplierMethod() || '').trim().replace(/['׳]/g, '') === 'הוראת קבע' &&
    ['חשבונית', 'חשבונית מרכזת', 'חשבונית/קבלה', 'חשבון עסקה'].includes(form.doc_type);
  // The approve-while-paying offer for the draft's own month. The page holds
  // the lifecycle of the document's ORIGINAL supplier; if the admin re-assigned
  // it in the form, that lifecycle says nothing about the new one — no offer.
  const approveOnPayOffer = () => {
    if (String(form.supplier_id || '') !== String(doc?.supplier_id || '')) return null;
    const range = monthRangeFromDocDate(form.date);
    return approvalOfferForDoc({ lifecycle: supplierLifecycle, docDate: form.date, docType: form.doc_type, docTotal: form.total, dateFrom: range?.dateFrom, dateTo: range?.dateTo });
  };

  // A document with only a total has no net for VAT or a discount to work from,
  // so the total stays as stored: the same rule as formTotals (#385).
  const hasOnlyTotal = (f) => items.length === 0 && !parseFloat(f.subtotal) && !!parseFloat(f.total);

  // Called when user types a manual VAT amount; keeps total in sync
  const handleManualVat = (value) => {
    const vat = parseFloat(value) || 0;
    setForm(f => {
      if (hasOnlyTotal(f)) return { ...f, vat };
      const taxable = (parseFloat(f.subtotal) || 0) - calcInvoiceDiscountAmount(f.subtotal, f.invoice_discount, f.invoice_discount_type);
      return { ...f, vat, total: roundDocTotal(taxable + round2(vat)) };
    });
  };

  // Invoice-level discount edits: accepts a partial patch of
  // { invoice_discount, invoice_discount_type } and re-derives vat + total.
  const handleInvoiceDiscount = (patch) => {
    setForm(f => {
      const next = { ...f, ...patch };
      if (hasOnlyTotal(f)) return next;
      const subtotal = parseFloat(next.subtotal) || 0;
      const taxable = subtotal - calcInvoiceDiscountAmount(subtotal, next.invoice_discount, next.invoice_discount_type);
      const vat = vatMode === 'auto'
        ? round2(taxable * (vatRate / 100))
        : parseFloat(f.vat) || 0;
      return { ...next, vat, total: roundDocTotal(taxable + round2(vat)) };
    });
  };

  // The totals chip's rounding fixes (#344) — from the chip, or from the small
  // התאם beside the הנחה / מע"מ fields, which offer the same two.
  const applyTotalsFix = (a) => {
    setVatMode('manual');
    setVatOpenedManual(false);
    if (editBaselineRef.current) editBaselineRef.current.vatToggled = true;
    // A sub-₪1 gap to the printed total is the VAT's to absorb…
    if (a.kind === 'vat-fit') { handleManualVat(String(a.vat)); return; }
    // …or the discount line's, in ₪ (owner, 31/10/2026). The VAT is held where
    // it is — in auto it would follow the new discount and miss the printed
    // total by agorot again.
    setForm(f => {
      const subtotal = parseFloat(f.subtotal) || 0;
      const vat = round2(parseFloat(f.vat) || 0);
      return { ...f, invoice_discount: a.discount, invoice_discount_type: 'amount', total: roundDocTotal(subtotal - a.discount + vat) };
    });
  };

  const checkMismatch = () => {
    const bad = items.findIndex(rowMismatched);
    if (bad < 0) return false;
    const it = items[bad];
    const expected = calcLineTotal(it);
    const actual   = parseFloat(it.line_total) || 0;
    toast(
      `שורה ${bad + 1}: סך הכל (${formatCurrency(actual)}) לא תואם כמות × מחיר נטו (${formatCurrency(expected)}). תקני לפני שמירה.`,
      'error'
    );
    return true;
  };

  // Pre-check duplicate doc_num against finalized docs. Only fires when
  // saving as a non-draft (drafts may carry the duplicate_warning flag for
  // admin review). The server enforces the same rule with a 409 — this
  // check is just for the friendlier upfront error.
  const checkDuplicateOrToast = async (statusBeingSaved) => {
    const trimmed = (form.doc_num || '').trim();
    if (!trimmed || !form.supplier_id || statusBeingSaved === 'טיוטה') return false;
    // A כרטסת carries the supplier's account number, which repeats every
    // month — the server does not treat it as a duplicate, so neither may we.
    if (isDupExemptDocType(form.doc_type)) return false;
    try {
      const r = await documentsApi.checkDuplicate(form.supplier_id, trimmed, form.doc_type);
      if (r.data?.exists && String(r.data.id) !== String(id)) {
        toast(`מסמך מספר ${trimmed} כבר קיים עבור ספק זה — לא ניתן לשמור כפיל`, 'error');
        return true;
      }
    } catch (e) {
      console.warn('[duplicate-check] skipped due to error:', e.message);
    }
    return false;
  };

  const handle409 = (err, fallbackMsg) => {
    if (err?.response?.status === 409 && err.response.data?.duplicate) {
      toast(err.response.data.error || 'מסמך כפיל — השמירה נחסמה', 'error');
      return true;
    }
    // A 4xx carries the server's own reason (e.g. 'נדרשים ספק וסוג מסמך' on a
    // supplier-less draft) — show it, not a generic 'שגיאה'.
    const status = err?.response?.status;
    const serverMsg = err?.response?.data?.error;
    toast(status >= 400 && status < 500 && serverMsg ? serverMsg : fallbackMsg, 'error');
    return false;
  };

  // An empty candidate list is not always "nothing to do": a draft note is
  // never linkable (#227), so right after a bulk upload every note the
  // invoice names can still be a draft. The modal's amber line is the only
  // place that tells the admin to approve them and come back — skipping it
  // loses the prompt. Coverage owns that answer (noteCoverage on the server
  // feeds both the list and the report), so ask it rather than guess. A
  // failure here means "don't know", and with nothing linkable that is no
  // reason to interrupt — the unlinked-notes flag still catches the notes
  // once they are approved.
  const hasNotesAwaitingApproval = async () => {
    try {
      const c = await documentsApi.coverage(id);
      return (c.data?.awaiting_approval?.length ?? 0) > 0;
    } catch {
      return false;
    }
  };

  // Offer the delivery-note linking modal for a חשבונית מרכזת. Returns true
  // when the modal was opened — the caller must then leave any navigation to
  // the modal's onClose, exactly like the open-credits offer below.
  //
  // Why every write path has to make this offer: `server/lib/amountOwed.js`
  // zeroes a delivery note ONLY once it is linked (LINKED_NOTE → 0). An
  // unlinked note therefore counts in full *beside* the מרכזת that already
  // contains it, and the supplier's balance is overstated by the note's value.
  // The link IS the arithmetic — nothing else writes the note down — yet the
  // modal used to be reachable from just two places (the interactive upload
  // flow, and the manual "ערוך קישורים" button on the read-only view), so
  // every approval path fell straight through and left the notes unlinked.
  //
  // Deliberately an offer and never a gate: some notes belong to NEXT month's
  // invoice, and a note linked by mistake is zeroed — its goods would vanish
  // from spend entirely. Closing the modal without linking must stay valid.
  const offerDeliveryNoteLink = async (src, afterClose, decisionFor = null) => {
    // PUT /documents/:id/links is requireAdmin, and the manual button renders
    // only for admins — don't hand a viewer a modal they can't save.
    if (!isAdmin || src?.doc_type !== 'חשבונית מרכזת') return false;
    const stillHere = routeOwner();
    // Pre-flight the candidate list so a מרכזת with nothing to link doesn't
    // pop an empty modal (the credits branch pre-checks its open count the
    // same way) — unless notes it names wait in drafts, see
    // hasNotesAwaitingApproval above. A FAILED pre-flight still opens it: unlike the credit offer
    // this link is the money math, so asking beats silently skipping.
    //
    // Does this invoice already HAVE links? The modal auto-selects a month
    // only on a first link, so this must never read 0 for an invoice that has
    // some. Two imperfect sources, so take whichever says "yes": the document
    // itself carries the authoritative list but only on the reload path (the
    // approval paths pass `form`, which has no such field), while the
    // candidate list's own already_linked_here can undercount, since it
    // filters by supplier_id and a linked note whose supplier was later
    // reassigned drops out of it entirely.
    let existingLinkCount = src.linked_delivery_notes?.length ?? 0;
    try {
      const r = await documentsApi.unlinkedDeliveryNotes(id);
      if (Array.isArray(r.data) && r.data.length === 0 && !(await hasNotesAwaitingApproval())) return false;
      if (Array.isArray(r.data)) {
        existingLinkCount = Math.max(
          existingLinkCount,
          r.data.filter(n => n.already_linked_here).length,
        );
      }
    } catch { /* fall through and ask anyway */ }
    if (!stillHere()) return false;
    setLinkModal({
      stillHere,
      invoiceId: id,
      invoiceDocNum: src.doc_num || doc?.doc_num,
      invoiceTotal: parseFloat(src.total) || 0,
      invoiceSubtotal: parseFloat(src.subtotal) || 0,
      supplierName: src.supplier_name || doc?.supplier_name || '',
      // Gates the "mark as לא תקין" offer: the server refuses to flag a doc
      // that is not תקין, so offering it there would be a control that lies.
      invoiceStatus: src.status || doc?.status || '',
      existingLinkCount,
      initialRange: rangeForInvoice(convertRange, src.supplier_id ?? doc?.supplier_id, src.date ?? doc?.date),
      // For the modal's "אשר גם לתשלום" — the month of the invoice as saved.
      supplierId: src.supplier_id ?? doc?.supplier_id,
      docDate: src.date ?? doc?.date,
      afterClose: afterClose || null,
      decisionFor,
    });
    return true;
  };

  // A draft מרכזת: the window opens BEFORE the approval, and a button there decides
  // (owner, 03/11/2026, #381). It used to open after the save, so the document was
  // already תקין, its side effects run, and closing could not keep it a draft.
  // False when there is nothing to link: the approval simply goes on.
  const offerLinkDecision = async (src, decisionFor) => {
    const stillHere = routeOwner();
    linkDecisionLeaveRef.current = null;
    return offerDeliveryNoteLink(src, () => {
      const go = linkDecisionLeaveRef.current;
      linkDecisionLeaveRef.current = null;
      return go && stillHere() ? continueAfterPendingNote(go) : undefined;
    }, decisionFor);
  };
  // Writing this status from the form makes it a decision the window has to take.
  // A credit line is already one (see approveCurrent).
  const windowDecidesFirst = (status) =>
    status === 'תקין' && form.doc_type === 'חשבונית מרכזת' && doc?.status === 'טיוטה' && !items.some(lineHasCredit);
  // True when the caller stops: the window opened, or the route moved during the
  // check, which must not read as "nothing to link" and write the draft left behind.
  const windowTakesTheDecision = async (stillHere, note = null) => {
    // The window lists the notes of the SAVED supplier and type, so a change
    // to those lands first, still a draft. No review on this save: it would close
    // the gate view that the decision has yet to report against.
    const saved = linkListedForRef.current ?? doc;
    const retargeted = String(form.supplier_id ?? '') !== String(saved.supplier_id ?? '') || form.doc_type !== saved.doc_type;
    if (retargeted) {
      await documentsApi.update(id, { ...form, status: 'טיוטה', items: itemsForSave(items) });
      if (stillHere()) {
        linkListedForRef.current = { supplier_id: form.supplier_id, doc_type: form.doc_type };
        // All of it is saved now, so nothing is unsaved to warn about.
        editBaselineRef.current = { ...editBaselineRef.current, form: { ...form }, items };
        toast('השינויים נשמרו כדי להציג את תעודות הספק · המסמך נשאר טיוטה');
      }
    }
    const offered = stillHere() && await offerLinkDecision({ ...form, status: 'טיוטה', supplier_name: ruleSupplier?.name }, { source: 'edit', payNote: note });
    return offered || !stillHere();
  };

  // `status` overrides the סטטוס field: ⋮ → שמור כטיוטה and דלג's "save and
  // skip" both keep the document a draft whatever the field says. `onSaved`
  // replaces the stay-here ending (reload, view mode, link offers) for a
  // caller that leaves the page right after — דלג.
  const saveEdit = async ({ status = form.status, onSaved = null } = {}) => {
    // The first render after an in-place advance still holds the previous
    // draft's form under the new id: a click there would save one draft's
    // form onto the other.
    if (decisionsLocked) return false;
    // שולם ticked is an approval with a payment: אשר והבא's way, which asks for the
    // note before the window. The caller stops either way (Codex, #384).
    if (windowDecidesFirst(status) && payOnApprove.checked) { await approveAndNext(); return false; }
    const stillHere = routeOwner();
    if (checkMismatch()) { track(EVENTS.DOCUMENT_SAVE_BLOCKED, { reason: 'row_total_mismatch', context: 'draft_edit', doc_type: form.doc_type }); return false; }
    if (isImplausibleDateYear(form.date)) { track(EVENTS.DOCUMENT_SAVE_BLOCKED, { reason: 'implausible_date', context: 'draft_edit', doc_type: form.doc_type }); toast('השנה בתאריך חריגה — תקני את התאריך לפני שמירה', 'error'); return false; }
    if (await checkDuplicateOrToast(status)) { track(EVENTS.DOCUMENT_SAVE_BLOCKED, { reason: 'duplicate_doc_num', context: 'draft_edit', doc_type: form.doc_type }); return false; }
    if (stillHere()) setSaving(true);
    try {
      // The סטטוס field on תקין is an approval too: the window decides first. With
      // nothing to link it is the plain save it always was.
      if (windowDecidesFirst(status) && await windowTakesTheDecision(stillHere)) return false;
      // The סטטוס field can take a draft out of טיוטה on this plain save too, so
      // it carries the same review clock as an approval; the server ignores it
      // unless this save is the document's first decision.
      const r = await documentsApi.update(id, {
        ...form,
        status,
        items: itemsForSave(items),
        review: reviewPayload(),
      });
      if (onSaved) {
        toast('השינויים נשמרו · המסמך נשאר טיוטה');
        // Awaited: `saving` covers the move to the next draft (openNextDraft).
        await onSaved();
        return true;
      }
      const fresh = await load();
      setEditing(false);
      toast('המסמך עודכן בהצלחה');
      const bf = describeBackfill(r.data?.backfill);
      if (bf) toast(bf);
      // A plain save changes no status, so it never reached the post-approval
      // offer — retype an existing document's type to חשבונית מרכזת here and
      // there was no prompt to link its תעודות משלוח, ever. Offer it on this
      // path too, but ONLY while the invoice has zero links: an already
      // reconciled מרכזת must not re-nag on every unrelated edit. No
      // afterClose — a save doesn't navigate, so the modal just closes.
      // `fresh` is null when the reload after the save failed (load() shows
      // the retry) — nothing to offer a link on then.
      // A plain save can take a draft out of טיוטה too (the סטטוס field), and
      // then no approve button will ever come for a waiting customer note —
      // offer it here, before the link offer, same as an approval does.
      const offerLinks = async () => {
        // Saved ticks on a draft are the admin's answer: don't reopen on every save.
        // Past the draft, ticks still waiting are worth the offer until linked.
        const unlinked = (fresh?.linked_delivery_notes || []).length === 0;
        const offer = fresh?.status === 'טיוטה'
          ? unlinked && fresh.pending_link_count == null
          : unlinked || fresh?.pending_link_count > 0;
        if (fresh && offer) {
          await offerDeliveryNoteLink(fresh);
        }
      };
      if (fresh && fresh.status !== 'טיוטה') await continueAfterPendingNote(offerLinks);
      else await offerLinks();
      return true;
    } catch (err) { handle409(err, 'שגיאה בשמירה'); }
    // Once moving to the next draft, `saving` stays on until that route
    // takes over ([id] effect): this reset is urgent while the route change
    // is a transition, so it re-enabled the bar on the draft just decided.
    // Moved on, the flag is the new document's (see approveCurrent).
    finally       { if (stillHere() && !advancingRef.current) setSaving(false); }
    return false;
  };

  // ⋮ → בוצע תשלום. While editing, the form is saved first so the modal
  // opens on the amount the admin is looking at, never a stale one; a save
  // that is blocked or fails opens nothing.
  const payFromMenu = async () => {
    setDocMenuOpen(false);
    if (editing && !(await saveEdit())) return;
    setPaymentModal(true);
  };

  // After approving a credit/return doc, offer to close the supplier's open
  // credits (same modal as the interactive upload flow). Calls `go` (default:
  // back to /drafts) immediately when there's nothing to link; otherwise
  // defers it until the modal closes. `src` carries the just-approved doc's
  // fields. The destination is parameterized so "אשר והבא" can advance to the
  // next draft instead of bouncing to the list.
  //
  // It is also the post-save hook for a חשבונית מרכזת already decided לא תקין
  // (see offerDeliveryNoteLink). An approval opens the window before it saves, so
  // it does not come through here while the window has notes (offerLinkDecision).
  const offerCreditLinkThenLeave = async (src, go) => {
    // Its requests, and a dialog left open, can outlast the route: from then on
    // the dialog and the landing belong to a document no longer on screen, and
    // taking them would drop whatever is being typed on the one that is.
    const stillHere = routeOwner();
    const leave = () => {
      if (!stillHere()) return undefined;
      return go ? go() : navigate('/drafts');
    };
    if (CREDIT_DOC_TYPES.includes(src.doc_type) && src.supplier_id) {
      try {
        const oc = await creditsApi.getOpenCount(src.supplier_id);
        if (!stillHere()) return;
        if ((oc.data?.cnt || 0) > 0) {
          setOpenCreditsModal({
            creditDocId: id,
            creditDocTotal: parseFloat(src.total) || 0,
            creditDocSubtotal: parseFloat(src.subtotal) || 0,
            creditDocVat: parseFloat(src.vat) || 0,
            supplierId: src.supplier_id,
            supplierName: doc?.supplier_name || '',
            afterClose: leave,
          });
          return; // navigation deferred to the modal's onClose
        }
      } catch { /* fall through and navigate */ }
    }
    // A חשבונית מרכזת gets the delivery-note offer instead (the doc types are
    // mutually exclusive, so at most one branch ever fires). Same deferral
    // contract as above: once the modal is open, IT owns the navigation.
    if (await offerDeliveryNoteLink(src, leave)) return;
    // Returned: the approval awaits the move to the next draft (openNextDraft).
    return leave();
  };

  // Advance to the next draft in the queue (or back to /drafts when done).
  // Explicit action only — never fired silently by a plain save.
  // Drafts skipped with דלג this session are passed over (utils/draftQueue.js);
  // when only those remain, the toast says how many are still waiting.
  // `queue` defaults to the one loaded for edit mode; a delete after ביטול
  // passes one it fetched itself (continueAfterDraftDelete).
  const nextInQueue = (queue = draftQueue) => nextDraftId(queue || [], id, skippedDraftIds());
  // `countCurrent`: after דלג the current draft is itself one of those left.
  const queueDoneToast = ({ countCurrent = false } = {}) => {
    const left = skippedRemaining(draftQueue || [], countCurrent ? null : id, skippedDraftIds());
    toast(left > 0 ? `${queueDoneWord()} טופלו · נותרו ${left} שדילגת עליהן` : `${queueDoneWord()} טופלו`);
  };
  // `queue` goes with the reviewer (carriedQueueRef): the one this draft was
  // decided against, without it once it has left the queue (approved,
  // deleted) — a skipped draft stays.
  //
  // It first waits — on this screen, the bar busy, at most 1.5s — for what
  // the prefetch is loading for that draft, so it opens warm, in one render,
  // instead of on the full-page spinner: after a plain אשר והבא the rest of
  // the flow reaches here at once, before the next draft's request has even
  // left. The wait is no longer than the spinner's; it only drops the blank
  // page and the view-mode flash between the two forms. The queue is set
  // with the navigation, so no render of the next draft shows the old one.
  // Returns a promise the approval awaits (its `saving` covers the wait).
  const openNextDraft = async (nextId, queue) => {
    const fromId = String(id);
    // Called late from a document the route has left: `saving` is the new
    // document's, and nothing here would ever clear it.
    if (currentIdRef.current !== fromId) return;
    readyClockRef.current = { docId: String(nextId), startedAt: performance.now(), cache: 'none' };
    setSaving(true);
    await whenDraftPrefetchSettles({ rid: tabRestaurantId(), docId: nextId });
    // Left meanwhile (a link, the breadcrumb): the reviewer is elsewhere now.
    if (!mountedRef.current || currentIdRef.current !== fromId) return;
    carriedQueueRef.current = Array.isArray(queue) ? queue : null;
    arrivalQueueRef.current = carriedQueueRef.current ? { docId: String(nextId), queue: carriedQueueRef.current } : null;
    advanceTargetRef.current = String(nextId);
    if (carriedQueueRef.current) setDraftQueue(carriedQueueRef.current);
    advancingRef.current = true;
    navigate(`/document/${nextId}`, { state: { autoEdit: true, autoOpenFiles: true, entry: 'approve_next' } });
  };
  const queueWithout = (queue, docId) => (queue || []).filter(d => String(d.id) !== String(docId));
  // At the decision: the next draft's document and match when the early load
  // has nothing (yet) or held it too long — a no-op otherwise.
  const prefetchNextDraft = (queue = draftQueue) => {
    const nextId = nextInQueue(queue);
    if (nextId) prefetchDraftDoc({ rid: tabRestaurantId(), docId: nextId, load: () => loadDraftForReview(nextId) });
  };
  // `countCurrent`: דלג — the current draft stays in the queue.
  const goToNextDraft = ({ countCurrent = false } = {}) => {
    const nextId = nextInQueue();
    if (nextId) {
      return openNextDraft(nextId, countCurrent ? draftQueue : queueWithout(draftQueue, id));
    } else {
      queueDoneToast({ countCurrent });
      navigate('/drafts');
    }
  };

  // A draft deleted while working the queue continues to the next one, like
  // אשר והבא and דלג (#303) — the duplicate-compare delete included. "Working
  // the queue" = opened from /drafts or by a previous אשר והבא (reviewEntry,
  // which ביטול keeps); a draft opened directly, from a supplier page or a
  // link, keeps its old landing. The queue is loaded only in edit mode; after
  // ביטול, or when that load failed, it is fetched now, after the delete, so
  // the server has already dropped this draft and the next one is the queue's
  // first; a failed fetch claims nothing. The dialog stays
  // quiet and this shows ONE toast once the landing is known: the full one
  // when another draft follows, a short one when the queue is done (the
  // dialog has just said how to restore).
  const inDraftQueue = doc?.status === 'טיוטה' && (reviewEntry === 'queue_edit' || reviewEntry === 'approve_next');
  // A moved draft (#304) continues the same way, from any entry: it has left
  // this restaurant, so the next draft is the only place to go. `doneLead`
  // opens the toast when the queue is done.
  const continueAfterDraftDelete = async ({ notice, doneLead = 'הטיוטה נמחקה' }) => {
    let queue = draftQueueFailed ? null : draftQueue;
    if (!queue) {
      try {
        const r = await documentsApi.getDrafts({ gatePreview: readGatePreview(user?.id) });
        // Left meanwhile (a logout included): this answer writes nothing.
        if (!mountedRef.current) return;
        queue = workQueue(Array.isArray(r.data) ? r.data : [], id);
      } catch {
        // No queue to read: the list is the honest landing, without claiming
        // that every draft was handled.
        toast(notice);
        navigate('/drafts');
        return;
      }
    }
    const nextId = nextInQueue(queue);
    if (nextId) {
      prefetchNextDraft(queue);
      toast(notice);
      await openNextDraft(nextId, queueWithout(queue, id));
      return;
    }
    const left = skippedRemaining(queue, id, skippedDraftIds());
    toast(`${doneLead} · ${left > 0 ? `נותרו ${left} שדילגת עליהן` : `${queueDoneWord()} טופלו`}`);
    navigate('/drafts');
  };

  // Approve-and-save: persist current edits AND flip status to תקין in one
  // PUT (the edit route already accepts `status` in the body). After save,
  // offer credit-linking (for credit docs) then continue to `goAfter`
  // (default: back to /drafts — today's behavior).
  // Leaves the draft stage as `status` — 'תקין' (אשר והבא) or
  // 'לא תקין' (סטטוס set to לא תקין, or a credit line: the document is real and stays in the
  // money, but something on it is unresolved). Everything else — the שולם note,
  // the payment, the comment, the credit-link offer — is the same path.
  const approveCurrent = async (goAfter, { payNote: note = null, status: requested = 'תקין', linkDecided = false } = {}) => {
    if (decisionsLocked) return;
    // A credit on any line files the document as לא תקין, whichever button
    // was pressed (#260) — the bar already says so in amber; this makes it so.
    const creditGuard = requested === 'תקין' && items.some(lineHasCredit);
    const status = creditGuard ? 'לא תקין' : requested;
    // שולם ticked and no note yet: ask for it first, then come back here.
    if (payOnApprove.checked && form.supplier_id && note == null && isPayableDocType(form.doc_type) && !autoPaysOnApproval()) {
      setPayNote('');
      setPayNoteModal({ goAfter, status });
      return;
    }
    if (checkMismatch()) { track(EVENTS.DOCUMENT_SAVE_BLOCKED, { reason: 'row_total_mismatch', context: 'draft_approve', doc_type: form.doc_type }); return; }
    if (isImplausibleDateYear(form.date)) { track(EVENTS.DOCUMENT_SAVE_BLOCKED, { reason: 'implausible_date', context: 'draft_approve', doc_type: form.doc_type }); toast('השנה בתאריך חריגה — תקני את התאריך לפני שמירה', 'error'); return; }
    // The page stays mounted from one document to the next, so from the first
    // request on, this approval's answers can arrive on a form it never saved
    // (Back, a link). Whatever touches the screen asks stillHere first.
    const stillHere = routeOwner();
    const origin = reviewOrigin();
    if (await checkDuplicateOrToast(status)) { track(EVENTS.DOCUMENT_SAVE_BLOCKED, { reason: 'duplicate_doc_num', context: 'draft_approve', doc_type: form.doc_type }); return; }
    if (stillHere()) setSaving(true);
    try {
      if (!linkDecided && windowDecidesFirst(status) && await windowTakesTheDecision(stillHere, note)) return undefined;
      // review: when this review opened, how long it took and how the reviewer
      // got here — the decision record's measurement (server/lib/reviewLog.js).
      const review = reviewPayload(origin);
      const r = await documentsApi.update(id, {
        ...form,
        status,
        items: itemsForSave(items),
        review,
      });
      // Saved: a link dialog may keep this form on screen a while yet, and
      // closing the tab then loses nothing (hasUnsavedWork).
      if (stillHere() && editBaselineRef.current) editBaselineRef.current.decided = true;
      // The decision is written: what the next draft's re-check must cover,
      // and its load if the early one has nothing.
      if (goAfter && stillHere()) prefetchNextDraft();
      const changed = r.data?.review?.fields_changed_count;
      track(EVENTS.DOCUMENT_APPROVED, {
        source: goAfter ? 'doc_view_approve_next' : 'doc_view_approve',
        doc_type: form.doc_type,
        status,
        advanced_to_next: !!goAfter,
        line_count: items.length,
        ...(creditGuard ? { credit_guard: true } : {}),
        // Counts only, never values: seconds on screen and how many fields the
        // reviewer changed against the AI's read (the server computed it).
        ...(review.review_seconds != null ? { review_seconds: review.review_seconds } : {}),
        ...(Number.isInteger(changed) ? { fields_changed_count: changed } : {}),
        ...gateAnalytics(gateShown),
      });
      toast(status === 'לא תקין' ? 'המסמך נשמר כלא תקין' : 'הטיוטה אושרה ונשמרה');
      const bf = describeBackfill(r.data?.backfill);
      if (bf) toast(bf);
      if (r.data?.auto_pay?.fired) {
        // The standing order already paid it inside the save; a second POST
        // would 400 on "nothing left to pay" and drop the note.
        toast('שולם אוטומטית (הוראת קבע)');
      } else if (isPayableDocType(form.doc_type)) {
        await recordPaymentOnApprove(note, status);
      }
      // Only on an approval proper — שמור כלא תקין says something is still
      // wrong, which is not the moment to approve paying for the month.
      if (status === 'תקין') await approveMonthOnApprove();
      // The link window asked for this save: it links next, whatever is on screen by
      // then, because the document is decided. Its way out checks the route itself.
      if (linkDecided) return status;
      // Moved on meanwhile: the decision and its payment stand, but the dialogs
      // and the landing below belong to a document no longer on screen, and
      // taking them now would drop whatever is being typed on this one.
      if (!stillHere()) return undefined;
      // `form` still carries the pre-save status (טיוטה); the offer must see
      // the one just written, or the link modal treats a תקין/לא תקין
      // invoice as a draft the customer cannot see yet.
      await continueAfterPendingNote(() => offerCreditLinkThenLeave({ ...form, status }, goAfter));
    } catch (err) { handle409(err, 'שגיאה באישור'); }
    // Once moving to the next draft, `saving` stays on until that route
    // takes over ([id] effect): this reset is urgent while the route change
    // is a transition, so it re-enabled the bar on the draft just decided.
    // Moved on, that effect has already reset it, and the flag is the new
    // document's now: its own save may be the one holding it.
    finally       { if (stillHere() && !advancingRef.current) setSaving(false); }
  };

  // אשר ושמור (approve, back to the list) is gone (#260): 98.7% of decisions
  // went through אשר והבא, which returns to /drafts anyway once the queue is
  // done. One approve button, one shortcut (Ctrl/⌘+Enter).
  // סטטוס set to לא תקין, or a credit line, makes it a לא תקין save (the bar
  // shows the amber שמור כלא תקין והבא): on the last draft it stays on the
  // document instead of the list.
  const approveAndNext = () => {
    if (form.status === 'לא תקין') return saveInvalidAndNext();
    return approveCurrent(items.some(lineHasCredit) ? goToNextDraftOrStay : goToNextDraft);
  };
  // Next draft if there is one; otherwise stay on THIS document (view mode)
  // — a לא-תקין save is usually followed by looking at what is wrong, not by
  // the empty drafts list.
  const goToNextDraftOrStay = () => {
    const nextId = nextInQueue();
    if (nextId) {
      return openNextDraft(nextId, queueWithout(draftQueue, id));
    } else {
      queueDoneToast();
      // Drop the /drafts entry state, or the autoEdit effect would reopen the
      // form on the reload (the document is no longer a draft anyway).
      navigate(`/document/${id}`, { replace: true, state: {} });
      setEditing(false); setLoading(true); load();
    }
  };
  const saveInvalidAndNext = () => approveCurrent(goToNextDraftOrStay, { status: 'לא תקין' });

  // דלג (#260): the draft stays טיוטה and the queue moves on, passing over it
  // for the rest of this session. Nothing changed → straight on; something
  // changed (or a customer note is waiting) → ask first. No reason asked for —
  // the owner's call, superseding the roadmap's 08/10 "skip needs a reason".
  // A VAT-mode switch by hand counts too (the rule-driven switch on opening
  // does not: only the toggle's own click marks it). Nor does the page turning
  // סטטוס to לא תקין by itself: an approved document with a credit line does
  // that on opening (the effect above), and nobody has typed anything.
  const skipHasChanges = () => {
    const base = editBaselineRef.current;
    if (base?.vatToggled && vatMode !== base.vatMode) return true;
    const typedForm = autoInvalidRef.current && form.status === 'לא תקין' ? { ...form, status: base?.form?.status } : form;
    return hasUnsavedEdits(base, { form: typedForm, items }, { compareVat: vatMode === 'manual' && !supplierVatInclusive });
  };
  // What leaving would lose (#369). Not while `saving`, when those edits are on
  // their way to the server, nor once an approval has written them: the form
  // still differs from its opening while the next draft loads or a link dialog
  // is up, but it is all saved.
  const hasUnsavedWork = () => editing && !saving && !editBaselineRef.current?.decided && skipHasChanges();
  const skipDraft = () => {
    // The queue loads when the form opens; until then "next" is unknown and
    // an early click would read as "all done".
    if (saving || decisionsLocked || draftQueue === null) return;
    const changed = skipHasChanges();
    if (changed || pendingCustomerNote) { setSkipPrompt({ changed }); return; }
    leaveSkipped({ save: false });
  };
  const leaveSkipped = async ({ save }) => {
    setSkipPrompt(null);
    const advance = () => {
      markDraftSkipped(id);
      track(EVENTS.DRAFT_SKIPPED, { doc_type: form.doc_type, saved_changes: save });
      // Only the link window's way out gets here before the queue is known, and
      // "next" would then read as all done.
      if (draftQueue === null) return navigate('/drafts');
      prefetchNextDraft();
      return goToNextDraft({ countCurrent: true });
    };
    if (save) await saveEdit({ status: 'טיוטה', onSaved: advance });
    else await advance();
  };

  // Keyboard-first save: Ctrl/Cmd+S saves, Ctrl/Cmd+Enter approves-and-saves.
  // Handlers go through refs so the window listener is registered once per
  // edit session yet always calls the latest closure (form/items change every
  // keystroke).
  const keyHandlersRef = useRef({});
  keyHandlersRef.current = {
    // Not behind an open link window: Ctrl+Enter there would start the approval again.
    save: () => { if (!saving && !decisionsLocked && !linkModal) saveEdit(); },
    approve: () => { if (!saving && !decisionsLocked && !linkModal && isAdmin && doc?.status === 'טיוטה') approveAndNext(); },
  };
  latestRef.current = { draftQueue, saving, editing, hasEdits: () => Boolean(skipHasChanges() || pendingCustomerNote), hasUnsavedWork };
  useEffect(() => {
    if (!editing) return;
    const onKey = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      // The שולם note dialog owns the keyboard while it is open — Ctrl+Enter
      // there used to restart the approve with an empty note and the wrong exit.
      if (payNoteModalRef.current) return;
      // Same for ANY dialog over the edit screen: the comment thread advertises
      // Ctrl+Enter to send, and without this the same keystroke also approved
      // the draft behind it (or re-ran an approve under the link modal's
      // thread). Every Modal sets aria-modal.
      if (document.querySelector('[aria-modal="true"]')) return;
      if (e.key === 's' || e.key === 'S') {
        e.preventDefault();
        keyHandlersRef.current.save();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        keyHandlersRef.current.approve();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editing]);

  // Memoized so the supplier combobox's filter memo survives re-renders (the
  // form re-renders on every keystroke; without a stable array identity the
  // map() would rebuild each time and re-run the filter over every supplier).
  // Declared before the early returns below to satisfy rules-of-hooks.
  const supplierOptions = useMemo(
    () => suppliers.map(s => ({ value: String(s.id), label: s.name })),
    [suppliers],
  );
  const pendingSupplierName = newSupplierName || doc?.extracted_supplier_name || '';
  const showNewSupplierOptions = Boolean(
    isAdmin && !form.supplier_id && hasUnmatchedSupplierName(suppliers, pendingSupplierName),
  );

  const updateItem = (idx, field, value) =>
    setItems(prev => prev.map((it, i) => {
      if (i !== idx) return it;
      const updated = { ...it, [field]: value };
      // Typing a real price clears the "estimated" mark — it is now a real price.
      if (field === 'unit_price') updated.price_estimated = 0;
      if (['qty', 'unit_price', 'discount', 'discount_type', 'deposit', 'purchase_tax'].includes(field)) {
        updated.line_total   = calcLineTotal(updated);
        updated.credit_total = calcCreditTotal(updated);
      }
      if (field === 'credit_qty' || field === 'credit_price_diff') {
        updated.credit_total = calcCreditTotal(updated);
      }
      return updated;
    }));

  const addItem = () => setItems(prev => [...prev, {
    sku: '', description: '', qty: 1, unit_price: 0,
    discount: 0, discount_type: 'percent', line_total: 0,
    credit_qty: 0, credit_price_diff: 0, credit_total: 0,
    deposit: 0, purchase_tax: 0, _key: Date.now(),
  }]);

  const addFirstRow = async () => {
    try {
      if (await startEdit() === 'opened') addItem();
    } catch (err) {
      console.error('[return-note] could not open the form', err);
      toast('לא ניתן לפתוח את הטופס לעריכה — נסה שוב', 'error');
    }
  };

  const removeItem = idx => setItems(prev => prev.filter((_, i) => i !== idx));

  // A hook, so it must run before the early returns below.
  const openDeliveryNotes = useOpenDeliveryNotes({
    enabled: editing, supplierId: form.supplier_id, docType: form.doc_type, date: form.date,
  });

  if (loading) return <div className="loading-container"><div className="spinner" /><span>טוען מסמך...</span></div>;
  if (accessDenied) return <NoAccessNotice message={accessDenied} />;
  if (loadError && !doc) return <InlineLoadError message="לא ניתן לטעון את המסמך" onRetry={() => { setLoading(true); load(); }} />;
  if (!doc)    return <EmptyState message={missingMessage || 'מסמך לא נמצא'} />;

  const hasAnyCredit = (doc.items || []).some(it =>
    (it.credit_qty || 0) > 0 || (it.credit_price_diff || 0) > 0
  );
  // The lines on the edit form right now — what the review bar decides on.
  const editHasCredits = editing && items.some(lineHasCredit);
  // A draft about to be filed לא תקין — a credit line, or the סטטוס field set
  // to it by hand. One amber primary says so; there is no separate button.
  const editSavesInvalid = editHasCredits || (editing && form.status === 'לא תקין');
  // The view table's הנחה column, like the credit ones, only when it says
  // something — a column of dashes costs width the side pane needs.
  const hasAnyDiscount = (doc.items || []).some(it => (it.discount || 0) > 0);
  const hasQtyCredit   = (doc.items || []).some(it => (it.credit_qty || 0) > 0);
  const hasPriceCredit = (doc.items || []).some(it => (it.credit_price_diff || 0) > 0);
  const selectedSupplier = suppliers.find(s => String(s.id) === String(form.supplier_id));
  const hasDeposit     = selectedSupplier ? !!selectedSupplier.has_deposit      : !!doc.supplier_has_deposit;
  const hasPurchaseTax = selectedSupplier ? !!selectedSupplier.has_purchase_tax : !!doc.supplier_has_purchase_tax;

  // ── Side-by-side review derivations (edit mode only) ──
  // Invoice-level agreed discount: derive from the currently-selected supplier
  // (not the doc.* snapshot) so switching supplier updates the alert live —
  // same pattern as the deposit / purchase_tax flags above.
  const docFiles = withRotations((doc.files && doc.files.length > 0)
    ? doc.files
    : (doc.file_path ? [{ file_path: doc.file_path, page_number: 1 }] : []));
  const invAgreedRaw  = selectedSupplier
    ? selectedSupplier.fixed_invoice_discount
    : doc.supplier_fixed_invoice_discount;
  const invAgreedType = (selectedSupplier?.fixed_invoice_discount_type)
    || doc.supplier_fixed_invoice_discount_type
    || 'percent';
  const stripIssues = editing ? deriveReviewIssues({
    items, form,
    duplicateWarning: !!doc.duplicate_warning,
    duplicateOf: doc.duplicate_of || null,
    supplierMissing: !form.supplier_id,
    recipientMismatch: doc.recipient_match === 'mismatch',
    recipientName: doc.extracted_recipient_name || '',
    canAddAlias: isAdmin,
    recipientDecidedBy: doc.recipient_decided_by,
    recipientBusinessId: doc.extracted_recipient_business_id || null,
    restaurantBusinessId: doc.restaurant_business_id || null,
    moveSuggestion: doc.status === 'טיוטה' ? (doc.move_suggestion || null) : null,
    supplierUsesDeliveryNotes: selectedSupplier ? !!selectedSupplier.uses_delivery_notes : undefined,
    canMarkDeliveryNotes: isAdmin,
    openDeliveryNotes,
    canConvertToMerkaz: isAdmin,
    agreedInvoiceDiscount: invAgreedRaw,
    agreedInvoiceDiscountType: invAgreedType,
    // The money as printed (#314) — copied once from the AI's reading. Drafts
    // only (owner, 23/10/2026): once approved, a later edit is a person's
    // deliberate correction, and the AI's figures would keep it amber forever.
    printed: doc.status === 'טיוטה' ? {
      subtotal: doc.printed_subtotal, invoice_discount: doc.printed_invoice_discount,
      invoice_discount_type: doc.printed_invoice_discount_type, vat: doc.printed_vat, total: doc.printed_total,
    } : null,
    vatRate,
    vatRule: { inclusive: supplierVatInclusive, partial: supplierPartialVat },
  }) : [];
  // The gate's answer for THIS draft, and its reasons the strip has no live
  // chip for (utils/gateMark.js): warning chips, so the strip never says
  // אין תקלות פתוחות while the queue said red or yellow.
  const gateShown = editing && docIsCurrent && gateAnswer && gateAnswer.docId === String(id) ? gateAnswer : null;
  const reviewIssues = gateShown?.gate_state === 'ready'
    ? [...stripIssues, ...gateStripIssues(gateShown.gate_reasons, {
      opening: gateBaseline(gateShown.docId), form, now: new Date(Date.now()), uploadedAt: doc.created_at,
      vatRate, vatRule: { inclusive: supplierVatInclusive, partial: supplierPartialVat },
      duplicateShown: stripIssues.some(i => i.id === 'duplicate'),
      vatShown: stripIssues.some(i => i.id === 'totals' && (i.check === 'vat' || i.check === 'vat_missing')),
      // The strip's delivery-note chips answer live once their data is in —
      // the supplier's flag for a note, the open-notes lookup for an invoice —
      // and a type the check no longer applies to (corrected to a מרכזת, say)
      // answers it too. A lookup still loading or failed does not (round 2).
      deliveryNotesSettled: form.doc_type === 'תעודת משלוח'
        ? !!selectedSupplier
        : (!OPEN_NOTES_DOC_TYPES.includes(form.doc_type) || openDeliveryNotes != null),
    })]
    : stripIssues;
  const totalsIssue = reviewIssues.find(i => i.id === 'totals') || null;
  const totalsFix = (kind) => totalsIssue?.actions?.find(a => a.kind === kind) || null;
  const queuePos = editing && doc.status === 'טיוטה' && draftQueue
    ? draftPosition(draftQueue, id)
    : null;
  // By supplier, inside a run of two or more the badge counts the run
  // ("2 מתוך 3 מהספק"); going back to 1 is the sign the supplier changed. The
  // whole-queue count moves to the badge's tooltip.
  const queueGroup = queuePos?.position && queueOrder === 'supplier' ? draftGroupPosition(draftQueue, id) : null;
  // Inside a /drafts filter the badge names it — `לתיקון 1 מתוך 2` — so the
  // count reads as "my reds", not as a wrong count of the whole queue (#332
  // design review); the supplier run moves to the tooltip.
  const activeFilter = queuePos ? queueFilter() : null;
  const queueWhole = queuePos
    ? (queuePos.position ? `${activeFilter ? GATE_MARKS[activeFilter].label : 'טיוטה'} ${queuePos.position} מתוך ${queuePos.total}` : `${queuePos.total} טיוטות בתור`)
    : null;
  const queueRun = queueGroup && queueGroup.size > 1
    ? `${queueGroup.index} מתוך ${queueGroup.size} ${queueGroup.noSupplier ? 'ללא ספק' : 'מהספק'}`
    : null;
  const queueLabel = activeFilter ? queueWhole : (queueRun || queueWhole);
  const queueTitle = activeFilter
    ? (queueRun || undefined)
    : (queueLabel && queueLabel !== queueWhole ? `${queueWhole} בתור` : undefined);

  // ── View mode ──
  // Net unit price after the line discount — one calc for the desktop table
  // and the mobile rows, so the two can never disagree.
  const netUnitPrice = (item) => {
    const disc = item.discount || 0;
    return item.discount_type !== 'amount'
      ? Math.round((item.unit_price || 0) * (1 - disc / 100) * 1000) / 1000
      : Math.round(((item.unit_price || 0) - disc) * 1000) / 1000;
  };
  const openFiles = () => {
    setShowFiles(true);
    track(EVENTS.DOCUMENT_FILE_OPENED, { device: isMobile ? 'mobile' : 'desktop', trigger: 'manual' });
  };
  const closeFiles = () => { setShowFiles(false); setFileFull(false); };
  const maximizeFiles = () => {
    setFileFull(true);
    track(EVENTS.DOCUMENT_FILE_OPENED, { device: 'desktop', trigger: 'fullscreen' });
  };
  // The pane on a wide screen unless it was taken full screen; always full
  // screen at ≤1024px.
  const filePane = showFiles && docFiles.length > 0 && !isNarrow && !fileFull;
  const fileFullscreen = showFiles && docFiles.length > 0 && (isNarrow || fileFull);
  // Items as rows wherever the table can't fit: a phone, a narrow window
  // (wider threshold when the credit columns are showing), or beside the
  // open scan — the table's end-side columns (totals, credits) otherwise hid
  // behind a sideways scroll.
  const compactItems = isNarrow || filePane || (hasAnyCredit && tableTooWideWithCredits);
  // Admin only: the button is offered to admins alone, and the route refuses
  // anyone else anyway.
  const rotatePage = (file) => { if (isAdmin) savePageTurn(file); };
  // One download per page, staggered so the browser doesn't drop later ones.
  const downloadAll = () => {
    docFiles.forEach((f, i) => setTimeout(() => downloadDocFile(f.file_path), i * 250));
  };
  // Every way off this page that the page itself offers (the breadcrumb, now
  // that חזור is gone) asks first while leaving would lose something: unsaved
  // edits (#369) or a customer note still waiting. Closing the tab is covered
  // by the beforeunload guard. The queue's own moves don't come through here:
  // אשר והבא has saved by then, and דלג asks its own save-or-discard question.
  const leaveTo = (to) => {
    const prompt = leavePrompt({ changed: hasUnsavedWork(), pendingNote: !!pendingCustomerNote });
    if (!prompt) { navigate(to); return; }
    askConfirm({ ...prompt, onConfirm: () => navigate(to) });
  };
  // The view-mode approve, split so the link window can run it as its decision.
  // `linkDecided` hands the leaving to that window. Returns the status written.
  const quickApprove = async ({ linkDecided = false } = {}) => {
    const stillHere = routeOwner();
    try {
      if (!linkDecided && doc.doc_type === 'חשבונית מרכזת' && doc.status === 'טיוטה') {
        const offered = await offerLinkDecision({ ...doc }, { source: 'view' });
        // No from a route that moved is not "nothing to link" (see approveCurrent).
        if (offered || !stillHere()) return undefined;
      }
      // The page's own approve: a person had the document open, so the entry
      // is the page's (direct / queue_edit / approve_next), not the queue's ✓.
      const review = reviewPayload();
      const r = await documentsApi.approve(id, { entry: reviewEntry, review });
      const changed = r.data?.review?.fields_changed_count;
      track(EVENTS.DOCUMENT_APPROVED, {
        source: 'doc_view_quick', doc_type: doc.doc_type, advanced_to_next: false, ...gateAnalytics(null),
        ...(review.review_seconds != null ? { review_seconds: review.review_seconds } : {}),
        ...(Number.isInteger(changed) ? { fields_changed_count: changed } : {}),
      });
      // A draft with a credit line is filed as לא תקין by the server (#260).
      const written = r.data?.status || 'תקין';
      toast(written === 'לא תקין' ? 'יש זיכוי פתוח — המסמך נשמר כלא תקין' : 'הטיוטה אושרה');
      const bf = describeBackfill(r.data?.backfill);
      if (bf) toast(bf);
      if (linkDecided) return written;
      if (!stillHere()) return undefined;
      // `doc` is the pre-approval copy (טיוטה); pass the status just
      // written, same as approveCurrent.
      await continueAfterPendingNote(() => offerCreditLinkThenLeave({ ...doc, status: written }));
    } catch (err) {
      // The server's reason matters here — a refused approval is usually a
      // duplicate doc_num, and it names the document it collided with.
      // 'שגיאה באישור' alone leaves nothing to act on.
      toast(err.response?.data?.error || 'שגיאה באישור', 'error');
      return undefined;
    }
    return undefined;
  };
  // What the link window's buttons do when it opened on the approve button. Built
  // each render, so a queue that finished loading meanwhile is the one used. The way
  // out is set only once a save stands: a refused one stays on the form.
  const keepDraftFromWindow = async () => {
    if (!linkModal.stillHere()) return;
    setLinkModal(null);
    const changed = skipHasChanges();
    // Same question as דלג: a waiting customer note is lost on the way out.
    if (pendingCustomerNote) { setSkipPrompt({ changed }); return; }
    await leaveSkipped({ save: changed });
  };
  // A decision can land after the route moved: the document is decided and its links
  // still go through, but the way out belongs to the next document's window by then.
  const linkDecision = !linkModal?.decisionFor ? null : linkModal.decisionFor.source === 'edit' ? {
    save: async (to) => {
      const written = await approveCurrent(goToNextDraft, { payNote: linkModal.decisionFor.payNote, status: to, linkDecided: true });
      if (written && linkModal.stillHere()) linkDecisionLeaveRef.current = to === 'לא תקין' ? goToNextDraftOrStay : goToNextDraft;
      return written;
    },
    keep: keepDraftFromWindow,
    movesOn: true,
    savedFirst: !!linkListedForRef.current,
  } : {
    save: async (to) => {
      let written;
      if (to === 'תקין') written = await quickApprove({ linkDecided: true });
      else {
        const r = await documentsApi.updateStatus(id, to, reviewPayload());
        toast('המסמך נשמר כלא תקין');
        written = r.data?.status || to;
      }
      if (written && linkModal.stillHere()) linkDecisionLeaveRef.current = () => navigate('/drafts');
      return written;
    },
    keep: async () => {
      if (!linkModal.stillHere()) return;
      setLinkModal(null);
      await load();
    },
  };
  // The one filled button in the title row, per role. Admin: approve for a
  // draft, edit otherwise — both admin-only, like the server routes behind
  // them (PUT /documents/:id, POST /:id/approve: requireAdmin); on a phone
  // they fold into ⋮ (a lone icon button beside the title read as a glitch).
  // A restaurant manager can't edit — their job on this page is paying, so
  // בוצע תשלום leaves ⋮ and becomes their button, on every screen size.
  const primaryAction = isAdmin ? (doc.status === 'טיוטה' ? 'approve' : 'edit')
    : canPay ? 'pay' : null;
  const isInvoice = ['חשבונית', 'חשבונית מרכזת'].includes(doc.doc_type);
  const closeMenuThen = (fn) => () => { setDocMenuOpen(false); fn(); };
  const returnNote = createdReturn?.forDocId === doc.id
    ? { ...doc.return_note, eligible: false, created: { id: createdReturn.id, doc_num: createdReturn.doc_num } }
    : (doc.return_note || null);
  const formMatchesSaved = !editing || (String(form.supplier_id || '') === String(doc.supplier_id || '') && form.doc_type === doc.doc_type);
  const offerReturnNote = isAdmin && !!returnNote?.eligible && formMatchesSaved;
  // Rows typed by hand are the only work on a return draft, and checking the
  // other document mid-entry is natural: while editing it opens in a new tab.
  const relatedDocLink = (targetId, label) => (editing
    ? <a className="doc-related-btn" href={tenantUrl(doc.restaurant_id, `/document/${targetId}`)} target="_blank" rel="noopener noreferrer">{label}</a>
    : <button type="button" className="doc-related-btn" onClick={() => leaveTo(`/document/${targetId}`)}>{label}</button>);
  // ⋮ entries for this user, in the order they show. While editing, only
  // שמור כטיוטה on a draft plus the two the edit screen always had (pay,
  // delete) — its own bar owns the rest.
  const menuEntries = [
    // Save the edits and stay, still a draft (#260) — the review bar's exits
    // all leave the document; this one doesn't.
    editing && isAdmin && doc.status === 'טיוטה' && {
      key: 'save-draft', icon: 'save', label: 'שמור כטיוטה', onClick: closeMenuThen(() => saveEdit({ status: 'טיוטה' })),
      disabled: saving || decisionsLocked, title: 'שמירת השינויים בלי לאשר — המסמך נשאר טיוטה' },
    // One scan, two documents (#367). The server decides who is offered this
    // (doc.return_note, lib/combinedReturnNote.js), from the SAVED document: a
    // supplier or type changed on screen counts once it is saved.
    !editing && returnNote?.created && doc.status === 'טיוטה' && {
      key: 'open-return', icon: 'docs', label: `פתח תעודת החזרה ${returnNote.created.doc_num}`, onClick: closeMenuThen(() => leaveTo(`/document/${returnNote.created.id}`)) },
    offerReturnNote && {
      key: 'create-return', icon: 'docs', label: 'צור תעודת החזרה בנוסף', onClick: closeMenuThen(() => setCreatingReturnFor(doc.id)), disabled: saving },
    !editing && isAdmin && isMobile && primaryAction === 'approve' && {
      key: 'approve', icon: 'check', label: 'אשר טיוטה', onClick: closeMenuThen(quickApprove), tone: 'success' },
    !editing && isAdmin && isMobile && {
      key: 'edit', icon: 'edit', label: 'עריכה', onClick: closeMenuThen(startEdit) },
    canPay && primaryAction !== 'pay' && {
      key: 'pay', icon: 'card', label: doc.payment_count > 0 ? 'תשלום יתרה' : 'בוצע תשלום', onClick: payFromMenu, disabled: saving },
    !editing && isAdmin && {
      key: 'forward', icon: 'send', label: forwardSending ? 'שולח...' : 'שלח להנה"ח', onClick: closeMenuThen(handleManualForward),
      disabled: forwardSending, title: 'שלח את המסמך המקורי לכתובת ה-Dokka של המסעדה' },
    !editing && docFiles.length > 0 && {
      key: 'download', icon: 'download', label: docFiles.length > 1 ? `הורדת המסמך המקורי (${docFiles.length} קבצים)` : 'הורדת המסמך המקורי', onClick: closeMenuThen(downloadAll) },
    !editing && isAdmin && isInvoice && {
      key: 'backfill', icon: 'coins', label: 'מילוי מחירים בתעודות עם מחיר 0', onClick: closeMenuThen(runBackfill),
      title: 'מילוי המחיר האחרון מהקטלוג בשורות במחיר 0 בתעודות המשלוח המקושרות' },
    // On a draft the recipient chip offers it when the server recognises the
    // restaurant (#304); this is the way when it doesn't, and on any approved
    // document (#361), which returns to draft there.
    isAdmin && {
      key: 'move', icon: 'restaurant', label: 'העבר למסעדה אחרת', onClick: closeMenuThen(() => setMoving(true)), disabled: saving },
    isAdmin && {
      key: 'delete', icon: 'trash', label: 'מחק מסמך', onClick: closeMenuThen(handleDeleteDocument), tone: 'danger' },
  ].filter(Boolean);
  const MENU_TONES = { success: { color: 'var(--success-ink)', fontWeight: 600 }, danger: { color: 'var(--danger-ink)' } };
  // The document's own payment state — resolved on the server
  // (lib/documentPaymentState.js: טרם שולם / שולם חלקית / שולם ביתר / שולם),
  // the client only renders it (beside the total, in the facts strip), and
  // only where payment tracking is on.
  // "טרם שולם" is what makes "was it paid?" answerable at a glance — the
  // total's label no longer has to (it was "סה"כ לתשלום", read as "owed").
  const paid = paymentTrackingEnabled ? doc.payment_state : null;
  const PAID_BADGE = { unpaid: 'badge-gray', paid: 'badge-green', partial: 'badge-orange', over: 'badge-orange' };

  // The way to the original scan — one control, in the facts strip on
  // desktop and in the title row's actions on a phone: icon + "מסמך מקורי"
  // on one line (the app's one word for it; the owner cut the page-count
  // sub-line). With the desktop pane open it reads as pressed and closes it.
  const origControl = docFiles.length === 0 ? (
    <div className="doc-orig is-empty" title="לא נשמר צילום או קובץ של המסמך הזה">
      <Icon name="docs" size={18} />
      <span className="doc-orig-title">אין מסמך מקורי</span>
    </div>
  ) : (
    <button type="button" className={`doc-orig${filePane ? ' is-open' : ''}`}
      onClick={filePane ? closeFiles : openFiles} aria-pressed={filePane}
      title={filePane ? 'הסתרת המסמך המקורי' : undefined}>
      <Icon name="docs" size={18} />
      <span className="doc-orig-title">מסמך מקורי</span>
    </button>
  );

  // The edit screen's action bar. Above 900px it is the last row of the items
  // card, under the totals, so it covers neither the items nor the scanned
  // document (#359); ≤900px (the split stacks) it's a bottom sheet at page level.
  const reviewBar = (
    <div className="review-actions-sticky" style={{ display:'flex', gap:12, flexWrap:'wrap' }}>
        {/* One filled button — the action this screen exists for — at the
            reading start (right, in RTL), its alternatives as quiet buttons
            beside it, ביטול set apart behind a divider (the bar is a floating
            card). For a draft (#260): אשר והבא · דלג. אשר ושמור
            is gone (אשר והבא returns to /drafts once the queue is done); a
            plain "save, stay a draft" is ⋮ → שמור כטיוטה or Ctrl+S. סטטוס set
            to לא תקין (or a credit line) turns the primary into an amber
            שמור כלא תקין והבא. For an approved document the primary is
            שמור שינויים. */}
        <>
          {isAdmin && doc.status === 'טיוטה' ? (
            <>
              {editSavesInvalid ? (
                /* סטטוס לא תקין, or a credit on a line (the document cannot be
                   approved — approveCurrent forces the status, and so does the
                   server): the primary says what it will do, in amber. */
                <>
                  <button className="btn btn-caution" onClick={approveAndNext} disabled={saving || decisionsLocked || isImplausibleDateYear(form.date)} aria-busy={recheckPending}
                    title="Ctrl+Enter / ⌘Enter">
                    {saving ? 'שומר...' : <><Icon name="alert" size={16} /> שמור כלא תקין והבא ⏭</>}
                  </button>
                  {/* Beside the button it explains; a full row of its own on a phone
                      (.review-actions-sticky > span spans the grid). Only for a
                      credit — a hand-picked לא תקין needs no explaining. */}
                  {editHasCredits && <span className="review-credit-hint" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', color: 'var(--warning-ink)' }}>
                    <Icon name="info" size={14} /> נפתח זיכוי — המסמך יישמר כלא תקין
                  </span>}
                </>
              ) : (
                <button className="btn btn-primary" onClick={approveAndNext} disabled={saving || decisionsLocked || isImplausibleDateYear(form.date)} aria-busy={recheckPending}
                  title={recheckPending ? 'בודק עדכונים מהאישור הקודם…' : 'אישור המסמך ומעבר לטיוטה הבאה בתור (Ctrl+Enter / ⌘Enter)'}>
                  {saving ? 'מאשר...' : <><Icon name="check" size={16} /> אשר והבא ⏭</>}
                </button>
              )}
              {/* Instant tip (.btn-tip): what דלג does is not obvious from the
                  word alone — the document stays a draft (owner, #260). */}
              <button className="btn btn-secondary btn-tip" onClick={skipDraft} disabled={saving || decisionsLocked || draftQueue === null}
                data-tip="המסמך יישאר טיוטה · שינויים יישמרו רק באישורך" aria-describedby="skip-tip-text">
                דלג ⏭
              </button>
              <span id="skip-tip-text" hidden>המסמך יישאר טיוטה ויעבור לטיוטה הבאה; שינויים יישמרו רק באישורך</span>
            </>
          ) : (
            <button className="btn btn-success" onClick={() => saveEdit()} disabled={saving || decisionsLocked || isImplausibleDateYear(form.date)}
              title="Ctrl+S / ⌘S">
              {saving ? 'שומר...' : <><Icon name="save" size={16} /> שמור שינויים</>}
            </button>
          )}
          {pendingNoteChip}
          <button className="btn btn-secondary" onClick={() => setCommentsOpen(true)} disabled={saving}>
            <Icon name="comment" size={15} /> {commentCount > 0 ? `הערות (${commentCount})` : 'הערות'}
          </button>
          <button className="btn btn-secondary review-cancel" onClick={cancelEdit} disabled={saving}>ביטול</button>
        </>
    </div>
  );

  return (
    <div className={`doc-page${editing ? ' is-reviewing' : ''}`}>
      <div className="breadcrumb">
        <span className="link" onClick={() => leaveTo('/')}>ספקים</span>
        <Icon name="chevron-inline" size={13} className="breadcrumb-sep" />
        {doc.supplier_id ? (
          <span className="link" onClick={() => leaveTo(`/supplier/${doc.supplier_id}`)}>{doc.supplier_name}</span>
        ) : (
          <span style={{ color: 'var(--warning-ink)' }}>לא זוהה ספק</span>
        )}
        <Icon name="chevron-inline" size={13} className="breadcrumb-sep" />
        <span>{doc.doc_type} {doc.doc_num || '#' + doc.id}</span>
      </div>

      {/* Same .page-title-row / .page-menu idiom as the supplier page: the
          title on the start side, [הערות] [primary] [⋮] on the end — every
          action this page offers is up here, none at the bottom. The title
          says what the document IS ("חשבונית INV-1025") — the owner's call;
          it was briefly the supplier name, and the page read as the
          supplier's. The badges beside it are states only. */}
      <div className={`page-title-row doc-title-row${editing ? ' is-editing' : ''}`}>
        <div className="doc-title-block">
          <h1 className="page-title">
            <Icon name="documents" size={18} /> {doc.doc_type} {doc.doc_num || '#' + doc.id}
            <span className={`badge ${statusBadge(doc.status)}`}>{doc.status}</span>
            {doc.edited ? <span className="badge badge-gray">נערך</span> : null}
            {hasAnyCredit ? <span className="badge badge-orange">יש זיכויים</span> : null}
            {/* The supplier-month payment lifecycle for this document's month.
                Only on a document that counts toward it: is_payable (server-
                side; a note already rolled up under a מרכזת is not), and never
                a draft — a draft is in no lifecycle input (lib/draftScope.js),
                so approving from it would approve a month it isn't part of.
                isPayableDocType too: is_payable also covers a credit note,
                which never makes a month approvable (approvalOfferForDoc
                skips it for the same reason). */}
            {paymentTrackingEnabled && supplierLifecycle && doc.supplier_id
              && doc.status !== 'טיוטה' && Boolean(doc.is_payable) && isPayableDocType(doc.doc_type) && (
              <span className="doc-lifecycle" title={`מצב התשלום של הספק לחודש ${monthLabelFromDocDate(doc.date)}`}>
                <span className="doc-lifecycle-month">תשלום {monthLabelFromDocDate(doc.date)}:</span>
                <PaymentLifecycleControl
                  lifecycle={supplierLifecycle}
                  canApprove={canEdit}
                  busy={approvingMonth}
                  onToggle={toggleMonthApproval}
                />
              </span>
            )}
          </h1>
          {offerReturnNote && returnNote.number && (
            <div className="doc-related-link">
              במסמך יש גם תעודת החזרה <bdi>{returnNote.number}</bdi>{' · '}
              <button type="button" className="doc-related-btn" onClick={() => setCreatingReturnFor(doc.id)} disabled={saving}>
                צור
              </button>
            </div>
          )}
          {returnNote?.created && (
            <div className="doc-related-link">
              נוצרה בנוסף:{' '}
              {relatedDocLink(returnNote.created.id, <>תעודת החזרה {returnNote.created.doc_num}</>)}
            </div>
          )}
          {returnNote?.source && (
            <div className="doc-related-link">
              נוצרה מתוך:{' '}
              {relatedDocLink(returnNote.source.id, <>{returnNote.source.doc_type} {returnNote.source.doc_num}</>)}
            </div>
          )}
        </div>
        <div className="page-title-actions">
          {/* The thread, in view mode too and for every role — CommentsModal
              itself turns read-only for a viewer. Edit mode keeps its own
              הערות button in the review bar. */}
          {!editing && isMobile && origControl}
          {/* With notes it stands out (navy outline) instead of relying on
              "(2)" being read. On a phone it's icon-only — a speech bubble reads
              as comments on its own — with the count as a bubble on it; the
              accessible name keeps the count ("הערות (2)") either way. */}
          {!editing && (
            <button type="button"
              className={`btn btn-secondary btn-sm doc-comments-btn${commentCount > 0 ? ' has-notes' : ''}`}
              onClick={() => setCommentsOpen(true)}
              aria-label={commentCount > 0 ? `הערות (${commentCount})` : 'הערות'}
              title={isMobile ? undefined : (commentCount > 0 ? `${commentCount} הערות` : 'הוספת הערה')}>
              <Icon name="comment" size={isMobile ? 18 : 15} />
              {!isMobile && <span>{commentCount > 0 ? `הערות (${commentCount})` : 'הערות'}</span>}
              {isMobile && commentCount > 0 && <span className="doc-comments-count" aria-hidden="true">{commentCount}</span>}
            </button>
          )}
          {/* On a draft עריכה stays beside אשר טיוטה, quieter — reviewing a
              draft IS editing it, so it must not hide in ⋮ on desktop. */}
          {!editing && primaryAction === 'pay' && (
            <button type="button" className="btn btn-success btn-sm" onClick={payFromMenu} disabled={saving}>
              <Icon name="card" size={15} /> {doc.payment_count > 0 ? 'תשלום יתרה' : 'בוצע תשלום'}
            </button>
          )}
          {!editing && !isMobile && (primaryAction === 'edit' || primaryAction === 'approve') && (
            <button type="button" className={`btn btn-sm ${primaryAction === 'edit' ? 'btn-primary' : 'btn-secondary'}`} onClick={startEdit}>
              <Icon name="edit" size={15} /> עריכה
            </button>
          )}
          {!editing && !isMobile && primaryAction === 'approve' && (
            <button type="button" className="btn btn-success btn-sm" onClick={quickApprove}>
              <Icon name="check" size={15} /> אשר טיוטה
            </button>
          )}
          {(menuEntries.length > 0 || viewAsTargets.length > 0) && (
            <div className="page-menu">
              <button
                type="button"
                className="page-menu-trigger"
                aria-label="פעולות נוספות"
                aria-expanded={docMenuOpen}
                onClick={() => setDocMenuOpen(o => !o)}
              >⋮</button>
              {docMenuOpen && (
                <>
                  <div className="menu-backdrop" onClick={() => setDocMenuOpen(false)} />
                  <div className="page-menu-panel" style={{ minWidth: 250 }}>
                    {menuEntries.map(m => (
                      <button
                        key={m.key}
                        type="button"
                        className="page-menu-item"
                        style={MENU_TONES[m.tone]}
                        disabled={m.disabled}
                        title={m.title}
                        onClick={m.onClick}
                      >
                        <Icon name={m.icon} size={15} /> {m.label}
                      </button>
                    ))}
                    <ViewAsMenuItems onDone={() => setDocMenuOpen(false)} />
                  </div>
                </>
              )}
            </div>
          )}
        </div>
        {payNoteModal && (
          <Modal
            onClose={() => setPayNoteModal(null)}
            size={440}
            ariaLabel="המסמך מסומן כשולם"
          >
            <h2 id="pay-note-title" className="modal-title">המסמך מסומן כשולם</h2>
            <p style={{ fontSize: '0.88rem', color: 'var(--ink-muted)', marginTop: 0 }}>
              תעדי מדוע המסמך נחשב שולם. ההערה תישמר על המסמך, על שמך, ותוצג לכל המשתמשים.
            </p>
            <div className="reason-chips" style={{ marginBottom: 10 }}>
              <button type="button" className="reason-chip" onClick={() => setPayNote(PAY_NOTE_QUICK)}>
                {PAY_NOTE_QUICK}
              </button>
            </div>
            <div className="form-group">
              <label htmlFor="pay-note-text">הערה</label>
              <textarea id="pay-note-text" className="form-control" rows={3} value={payNote}
                onChange={e => setPayNote(e.target.value)} placeholder="למשל: הופיע שולם על המסמך" />
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setPayNoteModal(null)}>ביטול</button>
              <button type="button" className="btn btn-success" disabled={!payNote.trim() || saving}
                onClick={() => { const m = payNoteModal; setPayNoteModal(null); approveCurrent(m.goAfter, { payNote: payNote.trim(), status: m.status }); }}>
                <Icon name="check" size={16} /> {payNoteModal.status === 'לא תקין' ? 'שמור כלא תקין והבא' : 'אשר והבא'}
              </button>
            </div>
          </Modal>
        )}
        {skipPrompt && (
          <Modal onClose={() => setSkipPrompt(null)} size={400}
            ariaLabel={skipPrompt.changed ? 'יש שינויים שלא נשמרו' : 'הערה ממתינה ללקוח'}>
            <h2 className="modal-title">{skipPrompt.changed ? 'יש שינויים שלא נשמרו' : 'הערה ממתינה ללקוח'}</h2>
            {/* The same status chip as the title row, so "stays a draft" reads at a glance. */}
            <p style={{ fontSize: '0.88rem', color: 'var(--ink-muted)', marginTop: 0, lineHeight: 1.8 }}>
              המסמך יישאר <span className={`badge ${statusBadge('טיוטה')}`}>טיוטה</span> ולא יחזור בתור עד לרענון הדף.
              {pendingCustomerNote ? ' ההערה שממתינה ללקוח לא תישלח — היא נשלחת רק עם אישור.' : ''}
            </p>
            <div className="modal-actions" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
              {skipPrompt.changed ? (
                <>
                  <button type="button" className="btn btn-primary" style={{ justifyContent: 'center' }} disabled={saving} onClick={() => leaveSkipped({ save: true })}>
                    <Icon name="save" size={16} /> שמור שינויים ודלג
                  </button>
                  <button type="button" className="btn btn-secondary" style={{ justifyContent: 'center' }} disabled={saving} onClick={() => leaveSkipped({ save: false })}>
                    דלג בלי לשמור
                  </button>
                </>
              ) : (
                <button type="button" className="btn btn-primary" style={{ justifyContent: 'center' }} disabled={saving} onClick={() => leaveSkipped({ save: false })}>
                  דלג
                </button>
              )}
              <button type="button" className="btn btn-secondary" style={{ justifyContent: 'center' }} onClick={() => setSkipPrompt(null)}>
                חזרה לעריכה
              </button>
            </div>
          </Modal>
        )}
        {paymentModal && canPay && (
          <PaymentModal
            supplierId={doc.supplier_id}
            supplierName={doc.supplier_name}
            documentId={doc.id}
            documentInfo={{
              doc_type: doc.doc_type,
              doc_num: doc.doc_num,
              total: doc.total,
              remaining: doc.payment_remaining,
              paidTotal: doc.payment_paid_total,
            }}
            approvalOffer={approvalOfferForDoc({
              lifecycle: supplierLifecycle, docDate: doc.date, docType: doc.doc_type, docTotal: doc.total,
              dateFrom: docMonth?.dateFrom, dateTo: docMonth?.dateTo,
            })}
            onSuccess={() => { setPaymentModal(false); setLoading(true); load(); }}
            onClose={() => setPaymentModal(false)}
          />
        )}
      </div>

      {/* A customer note held for after approval outlives leaving edit mode
          (the view-mode אשר טיוטה sends it too), so view mode shows it. */}
      {!editing && pendingNoteChip && <div className="doc-pending-note">{pendingNoteChip}</div>}

      {/* Facts strip — the supplier page's stat-tile language in one row:
          value over a small label, tiles spread evenly. The total leads and
          is the biggest (what an owner looks for first), then ספק (a link) ·
          מספר מסמך · סוג מסמך · תאריך · הנה"ח (once forwarded). The original's control is
          its last cell on desktop (no height of its own); a phone shows the
          total full-width on top, the rest two per row, and the original as a
          title-row button. A missing value is a muted "—" — never ₪0.00,
          which would read as a free document. */}
      {!editing && (
        <div className="card doc-facts">
          <div className="doc-fact doc-fact--total">
            {/* The payment badge sits ON the amount it's about — "₪21,705.88
                טרם שולם" answers "how much, and was it paid" in one glance. */}
            <span className="doc-fact-total-row">
              {doc.total == null
                ? <span className="doc-fact-value is-missing">—</span>
                : <span className="doc-fact-value is-total">{formatCurrency(doc.total)}</span>}
              {paid && (
                <span className={`badge ${PAID_BADGE[paid.state] || 'badge-gray'}`}
                  // Desktop hover: what was paid AND what's left (both from the
                  // server — payment_state.amount and payment_remaining); the
                  // badge itself keeps the supplier list's "שולם חלקית · ₪paid".
                  title={!isMobile && paid.state === 'partial'
                    ? `שולם ${formatCurrency(paid.amount)} · נותר לתשלום ${formatCurrency(doc.payment_remaining)}`
                    : undefined}>
                  {paid.label}{paid.amount != null && ` · ${formatCurrency(paid.amount)}`}
                </span>
              )}
            </span>
            <span className="doc-fact-label">{totalLabel(doc.vat)}</span>
          </div>
          <div className="doc-fact doc-fact--supplier">
            {doc.supplier_id ? (
              <button type="button" className="doc-fact-value doc-fact-link" title={doc.supplier_name}
                onClick={() => leaveTo(`/supplier/${doc.supplier_id}`)}>
                {doc.supplier_name}<HqOnlyLock hqOnly={doc.supplier_hq_only} />
              </button>
            ) : <span className="doc-fact-value doc-fact-missing-supplier">ספק לא זוהה</span>}
            <span className="doc-fact-label">ספק</span>
          </div>
          <div className="doc-fact doc-fact--num">
            {/* bdi/ltr: a number like 2026-401579289 or A-12/3 must never be
                reordered by the RTL paragraph around it. */}
            {doc.doc_num
              ? <span className="doc-fact-value"><bdi dir="ltr">{doc.doc_num}</bdi></span>
              : <span className="doc-fact-value is-missing">—</span>}
            <span className="doc-fact-label">מספר מסמך</span>
          </div>
          <div className="doc-fact">
            {doc.doc_type
              ? <span className="doc-fact-value">{doc.doc_type}</span>
              : <span className="doc-fact-value is-missing">—</span>}
            <span className="doc-fact-label">סוג מסמך</span>
          </div>
          <div className="doc-fact">
            {doc.date
              ? <span className="doc-fact-value">{formatDateShort(doc.date) || doc.date}</span>
              : <span className="doc-fact-value is-missing">—</span>}
            <span className="doc-fact-label">תאריך</span>
          </div>
          {forwardInfo && (forwardInfo.status === 'sent' || forwardInfo.status === 'failed') && (
            <div className="doc-fact">
              {forwardInfo.status === 'sent' ? (
                <span className="doc-fact-value is-ok" title={`נשלח אל ${forwardInfo.sent_to}`}>
                  <Icon name="check-circle" size={13} /> הועבר {formatDateShort(forwardInfo.sent_at) || ''}
                </span>
              ) : (
                <span className="doc-fact-value is-err" title={forwardInfo.error_message || 'נכשלה העברה'}>
                  <Icon name="alert" size={13} /> נכשלה העברה
                </span>
              )}
              <span className="doc-fact-label">הנה"ח</span>
            </div>
          )}
          {!isMobile && <div className="doc-fact doc-fact--orig">{origControl}</div>}
        </div>
      )}

      {/* Duplicate card — shown for any draft flagged by the bulk-upload path,
          in view mode AND while editing (owner, 12/10/2026, #243: from the
          edit screen the strip's small "השווה" chip action was not noticed).
          The issue strip still lists the duplicate as blocking, so its "all
          clear" stays truthful; this card is where the comparison is offered.
          While editing it carries only השווה: deleting or renumbering is done
          in the form itself, and a second set of exits would compete with
          the form's own buttons. */}
      {doc.duplicate_warning ? (
        <div style={{
          background: 'var(--danger-bg)', border: '2px solid var(--danger-ink)', borderRadius: 8,
          padding: '14px 18px', marginBottom: 16,
          display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        }}>
          <Icon name="alert" size={20} style={{ color: 'var(--danger-ink)' }} />
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontWeight: 700, color: 'var(--danger-ink)', fontSize: '0.95rem' }}>
              כפיל של מסמך קיים{doc.duplicate_of ? ` #${doc.duplicate_of.id}` : ''}
            </div>
            <div style={{ fontSize: '0.85rem', color: 'var(--danger-ink)', marginTop: 4 }}>
              {/* One fact; the buttons beside it say what to do. */}
              מספר <strong>{doc.doc_num}</strong> כבר קיים אצל הספק
              {doc.duplicate_of ? ` (${doc.duplicate_of.doc_type}, ${formatDateShort(doc.duplicate_of.date) || '—'})` : ''}.
            </div>
            {/* The money / date / lines verdict before any click — most
                duplicates are settled right here (#234). */}
            {doc.duplicate_of && (
              <div className="dup-signals" style={{ marginTop: 8 }}>
                {duplicateSignals(doc, doc.duplicate_of).map(sig => (
                  <span key={sig.key} className={`dup-signal${sig.same ? ' is-same' : ''}`}>
                    <Icon name={sig.same ? 'check' : 'close'} size={11} /> {sig.label}
                  </span>
                ))}
              </div>
            )}
          </div>
          {isAdmin && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {doc.duplicate_of && (
                <button type="button" className="btn btn-sm btn-primary" onClick={() => setComparing(true)}>
                  <Icon name="eye" size={15} /> השווה זה ליד זה
                </button>
              )}
              {!editing && (
                <>
                  <button type="button" className="btn btn-sm btn-secondary" onClick={() => setDeleting('duplicate')}>
                    <Icon name="trash" size={15} /> מחק טיוטה
                  </button>
                  <button type="button" className="btn btn-sm btn-secondary" onClick={changeDocNumber}>
                    <Icon name="edit" size={15} /> שנה מספר מסמך
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      ) : null}

      {editing ? (
        <>
          {/* Recipient mismatch is folded into the issue strip below as a
              warning chip (single source of truth), so no separate banner here. */}
          {/* The re-check of a draft loaded early found it changed after the
              reviewer had started on it (back-filled prices, another admin's
              save): their edits stay, this says so and offers the server's
              copy. Amber, never blocking. */}
          {updatedMeanwhile && (
            <div className="review-updated-note" role="status">
              <Icon name="alert" size={16} />
              <span>הטיוטה עודכנה בזמן שעבדת עליה (מחירים או שורות). אישור עכשיו ידרוס את העדכון — בחרי איך להמשיך.</span>
              <span className="review-updated-actions">
                <button type="button" className="btn btn-sm btn-outline" disabled={reloadingFresh} onClick={reloadFreshCopy}>
                  {reloadingFresh ? 'טוען…' : 'טען את הגרסה העדכנית'}
                </button>
                <button type="button" className="btn btn-sm btn-outline" disabled={reloadingFresh} onClick={() => setUpdatedMeanwhile(false)}>המשך עם השינויים שלי</button>
              </span>
            </div>
          )}
          {recheckFailed && (
            <div className="review-updated-note" role="status">
              <Icon name="alert" size={16} />
              <span>לא הצלחנו לבדוק את הטיוטה מול השרת אחרי האישור הקודם — האישור נעול עד שהבדיקה תעבור.</span>
              <span className="review-updated-actions">
                <button type="button" className="btn btn-sm btn-outline" onClick={retryRecheck}>נסה שוב</button>
              </span>
            </div>
          )}
          {/* Sticky issue strip, then metadata as a compact full-width bar,
              then the document ⟷ items split. */}
          <ReviewIssueSummary issues={reviewIssues} gate={gateShown} queueInfo={queueLabel} queueTitle={queueTitle}
            hideAllClear={form.doc_type === 'תעודת החזרה' && !!returnNote?.generated && items.length === 0} onAction={a => {
            if (a.kind === 'move') {
              setMoving(true);
            } else if (a.kind === 'add-alias') {
              // The DOCUMENT's restaurant, not whatever the tenant chip shows.
              confirmAddRecipientAlias({
                askConfirm, restaurantId: doc.restaurant_id, name: a.name,
                docBusinessId: doc.extracted_recipient_business_id || null,
                onAdded: () => setDoc(d => ({ ...d, recipient_match: 'match' })),
              });
            } else if (a.kind === 'ignore-bid') {
              confirmIgnoreRecipientBid({
                askConfirm, restaurantId: doc.restaurant_id, bid: a.bid, name: a.name, docSaved: true,
                onResult: r => setDoc(d => ({ ...d, recipient_match: r.match, recipient_decided_by: r.decided_by })),
              });
            } else if (a.kind === 'mark-delivery-notes') {
              markSupplierDeliveryNotes(form.supplier_id, setSuppliers);
            } else if (a.kind === 'convert-to-merkaz') {
              // Form only — saving/approving then opens the link modal through the
              // existing מרכזת path; the chip disappears as the type no longer qualifies.
              setForm(f => ({ ...f, doc_type: 'חשבונית מרכזת' }));
              setConvertRange(openNotesRange(openDeliveryNotes, form.supplier_id, form.date));
              toast('סוג המסמך שונה לחשבונית מרכזת — בשמירה ייפתח חלון קישור התעודות');
            } else if (a.kind === 'compare') {
              setComparing(true);
            } else if (a.kind === 'vat-auto') {
              // The calculated VAT, by the reviewer's tap (#314) — the same as
              // pressing אוטו on the totals card.
              setVatMode('auto');
              setVatOpenedManual(false);
              if (editBaselineRef.current) editBaselineRef.current.vatToggled = true;
            } else if (a.kind === 'vat-fit' || a.kind === 'discount-fit') {
              applyTotalsFix(a);
            } else {
              navigate(a.to);
            }
          }} />
          {/* Document metadata (supplier / type / number / date / status) is
              glance-once info — it sits on top as a compact bar so it doesn't
              compete with the document for the operator's eye; the split below
              then pairs the scanned document directly with the פריטים table,
              which is the real line-by-line validation work. */}
          {/* No heading (owner, #260): the page title names the document and
              every field is labelled, so "עריכת פרטי מסמך" only cost a line.
              The group keeps the name for screen readers. */}
          <div className="card review-meta-bar" role="group" aria-label="פרטי מסמך">
            <div className="ri-meta-row">
              <div className="form-group ri-meta-supplier">
                <OcrSupplierReadLine show={isAdmin} read={ocrSupplierRead(doc, form.supplier_id)} label="ספק *" />
                {/* Searchable combobox with inline "add supplier" folded in as a
                    list row (no separate button). onCreate creates the supplier
                    directly from the typed name, refreshes the list, and selects it. */}
                <SearchableSelect
                  id="ri-supplier"
                  ariaLabel="ספק"
                  value={form.supplier_id || ''}
                  onChange={v => setForm(f => ({ ...f, supplier_id: v }))}
                  options={supplierOptions}
                  placeholder="חפש או בחר ספק…"
                  initialQuery={form.supplier_id ? '' : (doc.extracted_supplier_name || '')}
                  onQueryChange={setNewSupplierName}
                  createLabel={q => `הוסף ספק חדש: "${q}"`}
                  onCreate={async (name) => {
                    try {
                      // With the ח.פ. printed on this document (captured on upload).
                      const r = await suppliersApi.create({ name, business_id: doc.ocr_supplier_business_id || '', ...newSupplierOptions });
                      const fresh = await suppliersApi.getAll({ include_empty: true });
                      setSuppliers(fresh.data);
                      setForm(f => ({ ...f, supplier_id: String(r.data.id) }));
                      setNewSupplierOptions({ hq_only: false, category_line_id: '' });
                      toast(r.data.created === false ? 'הספק כבר קיים — נבחר' : 'ספק נוצר');
                    } catch (e) {
                      toast(e.response?.data?.error || 'שגיאה ביצירת ספק', 'error');
                    }
                  }}
                />
                {showNewSupplierOptions && (
                  <NewSupplierOptions
                    compact
                    name={pendingSupplierName}
                    businessId={doc.ocr_supplier_business_id || ''}
                    hqOnly={newSupplierOptions.hq_only}
                    onHqOnlyChange={hq_only => setNewSupplierOptions(current => ({ ...current, hq_only }))}
                    categoryLineId={newSupplierOptions.category_line_id}
                    onCategoryLineChange={category_line_id => setNewSupplierOptions(current => ({ ...current, category_line_id }))}
                  />
                )}
                {!form.supplier_id && (
                  <p style={{ fontSize: '0.78rem', color: 'var(--warning-ink)', marginTop: 6 }}>
                    {doc.extracted_supplier_name
                      ? <><Icon name="alert" size={13} /> {`לא נמצא ספק תואם ל"${doc.extracted_supplier_name}". בחר ספק קיים או הוסף חדש.`}</>
                      : <><Icon name="alert" size={13} /> לא זוהה ספק — יש לבחור ספק לפני האישור</>}
                  </p>
                )}
              </div>
              <div className="form-group ri-meta-doctype">
                <label>סוג מסמך</label>
                <SearchableSelect
                  id="ri-doc-type"
                  ariaLabel="סוג מסמך"
                  value={form.doc_type}
                  onChange={v => setForm(f => ({ ...f, doc_type: v }))}
                  options={DOC_TYPE_OPTIONS}
                  placeholder="חפש סוג מסמך…"
                />
              </div>
              {form.doc_type === 'חשבונית זיכוי' && (
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 600 }}>
                    <input type="checkbox" checked={!!form.is_price_diff_credit}
                      onChange={e => setForm(f => ({ ...f, is_price_diff_credit: e.target.checked ? 1 : 0 }))} />
                    זיכוי על הפרש מחיר (לא הוחזרה סחורה)
                  </label>
                  <div style={{ fontSize: 12, color: 'var(--ink-muted)', marginTop: 4 }}>
                    כשמסומן — בניתוח הרכש הזיכוי מקטין את הסכום בלבד, ולא נספר כהחזרת כמות.
                  </div>
                </div>
              )}
              <div className="form-group ri-meta-docnum">
                <label>מספר מסמך</label>
                <input id="ri-doc_num" className="form-control" value={form.doc_num} onChange={e => setForm(f => ({ ...f, doc_num: e.target.value }))} />
              </div>
              <div className="form-group ri-meta-date">
                <label>תאריך</label>
                <input id="ri-date" className="form-control" placeholder="DD/MM/YYYY" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} />
                {isImplausibleDateYear(form.date) && (
                  <div style={{ color: 'var(--danger-ink)', fontSize: 13, marginTop: 4 }}>
                    <Icon name="alert" size={15} /> השנה בתאריך ({form.date.trim().slice(-4)}) חריגה — לא ניתן לשמור עד שתתוקן
                  </div>
                )}
              </div>
              <div className="form-group ri-meta-status">
                <label>סטטוס</label>
                <SearchableSelect
                  ariaLabel="סטטוס"
                  value={form.status}
                  onChange={v => setForm(f => ({ ...f, status: v }))}
                  options={STATUS_OPTIONS}
                />
              </div>
            </div>

          </div>

          <div className="review-split">
          {/* Keyed by document: a warm advance keeps this subtree mounted,
              and the pane's own page / zoom / collapsed state belongs to the
              draft it was set on — the next one opens on its first page, the
              prefetched one, as a cold open does (Codex review, round 2). */}
          <ReviewDocPane key={doc.id} files={docFiles} onRotate={isAdmin ? rotatePage : undefined} prefetched={handedScan} />
          <div className="review-split-fields">
          <div className="card review-items-card" id="ri-items" tabIndex={-1}>
            <div className="review-items-head">
              {/* The row count, to check against the paper that every line was read (#260). */}
              <h3 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--ink)', margin: 0 }}>
                פריטים{' '}
                <span className="items-count" style={{ fontSize: '0.82rem', fontWeight: 500, color: 'var(--ink-muted)' }}>
                  ({items.length === 1 ? 'שורה אחת' : `${items.length} שורות`})
                </span>
              </h3>
              <button type="button" className="btn btn-sm btn-outline" onClick={addItem}>+ הוסף שורה</button>
            </div>
            <div className="table-wrapper table-scroll-mobile">
              <table className="items-table-edit">
                <thead>
                  <tr>
                    <th style={{ width: '12%' }}>מק"ט</th>
                    <th style={{ width: '19%' }}>תיאור</th>
                    <th style={{ width: '6%'  }}>כמות</th>
                    <th style={{ width: '7%'  }}>מחיר ליחידה</th>
                    <th style={{ width: '7%'  }}>הנחה</th>
                    <th style={{ width: '7%'  }}>מחיר נטו</th>
                    {hasDeposit     && <th style={{ width: '7%', background: 'var(--info-bg)' }}>פיקדון</th>}
                    {hasPurchaseTax && <th style={{ width: '7%', background: 'var(--tint-strong)' }}>מס קנייה</th>}
                    <th style={{ width: '7%'  }}>סך הכל</th>
                    {/* כמות לזיכוי / הפרש מחיר לזיכוי are pinned to the same PIXEL
                        width (not just the same %), and both labels wrap onto two
                        lines. The table uses default auto layout, which sizes each
                        column to its widest cell's content — a percentage alone is
                        only a hint there, so "הפרש מחיר לזיכוי" (nearly double the
                        length of "כמות לזיכוי") was winning that negotiation and
                        rendering visibly wider until both were pinned equal and
                        wrapped short enough to stop dictating the column width. */}
                    <th style={{ width: 90, background: 'var(--warning-bg)' }}>
                      <span style={{ display: 'inline-block', maxWidth: 78, whiteSpace: 'normal', lineHeight: 1.2 }}>כמות לזיכוי</span>
                    </th>
                    <th style={{ width: 90, background: 'var(--warning-bg)' }}>
                      <span style={{ display: 'inline-block', maxWidth: 78, whiteSpace: 'normal', lineHeight: 1.2 }}>הפרש מחיר לזיכוי</span>
                    </th>
                    <th style={{ width: '7%', background: 'var(--warning-bg)' }}>סך לזיכוי</th>
                    <th style={{ width: '6%'  }}></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, i) => {
                    const isPct = it.discount_type !== 'amount';
                    const hasCredit = (parseFloat(it.credit_qty) || 0) > 0 || (parseFloat(it.credit_price_diff) || 0) > 0;
                    // Shared row analysis (utils/reviewIssues) — the exact same
                    // computation feeds the consolidated issue strip, so the
                    // inline cell flags and the strip can never diverge.
                    const {
                      mismatched, totalWarn, expectedTotal, curPrice, anchorPrice, anchorIsFixed,
                      priceChanged, overcharge, creditDiffSet,
                      agreedDiscount, agreedDiscountType, discountMismatch, discountOvercharge,
                    } = analyzeReviewRow(it);
                    return (
                      <tr key={it._key ?? it.id ?? i} style={{ background: hasCredit ? 'var(--warning-bg)' : undefined }}>
                        <td><input value={it.sku || ''} onChange={e => updateItem(i,'sku',e.target.value)} placeholder="—" /></td>
                        <td><input value={it.description||''} onChange={e => updateItem(i,'description',e.target.value)} /></td>
                        <td><input type="number" step="any" value={it.qty} onChange={e => updateItem(i,'qty',e.target.value)} /></td>
                        <td style={{ verticalAlign: 'top' }}>
                          <input
                            id={`ri-item-${i}-unit_price`}
                            type="number"
                            step="any"
                            value={it.unit_price}
                            onChange={e => updateItem(i,'unit_price',e.target.value)}
                            title={priceChanged ? (anchorIsFixed ? `מחיר קבוע בקטלוג: ${formatCurrency(anchorPrice)}` : `מחיר קודם בקטלוג: ${formatCurrency(anchorPrice)}`) : undefined}
                            style={priceChanged
                              ? { border: '2px solid var(--warning-ink)', background: 'var(--warning-bg)', borderRadius: 4 }
                              : undefined}
                          />
                          {priceChanged && (
                            <div
                              title={anchorIsFixed
                                ? `מחיר קבוע בקטלוג: ${formatCurrency(anchorPrice)} • עכשיו: ${formatCurrency(curPrice)}`
                                : `שינוי מחיר — היה ${formatCurrency(anchorPrice)}, עכשיו ${formatCurrency(curPrice)}`}
                              style={{ fontSize: '0.68rem', color: 'var(--warning-ink)', fontWeight: 600, marginTop: 3, lineHeight: 1.2 }}
                            >
                              {anchorIsFixed ? 'קבוע ' : 'היה '}
                              {formatCurrency(anchorPrice)} <Icon name="chevron-inline" size={11} /> {formatCurrency(curPrice)}
                            </div>
                          )}
                          <PriceSourceBadge item={it} />
                          {overcharge > 0 && !creditDiffSet && (
                            <button
                              type="button"
                              onClick={() => updateItem(i, 'credit_price_diff', overcharge)}
                              style={{ marginTop: 4, fontSize: '0.72rem', fontWeight: 600, color: '#fff', background: 'var(--danger-ink)', border: 'none', borderRadius: 4, padding: '3px 8px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                              title={`מילוי אוטומטי של 'הפרש מחיר לזיכוי' בערך ${formatCurrency(overcharge)} ליחידה. הסכום הכולל יהיה ${formatCurrency(overcharge * (parseFloat(it.qty) || 0))}.`}
                            >
                              פתח זיכוי על ההפרש ({formatCurrency(overcharge)}/יח')
                            </button>
                          )}
                        </td>
                        <td style={{ verticalAlign: 'top' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                            <input id={`ri-item-${i}-discount`} type="number" step="any" min="0" value={it.discount??0} onChange={e => updateItem(i,'discount',e.target.value)}
                              title={discountMismatch ? `הנחה מוסכמת: ${agreedDiscount}${agreedDiscountType === 'percent' ? '%' : '₪'}` : undefined}
                              style={{
                                // 48px floor: at 0 the column squeezed it to a clipped "0".
                                flex:1, minWidth:48,
                                ...(discountMismatch ? { border: '2px solid var(--warning-ink)', background: 'var(--warning-bg)', borderRadius: 4 } : {}),
                              }} />
                            <button type="button" onClick={() => updateItem(i,'discount_type',isPct?'amount':'percent')}
                              style={{ padding:'3px 6px', fontSize:'0.78rem', fontWeight:700, border:'1px solid var(--outline)', borderRadius:4, background: isPct?'var(--info-bg)':'var(--warning-bg)', color: isPct?'var(--info-ink)':'var(--warning-ink)', cursor:'pointer', flexShrink:0 }}>
                              {isPct?'%':'₪'}
                            </button>
                          </div>
                          {discountMismatch && (
                            <div style={{ fontSize: '0.72rem', color: 'var(--warning-ink)', fontWeight: 600, marginTop: 3, whiteSpace: 'nowrap' }}>
                              הנחה מוסכמת: {agreedDiscount}{agreedDiscountType === 'percent' ? '%' : '₪'}
                            </div>
                          )}
                          {discountOvercharge > 0 && !creditDiffSet && (
                            <button
                              type="button"
                              onClick={() => updateItem(i, 'credit_price_diff', discountOvercharge)}
                              style={{ marginTop: 4, fontSize: '0.72rem', fontWeight: 600, color: '#fff', background: 'var(--danger-ink)', border: 'none', borderRadius: 4, padding: '3px 8px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                              title={`מילוי אוטומטי של 'הפרש מחיר לזיכוי' בערך ${formatCurrency(discountOvercharge)} ליחידה. הסכום הכולל יהיה ${formatCurrency(discountOvercharge * (parseFloat(it.qty) || 0))}.`}
                            >
                              פתח זיכוי על ההנחה החסרה ({formatCurrency(discountOvercharge)}/יח')
                            </button>
                          )}
                        </td>
                        <td style={{ textAlign:'center', fontSize:'0.88rem', fontWeight:500 }}>{formatCurrency(calcNetUnitPrice(it))}</td>
                        {hasDeposit && (
                          <td style={{ background: 'var(--info-bg)' }}>
                            <input type="number" step="any" min="0" value={it.deposit ?? 0}
                              onChange={e => updateItem(i, 'deposit', e.target.value)}
                              style={{ background: 'transparent' }} />
                          </td>
                        )}
                        {hasPurchaseTax && (
                          <td style={{ background: 'var(--tint-strong)' }}>
                            <input type="number" step="any" min="0" value={it.purchase_tax ?? 0}
                              onChange={e => updateItem(i, 'purchase_tax', e.target.value)}
                              style={{ background: 'transparent' }} />
                          </td>
                        )}
                        <td
                          id={`ri-item-${i}-line_total`}
                          tabIndex={-1}
                          title={mismatched || totalWarn ? `כמות × מחיר נטו = ${formatCurrency(expectedTotal)}` : undefined}
                          style={{
                            textAlign: 'center', fontSize: '0.9rem', fontWeight: 600,
                            color: mismatched ? 'var(--danger-ink)' : totalWarn ? 'var(--warning-ink)' : 'var(--ink)',
                            background: mismatched ? 'var(--danger-bg)' : totalWarn ? 'var(--warning-bg)' : 'var(--tint)',
                            border: mismatched ? '2px solid var(--danger-ink)' : totalWarn ? '2px solid var(--warning-ink)' : undefined,
                          }}
                        >
                          {formatCurrency(it.line_total)}
                          {(mismatched || totalWarn) && (
                            <div style={{ fontSize:'0.72rem', fontWeight:600, marginTop:3, whiteSpace:'nowrap' }}>
                              צפוי: {formatCurrency(expectedTotal)}
                            </div>
                          )}
                        </td>
                        <td style={{ background:'var(--warning-bg)' }}>
                          <input type="number" step="any" min="0" value={it.credit_qty??0} onChange={e => updateItem(i,'credit_qty',e.target.value)}
                            style={{ background: 'transparent' }} />
                        </td>
                        <td style={{ background:'var(--warning-bg)' }}>
                          <input type="number" step="any" min="0" value={it.credit_price_diff??0} onChange={e => updateItem(i,'credit_price_diff',e.target.value)}
                            style={{ background: 'transparent' }}
                            title="הפרש מחיר לכל יחידה. סך הזיכוי = הפרש × כמות. שימושי כשהספק חייב יותר מהמחיר המוסכם." />
                        </td>
                        <td style={{ textAlign:'center', fontSize:'0.88rem', background:'var(--warning-bg)', color: hasCredit?'var(--warning-ink)':'var(--ink-subtle)', fontWeight: hasCredit?600:400 }}>
                          {hasCredit ? formatCurrency(it.credit_total) : '—'}
                        </td>
                        <td>
                          <button type="button" className="btn btn-sm btn-danger" onClick={() => removeItem(i)}
                            aria-label={`מחק שורה: ${it.description || 'ללא תיאור'}`} title="מחק שורה">
                            <Icon name="trash" size={14} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {items.length === 0 && <div className="empty-state" style={{ padding:24 }}><p>אין פריטים. לחץ "הוסף שורה"</p></div>}

            <div className="totals-box" style={{ marginTop:20 }}>
              <div className="total-row">
                <span>סכום לפני מע"מ</span>
                <span style={TOTALS_AMOUNT}>{formatCurrency(form.subtotal)}</span>
              </div>
              {(() => {
                const discountAmount = calcInvoiceDiscountAmount(form.subtotal, form.invoice_discount, form.invoice_discount_type);
                // Invoice-level discount agreement enforcement. Anchor =
                // supplier.fixed_invoice_discount (invAgreedRaw/invAgreedType,
                // derived above from the currently-selected supplier). The
                // mismatch predicate is shared with the issue strip via
                // utils/reviewIssues so the two can't diverge.
                const agreedInv     = invAgreedRaw > 0 ? invAgreedRaw : null;
                const agreedInvType = invAgreedType;
                const invDiscMismatch = invoiceDiscountMismatch({
                  agreed: invAgreedRaw,
                  agreedType: invAgreedType,
                  current: form.invoice_discount,
                  currentType: form.invoice_discount_type,
                });
                return (
                  <>
                    <div className="total-row" style={{ alignItems:'center' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        <span>הנחה</span>
                        <div style={{ display:'inline-flex', border:'1px solid var(--outline)', borderRadius:5, overflow:'hidden' }}>
                          {[
                            { id: 'percent', label: '%' },
                            { id: 'amount',  label: '₪' },
                          ].map(opt => (
                            <button key={opt.id} type="button"
                              onClick={() => handleInvoiceDiscount({ invoice_discount_type: opt.id })}
                              style={{ padding:'2px 9px', border:'none', cursor:'pointer', fontFamily:'inherit',
                                fontSize:'0.74rem', fontWeight:600,
                                background: form.invoice_discount_type === opt.id ? 'var(--selected-solid-bg)' : 'transparent',
                                color:      form.invoice_discount_type === opt.id ? '#fff'    : '#777' }}>
                              {opt.label}
                            </button>
                          ))}
                        </div>
                        <FitButton fix={totalsFix('discount-fit')} title={a => `הנחה של ${a.discount} ₪ — הסה"כ יתאים למסמך`} onFix={applyTotalsFix} />
                      </div>
                      <input id="ri-invoice_discount" type="number" step="any" value={form.invoice_discount ?? 0}
                        onChange={e => handleInvoiceDiscount({ invoice_discount: e.target.value })}
                        title={invDiscMismatch ? `הנחה מוסכמת לחשבונית: ${agreedInv}${agreedInvType === 'percent' ? '%' : '₪'}` : undefined}
                        style={{ width:90, borderRadius:6, padding:'3px 8px', fontSize:'0.9rem', textAlign:'left', direction:'ltr',
                          border: invDiscMismatch ? '2px solid var(--warning-ink)' : '1px solid var(--outline)',
                          background: invDiscMismatch ? 'var(--warning-bg)' : undefined }} />
                    </div>
                    {invDiscMismatch && (
                      <div className="total-row" style={{ paddingTop: 0, marginTop: -4 }}>
                        <span style={{ color: 'var(--warning-ink)', fontSize: '0.78rem' }}>
                          <Icon name="alert" size={13} /> הנחה קבועה מוסכמת לספק: {agreedInv}{agreedInvType === 'percent' ? '%' : ' ₪'} — לא תואם למסמך
                        </span>
                      </div>
                    )}
                    {invDiscMismatch && (
                      <div className="total-row">
                        <span style={{ fontSize: '0.78rem', color: 'var(--warning-ink)', fontWeight: 600 }}>
                          הנחה מוסכמת לחשבונית: {agreedInv}{agreedInvType === 'percent' ? '%' : '₪'}
                        </span>
                        <span></span>
                      </div>
                    )}
                    {hasInvoiceDiscount(discountAmount) && (
                      <div className="total-row">
                        <span style={{ color:'var(--warning-ink)' }}>סכום ההנחה</span>
                        <span style={{ ...TOTALS_AMOUNT, color:'var(--warning-ink)', fontWeight:600 }}>
                          {signedDiscount(discountAmount)}
                        </span>
                      </div>
                    )}
                    {hasInvoiceDiscount(discountAmount) && (
                      <div className="total-row" style={{ borderTop:'1px solid var(--hairline)', paddingTop:6 }}>
                        <span>סה"כ לפני מע"מ אחרי הנחה</span>
                        <span style={TOTALS_AMOUNT}>
                          {formatCurrency((parseFloat(form.subtotal) || 0) - discountAmount)}
                        </span>
                      </div>
                    )}
                  </>
                );
              })()}
              {/* Wraps: label + "לבדוק מע״מ" + toggle + the 120px manual input
                  don't fit the narrow totals card on one line, and without
                  wrapping the input pushed the pane into a horizontal scroll. */}
              <div className="total-row" id="ri-vat" tabIndex={-1} style={{ alignItems:'center', flexWrap:'wrap', rowGap:6 }}>
                <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap', minWidth:0 }}>
                  <span>מע"מ<VatCheckTag show={isAdmin && !!doc.vat_needs_check} /></span>
                  <div style={{ display:'inline-flex', border:'1px solid var(--outline)', borderRadius:5, overflow:'hidden' }}>
                    {['auto','manual'].map(m => (
                      <button key={m} type="button" onClick={() => { setVatMode(m); setVatOpenedManual(false); if (editBaselineRef.current) editBaselineRef.current.vatToggled = true; }}
                        style={{ padding:'2px 7px', border:'none', cursor:'pointer', fontFamily:'inherit',
                          fontSize:'0.74rem', fontWeight:600, whiteSpace:'nowrap',
                          background: vatMode === m ? 'var(--selected-solid-bg)' : 'transparent',
                          color: vatMode === m ? '#fff' : '#777' }}>
                        {m === 'auto' ? `אוטו (${vatRate}%)` : 'ידני ₪'}
                      </button>
                    ))}
                  </div>
                  <FitButton fix={totalsFix('vat-fit')} title={a => `מע"מ ${a.vat} ₪ — הסה"כ יתאים למסמך`} onFix={applyTotalsFix} />
                </div>
                {vatMode === 'auto' ? (
                  <span style={TOTALS_AMOUNT}>{formatCurrency(form.vat)}</span>
                ) : (
                  <input type="number" step="any" min="0" value={form.vat}
                    onChange={e => handleManualVat(e.target.value)}
                    style={{ width:90, maxWidth:'100%', marginRight:'auto', border:'1.5px solid var(--focus-color)', borderRadius:6,
                      padding:'3px 8px', fontSize:'0.9rem', textAlign:'left', direction:'ltr' }} />
                )}
              </div>
              {/* The form can open in manual on its own (utils/editVatMode.js:
                  the stored VAT isn't the auto figure) — say why, and how to
                  recompute, so a VAT that no longer follows the lines isn't a
                  surprise. Only when it opened that way by itself — not when the
                  user picked "ידני", and not for a supplier rule, which already
                  explains itself. */}
              {/* …unless the totals check is already saying it about the VAT (#314). */}
              {vatMode === 'manual' && vatOpenedManual && !supplierVatInclusive && !supplierPartialVat && totalsIssue?.targetId !== 'ri-vat' && (
                <div className="vat-manual-hint">המע"מ נשאר כפי שנשמר במסמך. לחישוב מחדש לפי האחוז, לחצו "אוטו".</div>
              )}
              <div className="total-row grand" id="ri-total" tabIndex={-1}>
                <span>{totalLabel(form.vat)}</span>
                <span style={{ ...TOTALS_AMOUNT, fontWeight:700 }}>{formatCurrency(form.total)}</span>
              </div>
              {/* The printed total, only while the totals check flags a gap
                  (#314); the colour stays in the strip — one amber per card. */}
              {totalsIssue && doc.printed_total != null && (
                <div className="total-row ri-printed-total">
                  <span>סה"כ במסמך</span>
                  <span style={TOTALS_AMOUNT}>{formatCurrency(doc.printed_total)}</span>
                </div>
              )}
              {/* "שולם" and "אשר גם לתשלום" — one compact row (owner, #260: the two
                  stacked blocks took a third of the card's height).
                  שולם — for a draft only: אשר והבא approves and then records
                  the payment (a draft itself cannot be paid). Needs a supplier;
                  the method defaults to the supplier's, the date to today.
                  אשר גם לתשלום — approve the supplier-month along with the
                  draft, for when no payment is being recorded (with שולם
                  ticked the same choice lives inside that block). Not tied to
                  שולם on purpose: a הוראת קבע supplier hides שולם but its month
                  still needs approving. Manager+ like the approval itself,
                  though only an admin reaches a draft form. */}
              {(() => {
                const showPay = isAdmin && paymentTrackingEnabled && doc.status === 'טיוטה' && isPayableDocType(form.doc_type) && !autoPaysOnApproval();
                const monthOffer = canEdit && paymentTrackingEnabled && doc.status === 'טיוטה' && !payOnApprove.checked ? approveOnPayOffer() : null;
                if (!showPay && !monthOffer) return null;
                return (
                  <div className="ri-approve-options" style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--hairline)', fontSize: '0.85rem' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px 18px' }}>
                      {showPay && (
                        <label className="ri-pay-on-approve" style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: form.supplier_id ? 'pointer' : 'default', fontWeight: 500 }}>
                          <input
                            type="checkbox"
                            checked={payOnApprove.checked}
                            disabled={!form.supplier_id}
                            onChange={e => setPayOnApprove(p => ({
                              ...p, checked: e.target.checked,
                              payment_method: p.payment_method || selectedSupplierMethod() || PAYMENT_METHODS[0],
                              payment_date: p.payment_date || todayISO(),
                            }))}
                          />
                          <span>שולם</span>
                          {!form.supplier_id && (
                            <span style={{ fontSize: '0.75rem', color: 'var(--ink-muted)', fontWeight: 400 }}>— יש לבחור ספק תחילה</span>
                          )}
                        </label>
                      )}
                      {monthOffer && (
                        <label className="ri-approve-month" style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontWeight: 500 }}
                          title="מאשר את כל החודש של הספק לתשלום, באישור המסמך">
                          <input type="checkbox" checked={payOnApprove.approveMonth}
                            onChange={e => setPayOnApprove(p => ({ ...p, approveMonth: e.target.checked }))} />
                          <span>אשר גם לתשלום ({monthOffer.monthLabel})</span>
                        </label>
                      )}
                    </div>
                    {/* Shown once ticked: the box approves the WHOLE month, not
                        just this document — worth saying at the moment it counts. */}
                    {monthOffer && payOnApprove.approveMonth && (
                      <div className="ri-approve-month-note" style={{ fontSize: '0.75rem', color: 'var(--ink-muted)', marginTop: 4 }}>
                        מאשר את כל החודש של הספק לתשלום, באישור המסמך
                      </div>
                    )}
                    {showPay && payOnApprove.checked && (
                      /* Method and date side by side, compact — the card is narrow. */
                      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '6px 10px', marginTop: 8 }}>
                        <div className="form-group" style={{ margin: 0 }}>
                          <label htmlFor="ri-pay-method" style={{ fontSize: '0.75rem', marginBottom: 2 }}>אמצעי תשלום</label>
                          <select id="ri-pay-method" className="form-control" value={payOnApprove.payment_method}
                            style={{ padding: '4px 8px', fontSize: '0.82rem' }}
                            onChange={e => setPayOnApprove(p => ({ ...p, payment_method: e.target.value }))}>
                            {PAYMENT_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                          </select>
                        </div>
                        <div className="form-group" style={{ margin: 0 }}>
                          <label htmlFor="ri-pay-date" style={{ fontSize: '0.75rem', marginBottom: 2 }}>תאריך תשלום</label>
                          <input id="ri-pay-date" type="date" className="form-control" value={payOnApprove.payment_date}
                            style={{ padding: '4px 8px', fontSize: '0.82rem' }}
                            onChange={e => setPayOnApprove(p => ({ ...p, payment_date: e.target.value }))} />
                        </div>
                        {approveOnPayOffer() && (
                          <label style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}
                            title="מאשר את כל החודש של הספק לתשלום, באישור המסמך">
                            <input type="checkbox" checked={payOnApprove.approveMonth}
                              onChange={e => setPayOnApprove(p => ({ ...p, approveMonth: e.target.checked }))} />
                            <span>אשר לתשלום לחודש {approveOnPayOffer().monthLabel}</span>
                          </label>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}
              {items.some(it => (it.credit_qty||0) > 0 || (it.credit_price_diff||0) > 0) && (
                <div className="total-row" style={{ color:'var(--warning-ink)', marginTop:8, borderTop:'1px solid var(--warning-line)', paddingTop:8 }}>
                  <span>סך זיכויים פתוחים (ללא מע״מ)</span>
                  <span style={{ fontWeight:700 }}>{formatCurrency(items.reduce((s,it)=>s+(it.credit_total||0),0))}</span>
                </div>
              )}
              <p className="ri-totals-note" style={{ fontSize:'0.72rem', color:'var(--ink-subtle)', marginTop:6, marginBottom:0 }}>
                * הסכומים מחושבים אוטומטית · מע"מ: אוטו לפי אחוז, או ידני
              </p>
            </div>
            {!narrowEdit && reviewBar}
          </div>
          </div>
          </div>
        </>
      ) : (
        <>
          {/* Digital copy on the start side; the original beside it on
              desktop once opened (auto-opened when the user arrived via
              "צפה במסמך"), full screen on a phone. One card, same place on
              both, is the way in — the old eye/download pair inside a details
              card hid the scan behind a toggle that grew an 80vh iframe. */}
          <div className={`dv-layout${filePane ? ' is-split' : ''}`}>
            <div className="dv-main">
              <div className="card">
                <h3 style={{ fontSize:'1rem', fontWeight:700, marginBottom:16, color:'var(--ink)' }}>
                  פריטים{doc.items?.length > 0 ? ` (${doc.items.length})` : ''}
                </h3>
                {/* Rows wherever the table can't fit (compactItems). */}
                {doc.items?.length > 0 && compactItems ? (
                  <DocItemsList items={doc.items} netPrice={netUnitPrice} />
                ) : doc.items?.length > 0 ? (
                  <div className="table-wrapper table-scroll-mobile">
                    <table className="doc-items-table">
                      <thead>
                        <tr>
                          {/* The item name leads, as on the phone rows; מחיר נטו
                              only differs from מחיר ליחידה when there's a discount. */}
                          <th>תיאור</th><th>מק"ט</th><th>כמות</th>
                          <th>מחיר ליחידה</th>{hasAnyDiscount && <><th>הנחה</th><th>מחיר נטו</th></>}
                          {hasDeposit     && <th style={{ background:'var(--info-bg)' }}>פיקדון</th>}
                          {hasPurchaseTax && <th style={{ background:'var(--tint-strong)' }}>מס קנייה</th>}
                          <th>סך הכל</th>
                          {/* Credit columns only when some line uses them — each of
                              the two kinds on its own, so a quantity-only credit
                              doesn't carry a column of price-difference dashes. */}
                          {hasAnyCredit && (
                            <>
                              {hasQtyCredit   && <th style={{ background:'var(--warning-bg)' }}>כמות לזיכוי</th>}
                              {hasPriceCredit && <th style={{ background:'var(--warning-bg)' }}>הפרש מחיר לזיכוי</th>}
                              <th style={{ background:'var(--warning-bg)' }}>סך לזיכוי</th>
                              <th style={{ background:'var(--warning-bg)' }}>אחרי זיכוי</th>
                            </>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {doc.items.map((item, i) => {
                          const disc = item.discount || 0;
                          const isPct = item.discount_type !== 'amount';
                          const net = netUnitPrice(item);
                          const discLabel = disc > 0 ? (isPct?`${disc}%`:formatCurrency(disc)) : '—';
                          const creditQty = item.credit_qty || 0;
                          const creditPriceDiff = item.credit_price_diff || 0;
                          const creditTotal = item.credit_total || 0;
                          const payable = (item.line_total||0) - creditTotal;
                          const hasCredit = creditQty > 0 || creditPriceDiff > 0;
                          return (
                            <tr key={item.id??i} style={{ background: hasCredit?'var(--warning-bg)':undefined }}>
                              <td>{item.description}</td>
                              <td style={{ color:'var(--ink-muted)', fontSize:'0.85rem' }}>{item.sku||'—'}</td>
                              <td>{item.qty}</td>
                              <td>{formatCurrency(item.unit_price)}<div><PriceSourceBadge item={item} /></div></td>
                              {hasAnyDiscount && <><td>{discLabel}</td><td>{formatCurrency(net)}</td></>}
                              {hasDeposit && (
                                <td style={{ background:'var(--info-bg)', textAlign:'center' }}>
                                  {(item.deposit || 0) > 0 ? formatCurrency(item.deposit) : '—'}
                                </td>
                              )}
                              {hasPurchaseTax && (
                                <td style={{ background:'var(--tint-strong)', textAlign:'center' }}>
                                  {(item.purchase_tax || 0) > 0 ? formatCurrency(item.purchase_tax) : '—'}
                                </td>
                              )}
                              <td style={{ fontWeight:600 }}>{formatCurrency(item.line_total)}</td>
                              {hasAnyCredit && (
                                <>
                                  {hasQtyCredit && (
                                    <td style={{ background:'var(--warning-bg)', color: creditQty>0?'var(--warning-ink)':'var(--ink-subtle)', textAlign:'center' }}>
                                      {creditQty>0 ? creditQty : '—'}
                                    </td>
                                  )}
                                  {hasPriceCredit && (
                                    <td style={{ background:'var(--warning-bg)', color: creditPriceDiff>0?'var(--warning-ink)':'var(--ink-subtle)', textAlign:'center' }}>
                                      {creditPriceDiff>0 ? formatCurrency(creditPriceDiff) : '—'}
                                    </td>
                                  )}
                                  <td style={{ background:'var(--warning-bg)', color: hasCredit?'var(--warning-ink)':'var(--ink-subtle)', fontWeight: hasCredit?600:400, textAlign:'center' }}>
                                    {hasCredit ? formatCurrency(creditTotal) : '—'}
                                  </td>
                                  <td style={{ fontWeight: hasCredit?700:600, color: hasCredit?'var(--ink)':'inherit' }}>
                                    {formatCurrency(payable)}
                                  </td>
                                </>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="empty-state">
                    {isAdmin && doc.status === 'טיוטה' && returnNote?.generated ? (
                      <>
                        <p>אין פריטים עדיין</p>
                        <button type="button" className="btn btn-sm btn-outline" onClick={addFirstRow}>הוסף שורה</button>
                      </>
                    ) : <p>אין פריטים</p>}
                  </div>
                )}
                {(() => {
                  // Round both sides to 2 decimals (works symmetrically for negatives)
                  // before subtracting, so float drift in either total can't trigger
                  // a false mismatch. Threshold of > 1 ignores sub-shekel rounding gaps
                  // (e.g. VAT rounding), which are not real discrepancies.
                  const itemsSum       = round2((doc.items||[]).reduce((s,it) => s + (parseFloat(it.line_total)||0), 0));
                  const discountAmount = round2(calcInvoiceDiscountAmount(itemsSum, doc.invoice_discount, doc.invoice_discount_type));
                  const itemsTotal     = round2((itemsSum - discountAmount) + (parseFloat(doc.vat) || 0));
                  const docTotal       = round2(doc.total);

                  // Skip the warning entirely for documents where a mismatch is meaningless:
                  //  - credit/return doc types (totals are intentionally negative-shaped)
                  //  - negative-total documents
                  //  - documents with any open credits (line_totals are gross, not net of credits)
                  //  - empty documents
                  const isCreditDoc = ['חשבונית זיכוי', 'תעודת החזרה'].includes(doc.doc_type)
                                      || docTotal < 0 || itemsTotal < 0;
                  const skip = (doc.items||[]).length === 0 || isCreditDoc || hasAnyCredit;
                  const hasMismatch = !skip && Math.abs(itemsTotal - docTotal) > 1;
                  return hasMismatch ? (
                    <div style={{ background:'var(--danger-bg)', border:'1px solid var(--danger-line)', borderRadius:8,
                      padding:'9px 14px', marginBottom:12, fontSize:'0.88rem', color:'var(--danger-ink)', fontWeight:500 }}>
                      <Icon name="alert" size={15} /> אי-התאמה: סכום הפריטים ({formatCurrency(itemsTotal)}) שונה מהסה"כ ({formatCurrency(docTotal)})
                    </div>
                  ) : null;
                })()}
                <div className="totals-box">
                  <div className="total-row"><span>סכום לפני מע"מ</span><span>{formatCurrency(doc.subtotal)}</span></div>
                  {(() => {
                    const discountAmount = calcInvoiceDiscountAmount(doc.subtotal, doc.invoice_discount, doc.invoice_discount_type);
                    if (!hasInvoiceDiscount(discountAmount)) return null;
                    const isPct = (doc.invoice_discount_type || 'percent') !== 'amount';
                    const label = isPct ? `הנחה (${doc.invoice_discount}%)` : 'הנחה';
                    const taxable = (parseFloat(doc.subtotal) || 0) - discountAmount;
                    return (
                      <>
                        <div className="total-row" style={{ color:'var(--warning-ink)' }}>
                          <span>{label}</span>
                          <span style={{ fontWeight:600 }}>{signedDiscount(discountAmount)}</span>
                        </div>
                        <div className="total-row" style={{ borderTop:'1px solid var(--hairline)', paddingTop:6 }}>
                          <span>סה"כ לפני מע"מ אחרי הנחה</span>
                          <span>{formatCurrency(taxable)}</span>
                        </div>
                      </>
                    );
                  })()}
                  <div className="total-row"><span>מע"מ ({vatRate}%)<VatCheckTag show={isAdmin && !!doc.vat_needs_check} /></span><span>{formatCurrency(doc.vat)}</span></div>
                  <div className="total-row grand"><span>{totalLabel(doc.vat)}</span><span>{formatCurrency(doc.total)}</span></div>
                  {hasAnyCredit && (
                    <div className="total-row" style={{ color:'var(--warning-ink)', marginTop:8, borderTop:'1px solid var(--warning-line)', paddingTop:8 }}>
                      <span>סך זיכויים פתוחים (ללא מע״מ)</span>
                      <span style={{ fontWeight:700 }}>
                        {formatCurrency((doc.items||[]).reduce((s,it)=>s+(it.credit_total||0),0))}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              {doc.doc_type === 'חשבונית מרכזת' && (() => {
                const links = doc.linked_delivery_notes || [];
                // Reconcile on NET (subtotal), not gross — delivery notes carry no VAT
                // of their own; the VAT lives on the consolidated invoice. Comparing
                // gross would show a phantom difference equal to the invoice VAT.
                // Single source of truth in reconcile.js, shared with the admin modal.
                const { linkedNet, invoiceNet, diff, matches } = reconcileNet(links, doc.subtotal);
                return (
                  <div className="card">
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
                      <h3 style={{ fontSize:'1rem', fontWeight:700, color:'var(--ink-muted)', margin:0 }}>
                        <Icon name="link" size={16} /> תעודות משלוח מקושרות {links.length > 0 ? `(${links.length})` : ''}
                      </h3>
                      {isAdmin && (
                        <button className="btn btn-sm btn-outline" onClick={() => setLinkModal({
                          stillHere: routeOwner(),
                          invoiceId: doc.id,
                          invoiceDocNum: doc.doc_num,
                          invoiceTotal: doc.total,
                          invoiceSubtotal: doc.subtotal,
                          supplierName: doc.supplier_name,
                          invoiceStatus: doc.status,
                          existingLinkCount: links.length,
                          supplierId: doc.supplier_id,
                          docDate: doc.date,
                          afterClose: null, // manual edit — stay on the page
                        })}>
                          <Icon name="edit" size={15} /> ערוך קישורים
                        </button>
                      )}
                    </div>

                    {doc.pending_link_count > 0 && (doc.status === 'טיוטה' ? (
                      <div className="link-pending-note" style={{ marginBottom: 12 }}>
                        {doc.pending_link_count === 1
                          ? 'תעודה אחת סומנה בטיוטה. היא תיקשר אחרי אישור המסמך ושמירת הקישורים.'
                          : `${doc.pending_link_count} תעודות סומנו בטיוטה. הן ייקשרו אחרי אישור המסמך ושמירת הקישורים.`}
                      </div>
                    ) : (
                      <div className="link-unlinked-warning" style={{ marginBottom: 12 }}>
                        <Icon name="alert" size={15} />{' '}
                        {doc.pending_link_count === 1
                          ? 'תעודה אחת סומנה ועדיין לא מקושרת. עד שתקושר, הסכום שלה נספר לתשלום בנוסף למרכזת.'
                          : `${doc.pending_link_count} תעודות סומנו ועדיין לא מקושרות. עד שיקושרו, הסכום שלהן נספר לתשלום בנוסף למרכזת.`}
                      </div>
                    ))}
                    {links.length === 0 ? (
                      <div className="empty-state" style={{ padding:24 }}>
                        <p>אין תעודות משלוח מקושרות לחשבונית זו</p>
                      </div>
                    ) : (
                      <>
                        <div className="table-wrapper table-scroll-mobile" style={{ marginBottom:12 }}>
                          <table>
                            <thead>
                              <tr>
                                <th>מספר תעודה</th>
                                <th>תאריך</th>
                                <th>סכום נטו</th>
                                <th style={{ width: 110 }}></th>
                              </tr>
                            </thead>
                            <tbody>
                              {links.map(l => (
                                <tr key={l.id} className="row-clickable" onClick={() => leaveTo(`/document/${l.id}`)}>
                                  <td style={{ color:'var(--ink-muted)' }}>{l.doc_num || '—'}</td>
                                  <td>{formatDateShort(l.date) || '—'}</td>
                                  <td style={{ fontWeight:600, color: l.doc_type === 'תעודת החזרה' ? 'var(--danger-ink)' : 'inherit' }}>{formatCurrency(signedNet(l))}</td>
                                  <td>
                                    <button className="btn btn-sm btn-secondary"
                                      onClick={e => { e.stopPropagation(); leaveTo(`/document/${l.id}`); }}>
                                      צפייה
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr style={{ background:'var(--selected-soft-bg)', fontWeight:700 }}>
                                <td colSpan={2} style={{ padding:'10px 16px' }}>
                                  סה"כ {links.length} תעודות (נטו)
                                </td>
                                <td style={{ padding:'10px 16px' }}>{formatCurrency(linkedNet)}</td>
                                <td></td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>

                        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
                                      padding:'10px 14px', borderRadius:8,
                                      background: matches ? 'var(--success-bg)' : 'var(--warning-bg)',
                                      border: `1px solid ${matches ? 'var(--success-line)' : 'var(--warning-line)'}` }}>
                          <div style={{ fontSize:'0.9rem', color: matches ? 'var(--success-ink)' : 'var(--warning-ink)' }}>
                            <Icon name={matches ? 'check-circle' : 'alert'} size={15} />{' '}
                            {matches
                              ? (Math.abs(diff) < 0.01
                                  ? `תעודות המשלוח (נטו ${formatCurrency(linkedNet)}) תואמות למרכזת (נטו ${formatCurrency(invoiceNet)})`
                                  : `תעודות המשלוח (נטו ${formatCurrency(linkedNet)}) תואמות למרכזת (נטו ${formatCurrency(invoiceNet)}) — הפרשי מחיר/עיגול: ${formatCurrency(Math.abs(diff))}`)
                              : `תעודות משלוח נטו: ${formatCurrency(linkedNet)} | מרכזת נטו: ${formatCurrency(invoiceNet)} | הפרש: ${diff > 0 ? '+' : ''}${formatCurrency(diff)}`}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                );
              })()}
            </div>
            {filePane && (
              <DocFileViewer variant="pane" files={docFiles} pageIdx={pageIdx} onPageChange={setPageIdx}
                url={fileObjectUrl} onClose={closeFiles} onDownload={downloadAll} onMaximize={maximizeFiles}
                onRotate={isAdmin ? rotatePage : undefined} />
            )}
          </div>
          {fileFullscreen && (
            <DocFileViewer variant="fullscreen" files={docFiles} pageIdx={pageIdx} onPageChange={setPageIdx}
              url={fileObjectUrl} onClose={closeFiles} onDownload={downloadAll}
              onRotate={isAdmin ? rotatePage : undefined}
              onDock={isNarrow ? undefined : () => setFileFull(false)}
              // iPads (≤1024) share the iOS PDF-in-iframe problem.
              offerNativeViewer={isNarrow} />
          )}
        </>
      )}

      {/* Edit mode's review bar. View mode has no bottom row: its actions
          live in the title row. Wide screens render it inside the items column
          (below); ≤900px, where the split stacks, it's a bottom sheet here. */}
      {editing && narrowEdit && reviewBar}

      {commentsOpen && doc && (
        <CommentsModal
          type="document"
          referenceId={doc.id}
          title={`מסמך ${doc.doc_num || doc.id}`}
          // The SAVED status, not the form's: what the customer can see is
          // what is stored, and a draft is hidden from them.
          internalOnlyReason={doc.status === 'טיוטה'
            ? 'הלקוח לא רואה טיוטות — כדי לשלוח לו, לחצי "שלח ללקוח אחרי האישור" וההערה תיפתח לשליחה מיד כשתאשרי את המסמך'
            : ''}
          initialText={doc.status === 'טיוטה' ? (pendingCustomerNote || '') : ''}
          onSendAfterApproval={doc.status === 'טיוטה' ? (text) => {
            setPendingCustomerNote(text);
            setCommentsOpen(false);
            toast('ההערה תיפתח לשליחה ללקוח מיד אחרי אישור המסמך');
          } : undefined}
          onClose={() => setCommentsOpen(false)}
          onCommentAdded={(saved) => {
            setCommentsAdded(c => c + 1);
            // The waiting text itself was saved as an internal note instead —
            // it is no longer waiting. Any OTHER note leaves it alone.
            if (doc.status === 'טיוטה' && saved === pendingCustomerNote) setPendingCustomerNote(null);
          }}
        />
      )}

      {afterApprovalNote && doc && (
        <CommentsModal
          type="document"
          referenceId={doc.id}
          title={`מסמך ${doc.doc_num || doc.id}`}
          initialText={afterApprovalNote.text}
          onClose={finishAfterApprovalNote}
          onCommentAdded={() => { if (afterApprovalNote.stillHere()) setCommentsAdded(c => c + 1); toast('ההערה נשלחה ללקוח'); finishAfterApprovalNote(); }}
        />
      )}

      {linkModal && (
        <LinkDeliveryNotesModal
          invoiceId={linkModal.invoiceId}
          invoiceDocNum={linkModal.invoiceDocNum}
          invoiceTotal={linkModal.invoiceTotal}
          invoiceSubtotal={linkModal.invoiceSubtotal}
          supplierName={linkModal.supplierName}
          invoiceStatus={linkModal.invoiceStatus}
          existingLinkCount={linkModal.existingLinkCount}
          initialRange={linkModal.initialRange || null}
          monthApproval={linkMonthApproval(linkModal)}
          files={docFiles}
          onRotate={isAdmin ? rotatePage : undefined}
          decision={linkDecision}
          // The modal calls onSaved then onClose, so closing is handled once,
          // here — and an approval that deferred its navigation resumes it.
          // A save that outlived its route calls them too, from a window already
          // gone: the window on screen by then is another document's.
          onClose={() => {
            if (!linkModal.stillHere()) return;
            const after = linkModal.afterClose;
            setLinkModal(null);
            after?.();
          }}
          // Only refresh when we're staying on the page; an approval that is
          // about to navigate away has nothing to re-render.
          onSaved={() => { if (linkModal.stillHere() && !linkModal.afterClose) load(); }}
        />
      )}

      {openCreditsModal && (
        <LinkOpenCreditsModal
          creditDocId={openCreditsModal.creditDocId}
          creditDocTotal={openCreditsModal.creditDocTotal}
          creditDocSubtotal={openCreditsModal.creditDocSubtotal}
          creditDocVat={openCreditsModal.creditDocVat}
          supplierId={openCreditsModal.supplierId}
          supplierName={openCreditsModal.supplierName}
          onClose={() => {
            const after = openCreditsModal.afterClose;
            setOpenCreditsModal(null);
            (after || (() => navigate('/drafts')))();
          }}
        />
      )}
      {confirmDialog}
      {deleting && (
        <DeleteDocumentDialog
          doc={doc}
          title={doc.status === 'טיוטה' ? 'מחיקת טיוטה' : 'מחיקת מסמך'}
          confirmLabel={inDraftQueue ? 'מחק והבא' : (doc.status === 'טיוטה' ? 'מחק טיוטה' : 'מחק מסמך')}
          successMessage={doc.status === 'טיוטה' ? 'הטיוטה נמחקה' : 'המסמך נמחק'}
          review={doc.status === 'טיוטה' ? reviewPayload : null}
          initialReason={deleting === 'duplicate' ? 'duplicate' : ''}
          // In the queue the page shows the toast once it knows where it lands.
          announce={!inDraftQueue}
          // לא שייך למסעדה הזו: offer the move instead (#304, #361).
          onMove={isAdmin ? () => { setDeleting(false); setMoving(true); } : null}
          onCancel={() => setDeleting(false)}
          // A draft deleted in the queue continues to the next draft (#303).
          // Opened directly: a duplicate deleted from the compare flow goes
          // to the queue, where the next flagged draft is; any other delete
          // leaves for the supplier page.
          onDeleted={(result) => {
            setDeleting(false);
            if (inDraftQueue) {
              continueAfterDraftDelete(result);
              return;
            }
            navigate(deleting === 'duplicate' ? '/drafts' : (doc.supplier_id ? `/supplier/${doc.supplier_id}` : '/'));
          }}
        />
      )}
      {moving && (
        <MoveDocumentDialog
          doc={doc}
          // Only a draft's review is timed, as in the delete dialog.
          review={doc.status === 'טיוטה' ? reviewPayload : null}
          // The move takes the SAVED document; say what stays behind.
          unsavedEdits={editing && skipHasChanges()}
          pendingNote={!!pendingCustomerNote}
          onCancel={() => setMoving(false)}
          onMoved={({ notice, doneLead }) => {
            setMoving(false);
            setPendingCustomerNote(null);
            if (doc.status === 'טיוטה') {
              continueAfterDraftDelete({ notice, doneLead });
              return;
            }
            // It has left this restaurant, so never its page: where a delete lands.
            toast(notice);
            navigate(doc.supplier_id ? `/supplier/${doc.supplier_id}` : '/');
          }}
        />
      )}
      {creatingReturnFor === doc.id && (
        <ReturnNoteDialog
          doc={doc}
          files={docFiles}
          onClose={() => setCreatingReturnFor(null)}
          onOpenExisting={(id) => {
            setCreatingReturnFor(null);
            if (editing) window.open(tenantUrl(doc.restaurant_id, `/document/${id}`), '_blank', 'noopener');
            else leaveTo(`/document/${id}`);
          }}
          onCreated={({ id, doc_num }) => {
            setCreatingReturnFor(null);
            // The form stays as it is: only this document's link changes.
            setCreatedReturn({ forDocId: doc.id, id, doc_num });
            toast(`נוצרה טיוטת תעודת החזרה ${doc_num}`);
          }}
        />
      )}
      {comparing && doc?.duplicate_of && (
        <DuplicateCompareDialog
          // docFiles, not doc.files: a turn made on this screen lives in
          // usePageRotations, and the draft's scan must match it.
          draft={{ ...doc, files: docFiles }}
          existing={doc.duplicate_of}
          onClose={() => setComparing(false)}
          onDelete={() => { setComparing(false); setDeleting('duplicate'); }}
          onKeepDraft={() => { setComparing(false); setDeletingExisting(doc.duplicate_of); }}
          onChangeNumber={changeDocNumber}
        />
      )}
      {deletingExisting && (
        <DeleteDocumentDialog
          doc={deletingExisting}
          title="מחיקת המסמך הקיים"
          message={keepDraftDeleteQuestion(deletingExisting, formatCurrency)}
          context={keepDraftDeleteContext(doc.id)}
          docLabel="המסמך הקיים"
          warning={keepDraftDeleteWarning(deletingExisting, formatCurrency)}
          confirmLabel="מחק את הקיים"
          successMessage={`המסמך הקיים #${deletingExisting.id} נמחק`}
          initialReason="duplicate"
          onCancel={() => setDeletingExisting(null)}
          onDeleted={keepDraftAfterDelete}
        />
      )}
    </div>
  );
}
